import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, Line, Legend, ComposedChart, BarChart, Bar, LabelList } from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, BarChart3, Plus, Trash2, Edit2, X, Wallet, Target, Zap, RefreshCw, LogOut, Check, Activity, Shield, Loader2, Globe, TrendingUp as TrendingIcon, Newspaper, Calendar, ExternalLink, Clock, ThumbsUp, ThumbsDown, Minus, LayoutDashboard, Briefcase, History, ChevronRight, Bell, PiggyBank, Download, FileText, Wrench, Upload, AlertCircle, HelpCircle, Info, BookOpen, Users } from 'lucide-react';
import { supabase } from './supabaseClient';

// API endpoint for prices (backend - no API key exposed)
const PRICES_API = '/api/prices';
const SYMBOL_INFO_API = '/api/symbol-info';

// Barbarians color palette
const AMBER = {
  50: '#fffbeb', 100: '#fef3c7', 200: '#fde68a', 300: '#fcd34d',
  400: '#fbbf24', 500: '#f59e0b', 600: '#d97706', 700: '#b45309',
  800: '#92400e', 900: '#78350f',
};

const COLORS = [AMBER[500], AMBER[400], AMBER[600], AMBER[300], AMBER[700], AMBER[200], '#C0C0C0'];
const TYPE_COLORS = { Stock: AMBER[500], ETF: AMBER[400], Bond: AMBER[600], Crypto: AMBER[300], BIST: AMBER[700], TEFAS: AMBER[200], Commodity: '#C0C0C0' };

// Bilinen kripto sembolleri - bunlar otomatik olarak Crypto type alacak
const KNOWN_CRYPTO_SYMBOLS = [
  // Top coins
  'BTC', 'ETH', 'BNB', 'XRP', 'ADA', 'DOGE', 'SOL', 'DOT', 'MATIC', 'SHIB',
  'LTC', 'TRX', 'AVAX', 'LINK', 'ATOM', 'UNI', 'XMR', 'ETC', 'XLM', 'BCH',
  'APT', 'FIL', 'LDO', 'ARB', 'NEAR', 'VET', 'ALGO', 'ICP', 'QNT', 'FTM',
  'GRT', 'AAVE', 'SAND', 'MANA', 'AXS', 'THETA', 'EOS', 'XTZ', 'EGLD', 'FLOW',
  'CHZ', 'CRV', 'KLAY', 'LUNC', 'BSV', 'NEO', 'MKR', 'SNX', 'RUNE', 'ZEC',
  'DASH', 'COMP', 'ENJ', 'BAT', 'YFI', 'SUSHI', '1INCH', 'CAKE', 'ZIL', 'WAVES',
  // Layer 2 & New Chains
  'MNT', 'OP', 'IMX', 'INJ', 'SUI', 'SEI', 'TIA', 'STX', 'STRK', 'BLAST', 'ZK', 'MANTA',
  // Meme coins
  'PEPE', 'FLOKI', 'BONK', 'WIF', 'MEME', 'TURBO', 'LADYS',
  // DeFi & Others
  'BAL', 'GALA', 'ROSE', 'FET', 'RNDR', 'OCEAN', 'AGIX', 'AR', 'HNT',
  'HBAR', 'KAVA', 'CKB', 'CELO', 'ONE', 'HOT'
];

// Bilinen BIST hisseleri - BIST30 + BIST50 + popüler hisseler
const KNOWN_BIST_SYMBOLS = [
  // BIST30
  'AKBNK', 'ARCLK', 'ASELS', 'BIMAS', 'EKGYO', 'EREGL', 'FROTO', 'GARAN',
  'GUBRF', 'HEKTS', 'ISCTR', 'KCHOL', 'KOZAA', 'KOZAL', 'KRDMD', 'MGROS',
  'ODAS', 'PETKM', 'PGSUS', 'SAHOL', 'SASA', 'SISE', 'TAVHL', 'TCELL',
  'THYAO', 'TKFEN', 'TOASO', 'TUPRS', 'VAKBN', 'YKBNK',
  // BIST50 ek
  'AEFES', 'AGESA', 'AKSA', 'AKSEN', 'ALARK', 'ALFAS', 'AYGAZ', 'BAGFS',
  'BRYAT', 'CCOLA', 'CIMSA', 'DOHOL', 'EGEEN', 'ENKAI', 'ENJSA', 'GESAN',
  'HALKB', 'ISGYO', 'KONTR', 'KTLEV', 'LOGO', 'MAVI', 'OYAKC', 'PENTA',
  'QUAGR', 'SELEC', 'SKBNK', 'SMRTG', 'SOKM', 'TATGD', 'TMSN', 'TSKB',
  'TTKOM', 'TTRAK', 'TURSG', 'ULKER', 'VESBE', 'VESTL', 'YEOTK', 'ZOREN',
  // Diğer popüler hisseler
  'AGHOL', 'ANHYT', 'ANSGR', 'AVOD', 'AYDEM', 'BIOEN', 'BINHO', 'BRSAN',
  'BTCIM', 'BUCIM', 'CWENE', 'DOAS', 'ECILC', 'EUREN', 'GENIL', 'GOLTS',
  'GWIND', 'HALKB', 'INDES', 'IPEKE', 'ISMEN', 'KARSN', 'KAYSE', 'KERVT',
  'KLSER', 'KMPUR', 'KONYA', 'KORDS', 'KOZAL', 'KRPLS', 'MAGEN', 'MAKIM',
  'MIATK', 'MPARK', 'NETAS', 'NTGAZ', 'NUGYO', 'OTKAR', 'OYAYO', 'OZKGY',
  'PAPIL', 'PARSN', 'PENGD', 'PETKIM', 'POLHO', 'PRKAB', 'RYGYO', 'SARKY',
  'SNGYO', 'SODSN', 'TABGD', 'TCELL', 'TERA', 'TRGYO', 'TRILC', 'TSGYO',
  'TUKAS', 'TUREX', 'ULUUN', 'VAKKO', 'VBTYZ', 'VERTU', 'YAYLA', 'YGGYO'
];

// Bilinen TEFAS fonları - popüler yatırım fonları
const KNOWN_TEFAS_SYMBOLS = [
  // Popüler hisse fonları
  'IPB', 'TI2', 'TTE', 'YAF', 'MAC', 'ZPX', 'GAF', 'GAE', 'TYH', 'AK2',
  'OYA', 'DAH', 'ICF', 'AKU', 'AFT', 'AES', 'AFA', 'AGI', 'AKK', 'AL1',
  'ALF', 'APK', 'ARF', 'ARL', 'ASF', 'BGH', 'BGU', 'BHK', 'BHS', 'BHY',
  'BZF', 'DZE', 'EC1', 'FYD', 'GAG', 'GAH', 'GAR', 'GBH', 'GBU', 'GEY',
  'GHS', 'GLY', 'GMR', 'GPZ', 'GSP', 'HAF', 'HBF', 'HBS', 'HBU', 'HHS',
  'HVS', 'HYF', 'IAF', 'IEF', 'IGH', 'IHS', 'IHU', 'IYD', 'KYA', 'LGH',
  'MAG', 'MCD', 'MZK', 'NNF', 'OFA', 'OPF', 'OSA', 'OYH', 'OZN', 'PHS',
  'SPF', 'TAU', 'TCD', 'TEF', 'TFF', 'THL', 'THZ', 'TIE', 'TIH', 'TIS',
  'TKF', 'TLS', 'TMG', 'TTE', 'TYA', 'TYS', 'VEF', 'VIA', 'YAC', 'YAE',
  'YAS', 'YEF', 'YHS', 'YKS', 'ZBA', 'ZBH', 'ZHE', 'ZHS', 'ZPE', 'ZRF'
];

// Bilinen emtia/commodity sembolleri
const KNOWN_COMMODITY_SYMBOLS = [
  // Değerli Metaller
  'GOLD', 'SILVER', 'PLATINUM', 'PALLADIUM',
  'XAU', 'XAG', 'XPT', 'XPD',  // Forex sembolleri
  'GC=F', 'SI=F', 'PL=F', 'PA=F',  // Yahoo Finance futures
  // Enerji
  'CRUDE', 'OIL', 'BRENT', 'WTI', 'NATGAS',
  'CL=F', 'BZ=F', 'NG=F',  // Yahoo Finance futures
  // Tarım
  'WHEAT', 'CORN', 'SOYBEAN', 'COFFEE', 'SUGAR', 'COTTON', 'COCOA',
  'ZW=F', 'ZC=F', 'ZS=F', 'KC=F', 'SB=F', 'CT=F', 'CC=F',
  // Endüstriyel Metaller
  'COPPER', 'ALUMINUM', 'ZINC', 'NICKEL',
  'HG=F', 'ALI=F',
];

// Emtia sembol eşleştirme (kullanıcı dostu isim -> Yahoo Finance sembolü)
const COMMODITY_SYMBOL_MAP = {
  'GOLD': 'GC=F',
  'ALTIN': 'GC=F',
  'XAU': 'GC=F',
  'SILVER': 'SI=F',
  'GUMUS': 'SI=F',
  'XAG': 'SI=F',
  'PLATINUM': 'PL=F',
  'PLATIN': 'PL=F',
  'XPT': 'PL=F',
  'PALLADIUM': 'PA=F',
  'PALADYUM': 'PA=F',
  'XPD': 'PA=F',
  'OIL': 'CL=F',
  'CRUDE': 'CL=F',
  'WTI': 'CL=F',
  'PETROL': 'CL=F',
  'BRENT': 'BZ=F',
  'NATGAS': 'NG=F',
  'DOGALGAZ': 'NG=F',
  'COPPER': 'HG=F',
  'BAKIR': 'HG=F',
  'WHEAT': 'ZW=F',
  'BUGDAY': 'ZW=F',
  'CORN': 'ZC=F',
  'MISIR': 'ZC=F',
  'COFFEE': 'KC=F',
  'KAHVE': 'KC=F',
  'SUGAR': 'SB=F',
  'SEKER': 'SB=F',
  'COTTON': 'CT=F',
  'PAMUK': 'CT=F',
};

// Bilinen ETF sembolleri (ABD ETF'leri)
const KNOWN_ETF_SYMBOLS = [
  // En popüler ETF'ler
  'SPY', 'VOO', 'IVV', 'VTI', 'QQQ', 'QQQM', 'VEA', 'IEFA', 'VWO', 'EEM',
  'AGG', 'BND', 'VNQ', 'GLD', 'SLV', 'TLT', 'IEF', 'LQD', 'HYG', 'TIP',
  'VIG', 'VYM', 'SCHD', 'DVY', 'SDY', 'DGRO', 'VUG', 'IWF', 'VTV', 'IWD',
  'IWM', 'VB', 'IJR', 'IWN', 'VBR', 'EFA', 'VGK', 'EWJ', 'FXI', 'INDA',
  'EWZ', 'EWT', 'EWY', 'MCHI', 'VPL', 'IEMG', 'XLF', 'XLK', 'XLE', 'XLV',
  'XLI', 'XLY', 'XLP', 'XLU', 'XLB', 'XLRE', 'XLC', 'VHT', 'IBB', 'XBI',
  'ARKK', 'ARKG', 'ARKF', 'ARKW', 'ARKQ', 'SOXL', 'SOXS', 'TQQQ', 'SQQQ',
  'SPXL', 'SPXS', 'UVXY', 'VXX', 'VIXY', 'USO', 'UNG', 'DBC', 'PDBC', 'GSG',
  'IAU', 'GLDM', 'SIVR', 'PPLT', 'JNK', 'BNDX', 'EMB', 'VXUS', 'IXUS', 'ACWI'
];

// Sembol tipini otomatik tespit et
const detectAssetType = (symbol) => {
  const upperSymbol = symbol.toUpperCase();

  if (KNOWN_CRYPTO_SYMBOLS.includes(upperSymbol)) return 'Crypto';
  if (KNOWN_BIST_SYMBOLS.includes(upperSymbol)) return 'BIST';
  if (KNOWN_TEFAS_SYMBOLS.includes(upperSymbol)) return 'TEFAS';
  if (KNOWN_ETF_SYMBOLS.includes(upperSymbol)) return 'ETF';
  if (KNOWN_COMMODITY_SYMBOLS.includes(upperSymbol) || COMMODITY_SYMBOL_MAP[upperSymbol]) return 'Commodity';

  // Varsayılan: Stock
  return null; // null döndür ki kullanıcı seçsin
};

// Translations
const TRANSLATIONS = {
  tr: {
    // Tabs
    dashboard: 'Panel',
    holdings: 'Varlıklar',
    analytics: 'Analiz',
    news: 'Haberler',
    economics: 'Ekonomi',
    calendar: 'Takvim',
    notes: 'Notlar',
    tools: 'Araçlar',
    // Dashboard
    totalValue: 'Toplam Değer',
    holdingsValue: 'Yatırımlar',
    totalCash: 'Nakit',
    totalGainLoss: 'Kar/Zarar',
    combined: 'USD + TRY toplam',
    assets: 'varlık',
    bestPerformer: 'En İyi',
    volatility: 'Volatilite',
    maxDrawdown: 'Maks. Düşüş',
    performanceHistory: 'Performans Geçmişi',
    allocation: 'Dağılım',
    total: 'TOPLAM',
    topPerformers: 'En İyi Performans',
    sectorBreakdown: 'Sektör Dağılımı',
    realized: 'Nakit Çıkışı',
    adjusted: 'Düzeltilmiş',
    beta: 'Beta',
    sharpeRatio: 'Sharpe Oranı',
    sortinoRatio: 'Sortino Oranı',
    weeklyHistory: 'Haftalık Geçmiş',
    portfolioValue: 'Portföy Değeri',
    currentPortfolio: 'Mevcut Portföy',
    adjustedWithWithdrawals: 'Çekimler Dahil',
    totalWithdrawals: 'Nakit Çıkışı',
    totalWealth: 'Toplam Servet',
    monthlyComparison: 'Aylık Karşılaştırma',
    vsBenchmark: 'vs S&P 500',
    portfolio: 'Portföy',
    alpha: 'Alpha',
    vsSp500: 'vs S&P 500',
    noSnapshot: 'Henüz kayıt yok',
    firstSnapshot: 'İlk kayıt bu Cuma 21:00 UTC\'de alınacak.',
    // Table Headers
    holdingsTableHeader: 'Varlıklar',
    cashWithdrawal: 'Nakit Çıkışı',
    change: 'Değişim',
    date: 'Tarih',
    // Help & Glossary
    help: 'Yardım',
    glossary: 'Terimler Sözlüğü',
    closeHelp: 'Kapat',
    understandMetrics: 'Metrikleri Anla',
    // Tooltip Descriptions
    tooltips: {
      totalValue: 'Tüm yatırımlarınızın ve nakit bakiyenizin toplam değeri (USD cinsinden).',
      holdingsValue: 'Nakit hariç, sadece yatırım enstrümanlarınızın toplam değeri.',
      totalCash: 'USD ve TRY nakit bakiyelerinizin toplamı (USD\'ye çevrilmiş).',
      totalGainLoss: 'Toplam kar veya zarar. Yeşil = kâr, kırmızı = zarar.',
      beta: 'Portföyünüzün piyasaya (S&P 500) göre oynaklığı.\n• 1.0 = Piyasa ile aynı hareket\n• >1 = Daha riskli/oynak\n• <1 = Daha stabil',
      sharpeRatio: 'Risk başına getiri oranı. Ne kadar yüksekse o kadar iyi.\n• <1 = Düşük\n• 1-2 = İyi\n• 2-3 = Çok iyi\n• >3 = Mükemmel',
      sortinoRatio: 'Sharpe gibi ama sadece düşüş riskini hesaplar. Yüksek = iyi.',
      volatility: 'Portföy değerindeki yıllık dalgalanma yüzdesi.\n• <%15 = Düşük risk\n• %15-25 = Orta risk\n• >%25 = Yüksek risk',
      maxDrawdown: 'En yüksek noktadan yaşanan en büyük kayıp yüzdesi. Düşük = iyi.',
      alpha: 'S&P 500\'e göre fazladan kazanç veya kayıp.\n• Pozitif = Piyasayı yendiniz\n• Negatif = Piyasanın gerisinde kaldınız',
      realized: 'Portföyden çekip gayrimenkul, araç gibi varlıklara dönüştürdüğünüz para.',
      totalWealth: 'Mevcut portföy + nakit çıkışları. Gerçek toplam servetiniz.',
      monthlyComparison: 'Aylık bazda portföyünüzün S&P 500 endeksine göre performansı.',
      weeklyHistory: 'Her Cuma otomatik kaydedilen portföy değerinizin geçmişi.',
      topPerformers: 'En yüksek kar oranına sahip varlıklarınız.',
      allocation: 'Portföyünüzdeki varlık dağılımı (ağırlık yüzdesi).',
      cashWithdrawal: 'O hafta portföyden çıkarılan nakit tutarı. Düzenlemek için tıklayın.'
    },
    // Holdings
    cashBalances: 'Nakit Bakiyeler',
    usDollar: 'Amerikan Doları',
    usdt: 'Tether (USDT)',
    turkishLira: 'Türk Lirası',
    manageAssets: 'Varlıklarınızı yönetin',
    addAsset: 'Varlık Ekle',
    add: 'Ekle',
    symbol: 'Sembol',
    name: 'İsim',
    type: 'Tip',
    shares: 'Adet',
    avgCost: 'Ort. Maliyet',
    price: 'Fiyat',
    value: 'Değer',
    gain: 'Kar/Zarar',
    weight: 'Ağırlık',
    actions: 'İşlemler',
    gainPercent: 'Kar %',
    gainAmount: 'Kar $',
    // Analytics
    riskMetrics: 'Risk Metrikleri',
    whatItMeans: 'Ne Anlama Geliyor?',
    // Tools
    aaiiTitle: 'AAII Duyarlılık Anketi',
    aaiiSubtitle: 'Kontrarian piyasa göstergesi',
    historicalAverages: 'Tarihsel Ortalamalar',
    howToInterpret: 'Nasıl Yorumlanır?',
    highOptimism: 'Yüksek İyimserlik',
    highPessimism: 'Yüksek Karamsarlık',
    neutralZone: 'Nötr Bölge',
    seeResults: 'Güncel Sonuçları Gör',
    updatedThursday: 'Sonuçlar her Perşembe güncellenir',
    exportReport: 'Rapor İndir',
    downloadReport: 'Portföy raporunuzu indirin',
    csvExport: 'CSV İndir',
    spreadsheet: 'Tablo formatı',
    pdfReport: 'PDF Rapor',
    printOrSave: 'Yazdır veya kaydet',
    importHoldings: 'Varlıkları İçe Aktar',
    importFromBroker: 'Broker dışa aktarımından içe aktar',
    sector: 'Sektör',
    typeSymbolToAutofill: 'Sembol yazarak otomatik doldur',
    autoFilledFromSymbol: 'Sembolden otomatik doldurulur',
    selectBroker: 'Broker Seçin',
    uploadFile: 'Dosya Yükle (CSV)',
    clickToSelect: 'Dosya seçmek için tıklayın',
    supported: 'Desteklenen: CSV, TXT',
    preview: 'Önizleme',
    reviewBeforeImport: 'İçe aktarmadan önce gözden geçirin',
    import: 'İçe Aktar',
    // Transactions
    transactionHistory: 'İşlem Geçmişi',
    addTransaction: 'İşlem Ekle',
    buy: 'ALIŞ',
    sell: 'SATIŞ',
    transactionDate: 'Tarih',
    quantity: 'Miktar',
    pricePerUnit: 'Birim Fiyat',
    noTransactions: 'Henüz işlem yok',
    addFirstTransaction: 'İlk işlemi ekleyin',
    realizedGain: 'Realize Kar',
    summary: 'Özet',
    current: 'Mevcut',
    close: 'Kapat',
    // Common
    refreshPrices: 'Fiyatları Güncelle',
    loading: 'Yükleniyor...',
    edit: 'Düzenle',
    delete: 'Sil',
    save: 'Kaydet',
    cancel: 'İptal',
    all: 'Tümü',
    investor: 'Yatırımcı',
    logout: 'Çıkış',
    // FMP Fundamentals
    fundamentals: 'Temel Veriler',
    companyInfo: 'Şirket Bilgisi',
    financialMetrics: 'Finansal Metrikler',
    ceo: 'CEO',
    employees: 'Çalışan Sayısı',
    industry: 'Sektör',
    marketCap: 'Piyasa Değeri',
    peRatio: 'F/K Oranı',
    pbRatio: 'F/DD Oranı',
    psRatio: 'F/S Oranı',
    roe: 'Özsermaye Karlılığı',
    roa: 'Aktif Karlılığı',
    grossMargin: 'Brüt Kar Marjı',
    netMargin: 'Net Kar Marjı',
    currentRatio: 'Cari Oran',
    debtToEquity: 'Borç/Özsermaye',
    notAvailable: 'Veri yok',
    onlyUSStocks: 'Sadece US hisseleri için',
    viewFundamentals: 'Temel Verileri Gör',
  },
  en: {
    // Tabs
    dashboard: 'Dashboard',
    holdings: 'Holdings',
    analytics: 'Analytics',
    news: 'News',
    economics: 'Economics',
    calendar: 'Calendar',
    notes: 'Notes',
    tools: 'Tools',
    // Dashboard
    totalValue: 'Total Value',
    holdingsValue: 'Holdings',
    totalCash: 'Total Cash',
    totalGainLoss: 'Total Gain/Loss',
    combined: 'USD + TRY combined',
    assets: 'assets',
    bestPerformer: 'Best Performer',
    volatility: 'Volatility',
    maxDrawdown: 'Max Drawdown',
    performanceHistory: 'Performance History',
    allocation: 'Allocation',
    total: 'TOTAL',
    topPerformers: 'Top Performers',
    sectorBreakdown: 'Sector Breakdown',
    realized: 'Realized',
    adjusted: 'Adjusted',
    beta: 'Beta',
    sharpeRatio: 'Sharpe Ratio',
    sortinoRatio: 'Sortino Ratio',
    weeklyHistory: 'Weekly History',
    portfolioValue: 'Portfolio Value',
    currentPortfolio: 'Current Portfolio',
    adjustedWithWithdrawals: 'With Withdrawals (Adjusted)',
    totalWithdrawals: 'Total Withdrawals',
    totalWealth: 'Total Wealth',
    monthlyComparison: 'Monthly Comparison',
    vsBenchmark: 'vs S&P 500',
    portfolio: 'Portfolio',
    alpha: 'Alpha',
    vsSp500: 'vs S&P 500',
    noSnapshot: 'No snapshots yet',
    firstSnapshot: 'First snapshot will be taken this Friday at 21:00 UTC.',
    // Table Headers
    holdingsTableHeader: 'Holdings',
    cashWithdrawal: 'Withdrawal',
    change: 'Change',
    date: 'Date',
    // Help & Glossary
    help: 'Help',
    glossary: 'Glossary',
    closeHelp: 'Close',
    understandMetrics: 'Understand Metrics',
    // Tooltip Descriptions
    tooltips: {
      totalValue: 'Total value of all your investments and cash balances (in USD).',
      holdingsValue: 'Total value of your investment instruments, excluding cash.',
      totalCash: 'Sum of your USD and TRY cash balances (converted to USD).',
      totalGainLoss: 'Total profit or loss. Green = profit, red = loss.',
      beta: 'Your portfolio\'s volatility compared to the market (S&P 500).\n• 1.0 = Moves with market\n• >1 = More volatile/risky\n• <1 = More stable',
      sharpeRatio: 'Risk-adjusted return ratio. Higher is better.\n• <1 = Low\n• 1-2 = Good\n• 2-3 = Very good\n• >3 = Excellent',
      sortinoRatio: 'Like Sharpe but only considers downside risk. Higher = better.',
      volatility: 'Annual fluctuation percentage of portfolio value.\n• <15% = Low risk\n• 15-25% = Medium risk\n• >25% = High risk',
      maxDrawdown: 'Largest drop from peak to trough. Lower = better.',
      alpha: 'Excess return compared to S&P 500.\n• Positive = Beat the market\n• Negative = Underperformed',
      realized: 'Money withdrawn from portfolio and converted to real estate, vehicles, etc.',
      totalWealth: 'Current portfolio + withdrawals. Your true total wealth.',
      monthlyComparison: 'Monthly performance of your portfolio compared to S&P 500 index.',
      weeklyHistory: 'History of your portfolio value recorded every Friday.',
      topPerformers: 'Your assets with the highest profit percentages.',
      allocation: 'Distribution of assets in your portfolio (weight percentage).',
      cashWithdrawal: 'Cash amount withdrawn that week. Click to edit.'
    },
    // Holdings
    cashBalances: 'Cash Balances',
    usDollar: 'US Dollar',
    usdt: 'Tether (USDT)',
    turkishLira: 'Turkish Lira',
    manageAssets: 'Manage your portfolio assets',
    addAsset: 'Add Asset',
    add: 'Add',
    symbol: 'Symbol',
    name: 'Name',
    type: 'Type',
    shares: 'Shares',
    avgCost: 'Avg Cost',
    price: 'Price',
    value: 'Value',
    gain: 'Gain',
    weight: 'Weight',
    actions: 'Actions',
    gainPercent: 'Gain %',
    gainAmount: 'Gain $',
    // Analytics
    riskMetrics: 'Risk Metrics',
    whatItMeans: 'What It Means?',
    // Tools
    aaiiTitle: 'AAII Sentiment Survey',
    aaiiSubtitle: 'Contrarian market indicator',
    historicalAverages: 'Historical Averages',
    howToInterpret: 'How to Interpret?',
    highOptimism: 'High Optimism',
    highPessimism: 'High Pessimism',
    neutralZone: 'Neutral Zone',
    seeResults: 'See Current Results',
    updatedThursday: 'Results updated every Thursday',
    exportReport: 'Export Report',
    downloadReport: 'Download your portfolio report',
    csvExport: 'CSV Export',
    spreadsheet: 'Spreadsheet format',
    pdfReport: 'PDF Report',
    printOrSave: 'Print or save as PDF',
    importHoldings: 'Import Holdings',
    importFromBroker: 'Import from broker export',
    sector: 'Sector',
    typeSymbolToAutofill: 'Type symbol to auto-fill',
    autoFilledFromSymbol: 'Auto-filled from symbol',
    selectBroker: 'Select Broker',
    uploadFile: 'Upload File (CSV)',
    clickToSelect: 'Click to select file or drag and drop',
    supported: 'Supported: CSV, TXT',
    preview: 'Preview',
    reviewBeforeImport: 'Review before importing',
    import: 'Import',
    // Transactions
    transactionHistory: 'Transaction History',
    addTransaction: 'Add Transaction',
    buy: 'BUY',
    sell: 'SELL',
    transactionDate: 'Date',
    quantity: 'Quantity',
    pricePerUnit: 'Price per Unit',
    noTransactions: 'No transactions yet',
    addFirstTransaction: 'Add your first transaction',
    realizedGain: 'Realized Gain',
    summary: 'Summary',
    current: 'Current',
    close: 'Close',
    // Common
    refreshPrices: 'Refresh Prices',
    loading: 'Loading...',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    all: 'All',
    investor: 'Investor',
    logout: 'Logout',
    // FMP Fundamentals
    fundamentals: 'Fundamentals',
    companyInfo: 'Company Info',
    financialMetrics: 'Financial Metrics',
    ceo: 'CEO',
    employees: 'Employees',
    industry: 'Industry',
    marketCap: 'Market Cap',
    peRatio: 'P/E Ratio',
    pbRatio: 'P/B Ratio',
    psRatio: 'P/S Ratio',
    roe: 'ROE',
    roa: 'ROA',
    grossMargin: 'Gross Margin',
    netMargin: 'Net Margin',
    currentRatio: 'Current Ratio',
    debtToEquity: 'Debt/Equity',
    notAvailable: 'N/A',
    onlyUSStocks: 'US stocks only',
    viewFundamentals: 'View Fundamentals',
  }
};

// Wall Street Quotes - Bilingual
const WALL_STREET_QUOTES = {
  tr: [
    { quote: "Başkaları açgözlü olduğunda korkak ol, korkak olduğunda açgözlü ol.", author: "Warren Buffett" },
    { quote: "Borsa, parayı sabırsızlardan sabırlılara aktaran bir araçtır.", author: "Warren Buffett" },
    { quote: "Fiyat ödediğinizdir. Değer elde ettiğinizdir.", author: "Warren Buffett" },
    { quote: "Kural 1: Asla para kaybetme. Kural 2: Asla Kural 1'i unutma.", author: "Warren Buffett" },
    { quote: "Harika bir şirketi makul fiyata almak, makul bir şirketi harika fiyata almaktan iyidir.", author: "Warren Buffett" },
    { quote: "En sevdiğimiz elde tutma süresi sonsuzdur.", author: "Warren Buffett" },
    { quote: "Risk, ne yaptığınızı bilmemekten kaynaklanır.", author: "Warren Buffett" },
    { quote: "Yapabileceğiniz en iyi yatırım kendinize yapacağınız yatırımdır.", author: "Warren Buffett" },
    { quote: "Piyasa 10 yıl kapansa bile mutlu tutacağınız bir şey almadıkça almayın.", author: "Warren Buffett" },
    { quote: "Bugün birisi gölgede oturuyor çünkü biri uzun zaman önce bir ağaç dikti.", author: "Warren Buffett" },
    { quote: "Bir hisseyi 10 yıl tutmayı düşünmüyorsanız, 10 dakika bile tutmayı düşünmeyin.", author: "Warren Buffett" },
    { quote: "Bir yatırımcı için en önemli özellik zeka değil, mizaçtır.", author: "Warren Buffett" },
    { quote: "Zaman harika şirketin dostu, vasat şirketin düşmanıdır.", author: "Warren Buffett" },
    { quote: "Anlamadığınız bir işe asla yatırım yapmayın.", author: "Warren Buffett" },
    { quote: "Büyük para alıp satmakta değil, beklemektedir.", author: "Charlie Munger" },
    { quote: "Her gün uyandığınızda biraz daha akıllı olmaya çalışın.", author: "Charlie Munger" },
    { quote: "Yatırımcıya yardımcı olan beklemektir ve çoğu insan beklemeye dayanamaz.", author: "Charlie Munger" },
    { quote: "Bileşik getirinin ilk kuralı: Gereksiz yere kesintiye uğratma.", author: "Charlie Munger" },
    { quote: "Ne bilmediğinizi bilmek, zeki olmaktan daha faydalıdır.", author: "Charlie Munger" },
    { quote: "Tersine çevir, her zaman tersine çevir.", author: "Charlie Munger" },
    { quote: "Ne sahip olduğunuzu bilin ve neden sahip olduğunuzu bilin.", author: "Peter Lynch" },
    { quote: "Hisse senetlerinde para kazanmanın anahtarı, onlardan korkmamaktır.", author: "Peter Lynch" },
    { quote: "Herhangi bir aptalın yönetebileceği bir iş bulun - çünkü er ya da geç bir aptal yönetecektir.", author: "Peter Lynch" },
    { quote: "Bu işte iyiyseniz, on seferden altısında haklı çıkarsınız.", author: "Peter Lynch" },
    { quote: "Düzeltmelere hazırlanırken kaybedilen para, düzeltmelerde kaybedilenden çok daha fazladır.", author: "Peter Lynch" },
    { quote: "Bildiklerinize yatırım yapın.", author: "Peter Lynch" },
    { quote: "Kısa vadede piyasa bir oylama makinesidir. Uzun vadede bir tartı makinesidir.", author: "Benjamin Graham" },
    { quote: "Yatırımcının en büyük sorunu - hatta en kötü düşmanı - muhtemelen kendisidir.", author: "Benjamin Graham" },
    { quote: "İyimserlikle değil, aritmetikle alın.", author: "Benjamin Graham" },
    { quote: "Güvenlik marjı her zaman ödenen fiyata bağlıdır.", author: "Benjamin Graham" },
    { quote: "Acı + Yansıma = İlerleme.", author: "Ray Dalio" },
    { quote: "Kristal küreyle yaşayan, kırık cam yiyecektir.", author: "Ray Dalio" },
    { quote: "İyi yatırım yapmak için çeşitlendirme en önemli şeydir.", author: "Ray Dalio" },
    { quote: "Yatırımcıların yaptığı en büyük hata, yakın geçmişte olanın devam edeceğine inanmaktır.", author: "Ray Dalio" },
    { quote: "Önemli olan haklı veya haksız olmanız değil, haklıyken ne kadar kazandığınızdır.", author: "George Soros" },
    { quote: "Piyasalar sürekli belirsizlik ve değişim halindedir.", author: "George Soros" },
    { quote: "Sadece ne zaman yanıldığımı bildiğim için zenginim.", author: "George Soros" },
    { quote: "Yatırımda en tehlikeli dört kelime: 'Bu sefer farklı.'", author: "John Templeton" },
    { quote: "Boğa piyasaları karamsarlıkta doğar, şüphecilikte büyür, iyimserlikte olgunlaşır, coşkuda ölür.", author: "John Templeton" },
    { quote: "Yatırım yapmanın en iyi zamanı paranız olduğu zamandır.", author: "John Templeton" },
    { quote: "Başkaları umutsuzca satarken al, hevesle alırken sat.", author: "John Templeton" },
    { quote: "En büyük yatırım hataları bilgisel veya analitik değil, psikolojik faktörlerden kaynaklanır.", author: "Howard Marks" },
    { quote: "Tahmin edemezsiniz ama hazırlanabilirsiniz.", author: "Howard Marks" },
    { quote: "Zamanın çok önünde olmak, yanılmaktan ayırt edilemez.", author: "Howard Marks" },
    { quote: "Piyasa asla yanılmaz - fikirler çoğu zaman yanılır.", author: "Jesse Livermore" },
    { quote: "Wall Street'te yeni bir şey yok. Spekülasyon dağlar kadar eskidir.", author: "Jesse Livermore" },
    { quote: "Para oturarak kazanılır, alım satım yaparak değil.", author: "Jesse Livermore" },
    { quote: "Bana büyük parayı kazandıran düşüncelerim değil, oturmam oldu.", author: "Jesse Livermore" },
    { quote: "Samanlıkta iğne aramayın. Samanlığı satın alın!", author: "John Bogle" },
    { quote: "Zaman dostunuzdur; dürtü düşmanınız.", author: "John Bogle" },
    { quote: "Rotada kalın. Ne olursa olsun, programınıza sadık kalın.", author: "John Bogle" },
    { quote: "Değer yatırımcılığı, özünde karşıtlık ve hesap makinesinin birleşimidir.", author: "Seth Klarman" },
    { quote: "Tüm finansal balonların kökünde aşırıya kaçmış iyi bir fikir vardır.", author: "Seth Klarman" },
    { quote: "Bileşik faiz dünyanın sekizinci harikasıdır.", author: "Albert Einstein" },
    { quote: "Bilgiye yapılan yatırım en iyi faizi öder.", author: "Benjamin Franklin" },
    { quote: "Piyasalar sizin ödeme gücünüzden daha uzun süre mantıksız kalabilir.", author: "John Maynard Keynes" },
  ],
  en: [
    { quote: "Be fearful when others are greedy, and greedy when others are fearful.", author: "Warren Buffett" },
    { quote: "The stock market is a device for transferring money from the impatient to the patient.", author: "Warren Buffett" },
    { quote: "Price is what you pay. Value is what you get.", author: "Warren Buffett" },
    { quote: "Rule No. 1: Never lose money. Rule No. 2: Never forget Rule No. 1.", author: "Warren Buffett" },
    { quote: "It's far better to buy a wonderful company at a fair price than a fair company at a wonderful price.", author: "Warren Buffett" },
    { quote: "Our favorite holding period is forever.", author: "Warren Buffett" },
    { quote: "Risk comes from not knowing what you're doing.", author: "Warren Buffett" },
    { quote: "The best investment you can make is in yourself.", author: "Warren Buffett" },
    { quote: "Only buy something that you'd be perfectly happy to hold if the market shut down for 10 years.", author: "Warren Buffett" },
    { quote: "Someone's sitting in the shade today because someone planted a tree a long time ago.", author: "Warren Buffett" },
    { quote: "If you aren't thinking about owning a stock for 10 years, don't even think about owning it for 10 minutes.", author: "Warren Buffett" },
    { quote: "The most important quality for an investor is temperament, not intellect.", author: "Warren Buffett" },
    { quote: "Time is the friend of the wonderful company, the enemy of the mediocre.", author: "Warren Buffett" },
    { quote: "Never invest in a business you cannot understand.", author: "Warren Buffett" },
    { quote: "The big money is not in the buying and selling, but in the waiting.", author: "Charlie Munger" },
    { quote: "Spend each day trying to be a little wiser than you were when you woke up.", author: "Charlie Munger" },
    { quote: "It's waiting that helps you as an investor, and a lot of people just can't stand to wait.", author: "Charlie Munger" },
    { quote: "The first rule of compounding: Never interrupt it unnecessarily.", author: "Charlie Munger" },
    { quote: "Knowing what you don't know is more useful than being brilliant.", author: "Charlie Munger" },
    { quote: "Invert, always invert.", author: "Charlie Munger" },
    { quote: "Know what you own, and know why you own it.", author: "Peter Lynch" },
    { quote: "The key to making money in stocks is not to get scared out of them.", author: "Peter Lynch" },
    { quote: "Go for a business that any idiot can run – because sooner or later, any idiot is probably going to run it.", author: "Peter Lynch" },
    { quote: "In this business, if you're good, you're right six times out of ten.", author: "Peter Lynch" },
    { quote: "Far more money has been lost by investors preparing for corrections than has been lost in corrections themselves.", author: "Peter Lynch" },
    { quote: "Invest in what you know.", author: "Peter Lynch" },
    { quote: "In the short run, the market is a voting machine. In the long run, it is a weighing machine.", author: "Benjamin Graham" },
    { quote: "The investor's chief problem – and even his worst enemy – is likely to be himself.", author: "Benjamin Graham" },
    { quote: "Buy not on optimism, but on arithmetic.", author: "Benjamin Graham" },
    { quote: "The margin of safety is always dependent on the price paid.", author: "Benjamin Graham" },
    { quote: "Pain + Reflection = Progress.", author: "Ray Dalio" },
    { quote: "He who lives by the crystal ball will eat shattered glass.", author: "Ray Dalio" },
    { quote: "Diversifying well is the most important thing you need to do in order to invest well.", author: "Ray Dalio" },
    { quote: "The biggest mistake investors make is to believe that what happened in the recent past is likely to persist.", author: "Ray Dalio" },
    { quote: "It's not whether you're right or wrong that's important, but how much money you make when you're right.", author: "George Soros" },
    { quote: "Markets are constantly in a state of uncertainty and flux.", author: "George Soros" },
    { quote: "I'm only rich because I know when I'm wrong.", author: "George Soros" },
    { quote: "The four most dangerous words in investing are: 'This time it's different.'", author: "John Templeton" },
    { quote: "Bull markets are born on pessimism, grow on skepticism, mature on optimism, and die on euphoria.", author: "John Templeton" },
    { quote: "The best time to invest is when you have money.", author: "John Templeton" },
    { quote: "To buy when others are despondently selling and sell when others are avidly buying.", author: "John Templeton" },
    { quote: "The biggest investing errors come not from factors that are informational or analytical, but from those that are psychological.", author: "Howard Marks" },
    { quote: "You can't predict, but you can prepare.", author: "Howard Marks" },
    { quote: "Being too far ahead of your time is indistinguishable from being wrong.", author: "Howard Marks" },
    { quote: "The market is never wrong – opinions often are.", author: "Jesse Livermore" },
    { quote: "There is nothing new in Wall Street. Speculation is as old as the hills.", author: "Jesse Livermore" },
    { quote: "Money is made by sitting, not trading.", author: "Jesse Livermore" },
    { quote: "It never was my thinking that made the big money for me. It always was my sitting.", author: "Jesse Livermore" },
    { quote: "Don't look for the needle in the haystack. Just buy the haystack!", author: "John Bogle" },
    { quote: "Time is your friend; impulse is your enemy.", author: "John Bogle" },
    { quote: "Stay the course. No matter what happens, stick to your program.", author: "John Bogle" },
    { quote: "Value investing is at its core the marriage of a contrarian streak and a calculator.", author: "Seth Klarman" },
    { quote: "At the root of all financial bubbles is a good idea carried to excess.", author: "Seth Klarman" },
    { quote: "Compound interest is the eighth wonder of the world.", author: "Albert Einstein" },
    { quote: "An investment in knowledge pays the best interest.", author: "Benjamin Franklin" },
    { quote: "Markets can remain irrational longer than you can remain solvent.", author: "John Maynard Keynes" },
  ]
};

// Navigation tabs configuration
const TABS = [
  { id: 'dashboard', icon: LayoutDashboard },
  { id: 'holdings', icon: Briefcase },
  { id: 'analytics', icon: Activity },
  { id: 'economics', icon: TrendingUp },
  { id: 'news', icon: Newspaper },
  { id: 'calendar', icon: Calendar },
  { id: 'notes', icon: FileText },
  { id: 'tools', icon: Wrench },
];

// Get random quote based on language (will be called with language parameter)
const getRandomQuote = (lang = 'tr') => {
  const quotes = WALL_STREET_QUOTES[lang] || WALL_STREET_QUOTES.tr;
  return quotes[Math.floor(Math.random() * quotes.length)];
};

// InfoTooltip component - shows info icon with hover tooltip
const InfoTooltip = ({ text, className = '' }) => {
  const [show, setShow] = useState(false);

  return (
    <div className={`relative inline-flex items-center ${className}`}>
      <button
        type="button"
        className="ml-1.5 text-neutral-500 hover:text-amber-500 transition-colors focus:outline-none"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={(e) => { e.stopPropagation(); setShow(!show); }}
      >
        <Info className="w-3.5 h-3.5" />
      </button>
      {show && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-neutral-800 border border-neutral-700 rounded-xl shadow-xl text-xs text-neutral-300 whitespace-pre-line">
          {text}
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1">
            <div className="border-8 border-transparent border-t-neutral-800"></div>
          </div>
        </div>
      )}
    </div>
  );
};

// HelpModal component - shows glossary of all terms
const HelpModal = ({ isOpen, onClose, t }) => {
  if (!isOpen) return null;

  const glossaryItems = [
    { term: t.totalValue, description: t.tooltips.totalValue, icon: DollarSign, color: 'text-amber-500' },
    { term: t.holdingsValue, description: t.tooltips.holdingsValue, icon: Briefcase, color: 'text-emerald-500' },
    { term: t.totalCash, description: t.tooltips.totalCash, icon: Wallet, color: 'text-blue-500' },
    { term: t.totalGainLoss, description: t.tooltips.totalGainLoss, icon: TrendingUp, color: 'text-emerald-500' },
    { term: t.beta, description: t.tooltips.beta, icon: Activity, color: 'text-purple-500' },
    { term: t.sharpeRatio, description: t.tooltips.sharpeRatio, icon: Target, color: 'text-cyan-500' },
    { term: t.sortinoRatio, description: t.tooltips.sortinoRatio, icon: Shield, color: 'text-indigo-500' },
    { term: t.volatility, description: t.tooltips.volatility, icon: Zap, color: 'text-orange-500' },
    { term: t.maxDrawdown, description: t.tooltips.maxDrawdown, icon: TrendingDown, color: 'text-red-500' },
    { term: t.alpha, description: t.tooltips.alpha, icon: Target, color: 'text-emerald-500' },
    { term: t.realized, description: t.tooltips.realized, icon: PiggyBank, color: 'text-purple-500' },
    { term: t.totalWealth, description: t.tooltips.totalWealth, icon: DollarSign, color: 'text-blue-500' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-neutral-900 border border-neutral-800 rounded-3xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-neutral-50">{t.glossary}</h2>
              <p className="text-sm text-neutral-500">{t.understandMetrics}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-800 rounded-xl transition-colors">
            <X className="w-5 h-5 text-neutral-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-100px)]">
          <div className="grid gap-4">
            {glossaryItems.map((item, idx) => (
              <div key={idx} className="flex gap-4 p-4 bg-neutral-800/50 rounded-2xl border border-neutral-700/50">
                <div className={`w-10 h-10 rounded-xl bg-neutral-800 flex items-center justify-center flex-shrink-0 ${item.color}`}>
                  <item.icon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-neutral-100 mb-1">{item.term}</h3>
                  <p className="text-sm text-neutral-400 whitespace-pre-line">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function BarbariansTracker({ user, onLogout }) {
  // Language state - persisted to localStorage
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('barbarians-language') || 'tr';
    }
    return 'tr';
  });

  // Translation helper
  const t = TRANSLATIONS[language];

  // Navigation state
  const [activeTab, setActiveTab] = useState('dashboard');

  // Random quote for this session (changes with language)
  const [dailyQuote, setDailyQuote] = useState(() => getRandomQuote(language));

  // Update quote when language changes
  useEffect(() => {
    setDailyQuote(getRandomQuote(language));
    localStorage.setItem('barbarians-language', language);
  }, [language]);

  // State
  const [holdings, setHoldings] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [cashBalance, setCashBalance] = useState(0);
  const [cashBalanceUSDT, setCashBalanceUSDT] = useState(0);
  const [cashBalanceTRY, setCashBalanceTRY] = useState(0);
  const [usdTryRate, setUsdTryRate] = useState(0);
  const [portfolioId, setPortfolioId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pricesLoading, setPricesLoading] = useState(false);
  const lastPriceUpdateRef = useRef(null);
  const isDataLoadedRef = useRef(false);
  const [sortConfig, setSortConfig] = useState({ key: 'value', direction: 'desc' });
  const [filterType, setFilterType] = useState('All');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingHolding, setEditingHolding] = useState(null);
  const [newHolding, setNewHolding] = useState({ symbol: '', name: '', type: 'Stock', shares: '', avgCost: '', sector: '' });
  const [editCash, setEditCash] = useState(false);
  const [editCashUSDT, setEditCashUSDT] = useState(false);
  const [editCashTRY, setEditCashTRY] = useState(false);
  const [newCashBalance, setNewCashBalance] = useState('');
  const [newCashBalanceUSDT, setNewCashBalanceUSDT] = useState('');
  const [newCashBalanceTRY, setNewCashBalanceTRY] = useState('');
  const [symbolLoading, setSymbolLoading] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [spyMonthlyReturns, setSpyMonthlyReturns] = useState({});
  const [spyWeeklyPrices, setSpyWeeklyPrices] = useState({});
  const [spyFirstPrice, setSpyFirstPrice] = useState(null);
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Withdrawal editing state
  const [editingWithdrawal, setEditingWithdrawal] = useState(null);
  const [withdrawalAmount, setWithdrawalAmount] = useState('');

  // Manual snapshot state
  const [snapshotSaving, setSnapshotSaving] = useState(false);
  const [snapshotDate, setSnapshotDate] = useState(() => {
    // Default to this Friday
    const now = new Date();
    const day = now.getDay();
    const diff = day <= 5 ? 5 - day : 0;
    const friday = new Date(now);
    friday.setDate(now.getDate() + diff);
    return friday.toISOString().split('T')[0];
  });

  // Import holdings state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importBroker, setImportBroker] = useState('interactive-brokers');
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState('');

  // Transaction tracking state
  const [selectedHolding, setSelectedHolding] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showAddTransactionModal, setShowAddTransactionModal] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [newTransaction, setNewTransaction] = useState({
    type: 'BUY',
    quantity: '',
    price: '',
    transaction_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  // News & Earnings state
  const [news, setNews] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [selectedNewsSymbol, setSelectedNewsSymbol] = useState('All');

  // FMP Fundamentals state
  const [showFundamentalsModal, setShowFundamentalsModal] = useState(false);
  const [fundamentalsData, setFundamentalsData] = useState(null);
  const [fundamentalsLoading, setFundamentalsLoading] = useState(false);
  const [fundamentalsSymbol, setFundamentalsSymbol] = useState(null);
  const [fundamentalsTab, setFundamentalsTab] = useState('overview'); // overview, analyst, financials
  const [fundamentalsExtendedData, setFundamentalsExtendedData] = useState({}); // ratings, estimates, income, cashflow
  const [fundamentalsExtendedLoading, setFundamentalsExtendedLoading] = useState(false);

  // Portfolio Beta state (FMP-powered)
  const [realPortfolioBeta, setRealPortfolioBeta] = useState(null);
  const [betaDetails, setBetaDetails] = useState([]); // eslint-disable-line no-unused-vars
  const [betaLoading, setBetaLoading] = useState(false); // eslint-disable-line no-unused-vars
  const [betaInterpretation, setBetaInterpretation] = useState(null); // eslint-disable-line no-unused-vars

  // Economics state
  const [economicsData, setEconomicsData] = useState(null);
  const [economicsLoading, setEconomicsLoading] = useState(false);

  // Fetch USD/TRY rate
  const fetchUsdTryRate = async () => {
    try {
      const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
      if (response.ok) {
        const data = await response.json();
        setUsdTryRate(data.rates.TRY || 0);
      }
    } catch (error) {
      console.log('USD/TRY rate fetch error:', error);
      setUsdTryRate(35.5);
    }
  };

  // Fetch symbol info (price + company name) from backend
  const fetchSymbolInfo = async (symbol, type) => {
    if (!symbol || symbol.length < 1) return;

    setSymbolLoading(true);
    try {
      const response = await fetch(`${SYMBOL_INFO_API}?symbol=${symbol}&type=${type}`);

      if (response.ok) {
        const data = await response.json();

        if (data.success) {
          // Düşük fiyatlı coinler için akıllı formatlama
          let formattedPrice = '';
          if (data.price) {
            if (data.price >= 1) {
              formattedPrice = data.price.toFixed(2);
            } else if (data.price >= 0.01) {
              formattedPrice = data.price.toFixed(4);
            } else if (data.price >= 0.0001) {
              formattedPrice = data.price.toFixed(6);
            } else {
              formattedPrice = data.price.toFixed(8);
            }
          }
          setNewHolding(prev => ({
            ...prev,
            name: data.name || symbol,
            avgCost: formattedPrice,
            sector: data.sector || 'Other'
          }));
        }
      }
    } catch (error) {
      console.log('Symbol lookup error:', error);
    } finally {
      setSymbolLoading(false);
    }
  };

  // Debounced symbol lookup
  useEffect(() => {
    const timer = setTimeout(() => {
      if (newHolding.symbol && newHolding.symbol.length >= 1) {
        fetchSymbolInfo(newHolding.symbol, newHolding.type);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [newHolding.symbol, newHolding.type]);

  // Load news from Supabase cache
  const loadNews = useCallback(async (symbols) => {
    if (!symbols || symbols.length === 0) return;

    setNewsLoading(true);
    try {
      // Fetch news for portfolio symbols
      const { data: newsData, error: newsError } = await supabase
        .from('news')
        .select('*')
        .in('symbol', symbols)
        .order('published_at', { ascending: false })
        .limit(50);

      if (!newsError && newsData) {
        setNews(newsData);
      }

      // Fetch upcoming earnings
      const { data: earningsData, error: earningsError } = await supabase
        .from('earnings_calendar')
        .select('*')
        .in('symbol', symbols)
        .gte('report_date', new Date().toISOString().split('T')[0])
        .order('report_date', { ascending: true })
        .limit(20);

      if (!earningsError && earningsData) {
        setEarnings(earningsData);
      }
    } catch (error) {
      console.error('Error loading news:', error);
    } finally {
      setNewsLoading(false);
    }
  }, []);

  // Load portfolio data - only runs once on initial mount
  const loadPortfolio = useCallback(async (forceRefresh = false) => {
    if (!user) return;

    // Skip if already loaded and not forcing refresh
    if (isDataLoadedRef.current && !forceRefresh) {
      return;
    }

    setLoading(true);

    try {
      await fetchUsdTryRate();

      // CRITICAL: Order by created_at ASC to always get the OLDEST (original) portfolio
      // This fixes the issue where users with duplicate portfolios see random/empty ones
      let { data: portfolios, error: portError } = await supabase
        .from('portfolios')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true })
        .limit(1);

      if (portError) throw portError;

      let portfolio;
      if (!portfolios || portfolios.length === 0) {
        // Use upsert-like pattern: check again before inserting to prevent race conditions
        // First, try to insert with ON CONFLICT DO NOTHING (if we had unique constraint)
        // For now, just insert - but the ORDER BY fix above ensures we always get the oldest one
        const { data: newPortfolio, error: createError } = await supabase
          .from('portfolios')
          .insert([{ user_id: user.id, cash_balance: 0, cash_balance_usdt: 0, cash_balance_try: 0 }])
          .select()
          .single();

        if (createError) {
          // If insert failed (maybe duplicate), try to fetch again
          console.warn('Portfolio insert failed, retrying fetch:', createError.message);
          const { data: retryPortfolios } = await supabase
            .from('portfolios')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: true })
            .limit(1);

          if (retryPortfolios && retryPortfolios.length > 0) {
            portfolio = retryPortfolios[0];
          } else {
            throw createError;
          }
        } else {
          portfolio = newPortfolio;
        }
      } else {
        portfolio = portfolios[0];
      }

      setPortfolioId(portfolio.id);
      setCashBalance(portfolio.cash_balance || 0);
      setCashBalanceUSDT(portfolio.cash_balance_usdt || 0);
      setCashBalanceTRY(portfolio.cash_balance_try || 0);

      const { data: holdingsData, error: holdError } = await supabase
        .from('holdings')
        .select('*')
        .eq('portfolio_id', portfolio.id);

      if (holdError) throw holdError;
      setHoldings(holdingsData || []);

      const { data: snapshotsData, error: snapError } = await supabase
        .from('snapshots')
        .select('*')
        .eq('portfolio_id', portfolio.id)
        .order('snapshot_date', { ascending: true });

      if (!snapError) {
        setSnapshots(snapshotsData || []);
      }

      if (holdingsData && holdingsData.length > 0) {
        await fetchPrices(holdingsData);

        // Load news for portfolio symbols
        const symbols = holdingsData.filter(h => h.type !== 'Crypto').map(h => h.symbol);
        await loadNews(symbols);
      }
      isDataLoadedRef.current = true;
    } catch (error) {
      console.error('Error loading portfolio:', error);
    } finally {
      setLoading(false);
    }
  }, [user, loadNews]);

  // Fetch real-time prices from backend (cached)
  const fetchPrices = async (holdingsList) => {
    setPricesLoading(true);
    const updatedHoldings = [...holdingsList];

    try {
      // Prepare symbols and types for API call
      const symbols = holdingsList.map(h => h.symbol).join(',');
      const types = holdingsList.map(h => h.type).join(',');

      const response = await fetch(`${PRICES_API}?symbols=${symbols}&types=${types}`);

      if (response.ok) {
        const data = await response.json();

        if (data.success && data.prices) {
          updatedHoldings.forEach(holding => {
            const priceData = data.prices[holding.symbol];
            if (priceData && priceData.price) {
              holding.current_price = priceData.price;
              holding.change_percent = priceData.change_percent;
            }
          });
        }
      }
    } catch (error) {
      console.log('Price fetch error:', error);
    }

    setHoldings(updatedHoldings);
    setPricesLoading(false);
    lastPriceUpdateRef.current = Date.now();
  };

  // Initial load
  useEffect(() => {
    loadPortfolio();
  }, [loadPortfolio]);

  // Fetch SPY historical data for comparison charts
  useEffect(() => {
    const fetchSPYData = async () => {
      try {
        // Fetch from backend API (avoids CORS issues)
        const response = await fetch('/api/spy-history');

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            if (data.monthlyReturns) {
              setSpyMonthlyReturns(data.monthlyReturns);
            }
            if (data.weeklyPrices) {
              setSpyWeeklyPrices(data.weeklyPrices);
            }
            if (data.firstWeeklyPrice) {
              setSpyFirstPrice(data.firstWeeklyPrice);
            }
            console.log('📊 SPY data loaded - Monthly:', Object.keys(data.monthlyReturns || {}).length, 'Weekly prices:', Object.keys(data.weeklyPrices || {}).length);
          }
        } else {
          console.log('SPY API error:', response.status);
        }
      } catch (error) {
        console.log('SPY data fetch error:', error);
      }
    };

    fetchSPYData();
  }, []);

  // Fetch portfolio beta when holdings change (with debounce)
  useEffect(() => {
    if (holdings.length > 0 && !loading) {
      // Small delay to ensure prices are loaded
      const timer = setTimeout(() => {
        fetchPortfolioBeta();
      }, 1000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holdings, loading]);

  // Calculate metrics
  const metrics = useMemo(() => {
    // Holdings value - BIST ve TEFAS için TRY'den USD'ye çevir
    const holdingsValue = holdings.reduce((sum, h) => {
      const price = h.current_price || h.avg_cost || 0;
      const rawValue = h.shares * price;

      // BIST ve TEFAS TL cinsinden, USD'ye çevir
      if ((h.type === 'BIST' || h.type === 'TEFAS') && usdTryRate > 0) {
        return sum + (rawValue / usdTryRate);
      }
      return sum + rawValue;
    }, 0);

    const tryInUsd = usdTryRate > 0 ? cashBalanceTRY / usdTryRate : 0;
    const usdtInUsd = cashBalanceUSDT; // 1 USDT = 1 USD
    const totalCashUsd = cashBalance + usdtInUsd + tryInUsd;
    const totalValue = holdingsValue + totalCashUsd;

    // Calculate total withdrawals from snapshots
    const totalWithdrawals = snapshots.reduce((sum, snap) => sum + (snap.withdrawal || 0), 0);
    const adjustedTotalValue = totalValue + totalWithdrawals;

    // Total cost - BIST ve TEFAS için TRY'den USD'ye çevir
    const totalCost = holdings.reduce((sum, h) => {
      const costValue = h.shares * (h.avg_cost || 0);
      if ((h.type === 'BIST' || h.type === 'TEFAS') && usdTryRate > 0) {
        return sum + (costValue / usdTryRate);
      }
      return sum + costValue;
    }, 0) + totalCashUsd;
    const totalGain = holdingsValue - (totalCost - totalCashUsd);
    const percentGain = totalCost > totalCashUsd ? ((totalGain / (totalCost - totalCashUsd)) * 100) : 0;

    let beta = 0, sharpeRatio = 0, sortinoRatio = 0, volatility = 0, maxDrawdown = 0;

    if (snapshots.length >= 2) {
      // Calculate adjusted values (value + cumulative withdrawals up to that point)
      let cumulativeWithdrawal = 0;
      const adjustedSnapshots = snapshots.map(snap => {
        cumulativeWithdrawal += (snap.withdrawal || 0);
        return {
          ...snap,
          adjusted_value: snap.total_value + cumulativeWithdrawal
        };
      });

      const returns = [];
      for (let i = 1; i < adjustedSnapshots.length; i++) {
        const prevValue = adjustedSnapshots[i-1].adjusted_value;
        const currValue = adjustedSnapshots[i].adjusted_value;
        if (prevValue > 0) {
          returns.push((currValue - prevValue) / prevValue);
        }
      }

      if (returns.length > 0) {
        const avgReturn = returns.reduce((a, b) => a + b, 0) / returns.length;
        const variance = returns.reduce((sum, r) => sum + Math.pow(r - avgReturn, 2), 0) / returns.length;
        volatility = Math.sqrt(variance) * Math.sqrt(52) * 100;

        const riskFreeRate = 0.05;
        const annualizedReturn = avgReturn * 52;
        sharpeRatio = volatility > 0 ? (annualizedReturn - riskFreeRate) / (volatility / 100) : 0;

        // Sortino Ratio - only downside volatility
        const negativeReturns = returns.filter(r => r < 0);
        if (negativeReturns.length > 0) {
          const downsideVariance = negativeReturns.reduce((sum, r) => sum + Math.pow(r, 2), 0) / returns.length;
          const downsideDeviation = Math.sqrt(downsideVariance) * Math.sqrt(52);
          sortinoRatio = downsideDeviation > 0 ? (annualizedReturn - riskFreeRate) / downsideDeviation : 0;
        } else {
          // No negative returns = infinite Sortino, cap at 10
          sortinoRatio = 10;
        }

        // Synthetic beta (fallback if real beta not available)
        beta = 0.7 + (volatility / 100) * 0.3;

        // Max drawdown using adjusted values
        let peak = adjustedSnapshots[0].adjusted_value;
        for (const snap of adjustedSnapshots) {
          if (snap.adjusted_value > peak) peak = snap.adjusted_value;
          const drawdown = ((peak - snap.adjusted_value) / peak) * 100;
          if (drawdown > maxDrawdown) maxDrawdown = drawdown;
        }
      }
    }

    return {
      totalValue, holdingsValue, totalCost, totalGain, percentGain, totalCashUsd, tryInUsd, usdtInUsd,
      totalWithdrawals, adjustedTotalValue,
      // Use real portfolio beta if available, fallback to synthetic
      beta: realPortfolioBeta !== null ? realPortfolioBeta.toFixed(2) : beta.toFixed(2),
      betaSource: realPortfolioBeta !== null ? 'fmp' : 'synthetic',
      sharpeRatio: sharpeRatio.toFixed(2),
      sortinoRatio: sortinoRatio.toFixed(2),
      volatility: volatility.toFixed(1),
      maxDrawdown: maxDrawdown.toFixed(1)
    };
  }, [holdings, cashBalance, cashBalanceUSDT, cashBalanceTRY, usdTryRate, snapshots, realPortfolioBeta]);

  // News sentiment summary
  const sentimentSummary = useMemo(() => {
    const positive = news.filter(n => n.sentiment === 'positive').length;
    const negative = news.filter(n => n.sentiment === 'negative').length;
    const neutral = news.filter(n => n.sentiment === 'neutral').length;
    const total = news.length;

    return { positive, negative, neutral, total };
  }, [news]);

  // Filtered news by symbol
  const filteredNews = useMemo(() => {
    if (selectedNewsSymbol === 'All') return news;
    return news.filter(n => n.symbol === selectedNewsSymbol);
  }, [news, selectedNewsSymbol]);

  // Allocation by individual asset
  const allocationByAsset = useMemo(() => {
    const assets = [];

    // Add cash balances
    if (cashBalance > 0) {
      assets.push({
        name: 'Cash (USD)',
        symbol: 'USD',
        value: cashBalance,
        type: 'Cash'
      });
    }
    if (cashBalanceUSDT > 0) {
      assets.push({
        name: 'Cash (USDT)',
        symbol: 'USDT',
        value: cashBalanceUSDT, // 1 USDT = 1 USD
        type: 'Cash'
      });
    }
    if (metrics.tryInUsd > 0) {
      assets.push({
        name: 'Cash (TRY)',
        symbol: 'TRY',
        value: metrics.tryInUsd,
        type: 'Cash'
      });
    }

    // Add each holding as separate asset
    holdings.forEach(h => {
      const price = h.current_price || h.avg_cost || 0;
      let value = h.shares * price;

      // BIST ve TEFAS için TRY'den USD'ye çevir (tutarlılık için)
      if ((h.type === 'BIST' || h.type === 'TEFAS') && usdTryRate > 0) {
        value = value / usdTryRate;
      }

      if (value > 0) {
        assets.push({
          name: h.symbol,
          symbol: h.symbol,
          value: Math.round(value),
          type: h.type
        });
      }
    });

    // Sort by value descending and calculate percentages
    return assets
      .sort((a, b) => b.value - a.value)
      .map(asset => ({
        ...asset,
        percentage: metrics.totalValue > 0 ? ((asset.value / metrics.totalValue) * 100).toFixed(1) : '0'
      }));
  }, [holdings, cashBalance, cashBalanceUSDT, usdTryRate, metrics.totalValue, metrics.tryInUsd]);

  // Allocation by sector
  const allocationBySector = useMemo(() => {
    const sectorMap = {};
    holdings.forEach(h => {
      const price = h.current_price || h.avg_cost || 0;
      let value = h.shares * price;

      // BIST ve TEFAS için TRY'den USD'ye çevir
      if ((h.type === 'BIST' || h.type === 'TEFAS') && usdTryRate > 0) {
        value = value / usdTryRate;
      }

      const sector = h.sector || 'Other';
      sectorMap[sector] = (sectorMap[sector] || 0) + value;
    });
    return Object.entries(sectorMap)
      .map(([name, value]) => ({ name, value: Math.round(value) }))
      .sort((a, b) => b.value - a.value);
  }, [holdings, usdTryRate]);

  // Top performers
  const topPerformers = useMemo(() => {
    return [...holdings]
      .filter(h => h.current_price && h.avg_cost)
      .map(h => ({
        ...h,
        gain: ((h.current_price - h.avg_cost) / h.avg_cost) * 100
      }))
      .sort((a, b) => b.gain - a.gain)
      .slice(0, 5);
  }, [holdings]);

  // Sorted and filtered holdings
  const displayHoldings = useMemo(() => {
    let filtered = filterType === 'All' ? holdings : holdings.filter(h => h.type === filterType);
    return [...filtered].sort((a, b) => {
      let aVal, bVal;
      const aPrice = a.current_price || a.avg_cost || 0;
      const bPrice = b.current_price || b.avg_cost || 0;

      if (sortConfig.key === 'value') {
        aVal = a.shares * aPrice;
        bVal = b.shares * bPrice;
      } else if (sortConfig.key === 'gain') {
        aVal = a.avg_cost ? (aPrice - a.avg_cost) / a.avg_cost : 0;
        bVal = b.avg_cost ? (bPrice - b.avg_cost) / b.avg_cost : 0;
      } else if (sortConfig.key === 'gainAmount') {
        aVal = (aPrice - (a.avg_cost || 0)) * a.shares;
        bVal = (bPrice - (b.avg_cost || 0)) * b.shares;
      } else if (sortConfig.key === 'weight') {
        aVal = a.shares * aPrice;
        bVal = b.shares * bPrice;
      } else {
        aVal = a[sortConfig.key];
        bVal = b[sortConfig.key];
      }
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [holdings, sortConfig, filterType]);

  // Performance chart data
  const performanceData = useMemo(() => {
    if (snapshots.length === 0) return [];

    // Sort by snapshot_date ascending
    const sortedSnapshots = [...snapshots].sort((a, b) =>
      new Date(a.snapshot_date) - new Date(b.snapshot_date)
    );

    // Calculate cumulative withdrawals for each snapshot
    let cumulativeWithdrawal = 0;
    const snapshotsWithAdjusted = sortedSnapshots.map(snap => {
      cumulativeWithdrawal += (snap.withdrawal || 0);
      return {
        ...snap,
        adjusted_value: snap.total_value + cumulativeWithdrawal,
        cumulative_withdrawal: cumulativeWithdrawal
      };
    });

    const firstValue = snapshotsWithAdjusted[0].total_value;
    const firstAdjustedValue = snapshotsWithAdjusted[0].adjusted_value;

    // Helper function to find closest SPY price for a given date
    const findClosestSpyPrice = (dateStr) => {
      if (!spyWeeklyPrices || Object.keys(spyWeeklyPrices).length === 0) return null;

      const targetDate = new Date(dateStr);
      const spyDates = Object.keys(spyWeeklyPrices).sort();

      // Find the closest date (prefer earlier date within 7 days)
      let closestDate = null;
      let minDiff = Infinity;

      for (const spyDate of spyDates) {
        const diff = Math.abs(new Date(spyDate) - targetDate);
        if (diff < minDiff) {
          minDiff = diff;
          closestDate = spyDate;
        }
      }

      return closestDate ? spyWeeklyPrices[closestDate] : null;
    };

    // Get first SPY price for cumulative return calculation
    const firstSnapshotDate = snapshotsWithAdjusted[0].snapshot_date;
    const firstSpyPrice = findClosestSpyPrice(firstSnapshotDate) || spyFirstPrice;

    return snapshotsWithAdjusted.map((snap) => {
      const portfolioReturn = firstValue > 0 ? ((snap.total_value - firstValue) / firstValue) * 100 : 0;
      const adjustedReturn = firstAdjustedValue > 0 ? ((snap.adjusted_value - firstAdjustedValue) / firstAdjustedValue) * 100 : 0;

      // Calculate real SPY cumulative return from first snapshot
      let spyReturn = 0;
      if (firstSpyPrice && firstSpyPrice > 0) {
        const currentSpyPrice = findClosestSpyPrice(snap.snapshot_date);
        if (currentSpyPrice) {
          spyReturn = ((currentSpyPrice - firstSpyPrice) / firstSpyPrice) * 100;
        }
      }

      return {
        date: new Date(snap.snapshot_date).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', { day: 'numeric', month: 'short' }),
        value: snap.total_value,
        adjustedValue: snap.adjusted_value,
        holdings: snap.total_value - (snap.cash_balance || 0),
        cash: snap.cash_balance || 0,
        withdrawal: snap.withdrawal || 0,
        portfolioReturn: portfolioReturn.toFixed(2),
        adjustedReturn: adjustedReturn.toFixed(2),
        spyReturn: spyReturn.toFixed(2)
      };
    });
  }, [snapshots, spyWeeklyPrices, spyFirstPrice, language]);

  // Monthly performance data for bar chart comparison (Portfolio vs S&P 500)
  const monthlyPerformanceData = useMemo(() => {
    if (snapshots.length < 4) return []; // Need at least a month of data

    const sortedSnapshots = [...snapshots].sort((a, b) =>
      new Date(a.snapshot_date) - new Date(b.snapshot_date)
    );

    // Group snapshots by month
    const monthlyData = {};
    sortedSnapshots.forEach(snap => {
      const date = new Date(snap.snapshot_date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { first: snap, last: snap };
      } else {
        monthlyData[monthKey].last = snap;
      }
    });

    // Calculate monthly returns
    const months = Object.keys(monthlyData).sort();
    const result = [];

    for (let i = 1; i < months.length; i++) {
      const prevMonth = monthlyData[months[i - 1]];
      const currMonth = monthlyData[months[i]];

      // Calculate cumulative withdrawals for adjustment
      let withdrawalInMonth = 0;
      sortedSnapshots.forEach(snap => {
        const snapDate = new Date(snap.snapshot_date);
        const snapMonth = `${snapDate.getFullYear()}-${String(snapDate.getMonth() + 1).padStart(2, '0')}`;
        if (snapMonth === months[i] && snap.withdrawal) {
          withdrawalInMonth += snap.withdrawal;
        }
      });

      // Adjusted return calculation (accounts for withdrawals)
      const prevValue = prevMonth.last.total_value;
      const currValue = currMonth.last.total_value + withdrawalInMonth;
      const portfolioReturn = prevValue > 0 ? ((currValue - prevValue) / prevValue) * 100 : 0;

      // Get S&P 500 return for this month (uses real SPY data from Yahoo Finance)
      const spyReturn = spyMonthlyReturns[months[i]] ?? 0;

      const monthDate = new Date(months[i] + '-01');
      const monthName = monthDate.toLocaleDateString(language === 'TR' ? 'tr-TR' : 'en-US', { month: 'short' });

      result.push({
        month: monthName,
        fullMonth: months[i],
        portfolioReturn: parseFloat(portfolioReturn.toFixed(2)),
        spyReturn: parseFloat(spyReturn.toFixed(2)),
        alpha: parseFloat((portfolioReturn - spyReturn).toFixed(2))
      });
    }

    // Return last 6 months
    return result.slice(-6);
  }, [snapshots, language, spyMonthlyReturns]);

  // Handlers
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // ===== IMPORT FUNCTIONS =====

  // Parse Interactive Brokers CSV/Activity Statement
  const parseInteractiveBrokersCSV = (text) => {
    const lines = text.split('\n');
    const holdings = [];
    let inPositionsSection = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      // IB Activity Statement has sections - look for "Open Positions" or "Positions"
      if (line.includes('Open Positions') || line.includes('Positions,Header')) {
        inPositionsSection = true;
        continue;
      }

      // End of positions section
      if (inPositionsSection && (line.startsWith('Total') || line === '' || line.includes('Notes'))) {
        if (line.startsWith('Total')) continue;
        if (line === '') {
          inPositionsSection = false;
          continue;
        }
      }

      if (!inPositionsSection) continue;

      // Parse CSV line
      const parts = line.split(',').map(p => p.trim().replace(/"/g, ''));

      // IB Format: Data Type, Asset Category, Currency, Symbol, Description, Quantity, Cost Basis, etc.
      // Or simpler: Symbol, Quantity, Cost Basis/Price
      if (parts.length >= 3) {
        // Try to find symbol and quantity
        let symbol = '';
        let quantity = 0;
        let costBasis = 0;
        let assetType = 'Stock';

        // Check if first column is "Positions" or "Data"
        if (parts[0] === 'Positions' || parts[0] === 'Data') {
          // IB Activity Statement format: Data, Asset Category, Currency, Symbol, Description, Quantity, Cost Basis, Close Price, Value, Unrealized P&L
          const category = parts[1];
          symbol = parts[3];
          quantity = parseFloat(parts[5]) || 0;
          costBasis = parseFloat(parts[6]) || 0;

          // Determine type from category
          if (category === 'Stocks') assetType = 'Stock';
          else if (category === 'Equity and Index Options') assetType = 'Stock';
          else if (category === 'Crypto') assetType = 'Crypto';
          else if (category === 'Bonds') assetType = 'Bond';
        } else {
          // Simple CSV format: Symbol, Quantity, Cost Basis
          symbol = parts[0];
          quantity = parseFloat(parts[1]) || 0;
          costBasis = parseFloat(parts[2]) || 0;
        }

        // Validate
        if (symbol && symbol.length > 0 && symbol.length <= 10 && quantity > 0) {
          // Auto-detect type
          const detectedType = detectAssetType(symbol);

          holdings.push({
            symbol: symbol.toUpperCase(),
            name: symbol.toUpperCase(),
            type: detectedType || assetType,
            shares: Math.abs(quantity), // Absolute value for short positions shown as positive
            avgCost: quantity !== 0 ? Math.abs(costBasis / quantity) : 0,
            sector: 'Other'
          });
        }
      }
    }

    return holdings;
  };

  // Parse uploaded file based on broker
  const parseImportFile = async (file, broker) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const text = e.target.result;
          let holdings = [];

          switch (broker) {
            case 'interactive-brokers':
              holdings = parseInteractiveBrokersCSV(text);
              break;
            case 'midas':
              // TODO: Midas parser will be added later
              holdings = [];
              break;
            default:
              // Generic CSV: Symbol, Quantity, Price
              const lines = text.split('\n').slice(1); // Skip header
              holdings = lines.map(line => {
                const [symbol, shares, avgCost] = line.split(',').map(s => s.trim());
                if (!symbol || !shares) return null;
                const detectedType = detectAssetType(symbol);
                return {
                  symbol: symbol.toUpperCase(),
                  name: symbol.toUpperCase(),
                  type: detectedType || 'Stock',
                  shares: parseFloat(shares) || 0,
                  avgCost: parseFloat(avgCost) || 0,
                  sector: 'Other'
                };
              }).filter(h => h && h.shares > 0);
          }

          resolve(holdings);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  // Handle file selection
  const handleImportFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImportFile(file);
    setImportError('');
    setImportLoading(true);

    try {
      const holdings = await parseImportFile(file, importBroker);

      if (holdings.length === 0) {
        setImportError('No holdings found in file. Please check the format.');
      } else {
        setImportPreview(holdings);
      }
    } catch (error) {
      setImportError('Failed to parse file: ' + error.message);
    } finally {
      setImportLoading(false);
    }
  };

  // Import holdings to database
  const handleImportHoldings = async () => {
    if (!portfolioId || importPreview.length === 0) return;

    setImportLoading(true);
    setImportError('');

    try {
      const holdingsToInsert = importPreview.map(h => ({
        portfolio_id: portfolioId,
        symbol: h.symbol,
        name: h.name,
        type: h.type,
        shares: h.shares,
        avg_cost: h.avgCost,
        sector: h.sector
      }));

      const { data, error } = await supabase
        .from('holdings')
        .insert(holdingsToInsert)
        .select();

      if (error) throw error;

      // Update local state
      const newHoldings = data.map(h => ({ ...h, current_price: h.avg_cost }));
      setHoldings([...holdings, ...newHoldings]);

      // Reset import state
      setShowImportModal(false);
      setImportFile(null);
      setImportPreview([]);
      setImportBroker('interactive-brokers');

      // Fetch prices for new holdings
      await fetchPrices([...holdings, ...newHoldings]);

      alert(`Successfully imported ${data.length} holdings!`);
    } catch (error) {
      setImportError('Failed to import: ' + error.message);
    } finally {
      setImportLoading(false);
    }
  };

  // ===== END IMPORT FUNCTIONS =====

  // ===== FMP FUNDAMENTALS FUNCTIONS =====

  // Fetch fundamentals from FMP API
  const fetchFundamentals = async (symbol) => {
    setFundamentalsLoading(true);
    setFundamentalsSymbol(symbol);
    setShowFundamentalsModal(true);
    setFundamentalsTab('overview'); // Reset to default tab
    setFundamentalsExtendedData({}); // Reset extended data

    try {
      const response = await fetch(`/api/fmp/fundamentals?symbol=${symbol}&type=all`);
      const data = await response.json();

      if (data.success) {
        setFundamentalsData(data);
      } else {
        console.error('FMP API error:', data.error);
        setFundamentalsData(null);
      }
    } catch (error) {
      console.error('Error fetching fundamentals:', error);
      setFundamentalsData(null);
    } finally {
      setFundamentalsLoading(false);
    }
  };

  // Fetch extended fundamentals data (ratings, estimates, income, cashflow)
  const fetchExtendedFundamentals = async (symbol, dataType) => {
    // Check if we already have this data
    if (fundamentalsExtendedData[dataType]) {
      return;
    }

    setFundamentalsExtendedLoading(true);
    try {
      const response = await fetch(`/api/fmp/fundamentals?symbol=${symbol}&type=${dataType}`);
      const data = await response.json();

      if (data.success) {
        setFundamentalsExtendedData(prev => ({
          ...prev,
          [dataType]: data[dataType]
        }));
      }
    } catch (error) {
      console.error(`Error fetching ${dataType}:`, error);
    } finally {
      setFundamentalsExtendedLoading(false);
    }
  };

  // Handle tab change and fetch data if needed
  const handleFundamentalsTabChange = async (tab) => {
    setFundamentalsTab(tab);

    if (!fundamentalsSymbol) return;

    // Fetch data based on tab
    if (tab === 'analyst') {
      // Fetch our custom Fair Value calculation
      if (!fundamentalsExtendedData.valuation) {
        await fetchExtendedFundamentals(fundamentalsSymbol, 'valuation');
      }
    } else if (tab === 'financials') {
      if (!fundamentalsExtendedData.income) {
        await fetchExtendedFundamentals(fundamentalsSymbol, 'income');
      }
      if (!fundamentalsExtendedData.cashflow) {
        fetchExtendedFundamentals(fundamentalsSymbol, 'cashflow');
      }
    } else if (tab === 'insider') {
      if (!fundamentalsExtendedData.insider) {
        await fetchExtendedFundamentals(fundamentalsSymbol, 'insider');
      }
    }
  };

  // Check if a symbol is a US stock (eligible for fundamentals)
  const isUSStock = (holding) => {
    return (holding.type === 'Stock' || holding.type === 'ETF') &&
           !KNOWN_BIST_SYMBOLS.includes(holding.symbol) &&
           !KNOWN_TEFAS_SYMBOLS.includes(holding.symbol) &&
           !KNOWN_CRYPTO_SYMBOLS.includes(holding.symbol) &&
           !KNOWN_COMMODITY_SYMBOLS.includes(holding.symbol);
  };

  // Fetch portfolio beta from FMP
  const fetchPortfolioBeta = async () => {
    if (holdings.length === 0) return;

    setBetaLoading(true);
    try {
      // Prepare holdings with current values
      const holdingsWithValues = holdings.map(h => ({
        symbol: h.symbol,
        type: h.type,
        value: h.shares * (h.current_price || h.avg_cost)
      }));

      const response = await fetch('/api/fmp/portfolio-beta', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ holdings: holdingsWithValues })
      });

      const data = await response.json();

      if (data.success) {
        setRealPortfolioBeta(parseFloat(data.portfolioBeta));
        setBetaDetails(data.details || []);
        setBetaInterpretation(data.interpretation);
        console.log('📊 Portfolio Beta:', data.portfolioBeta, data.interpretation);
      }
    } catch (error) {
      console.error('Error fetching portfolio beta:', error);
    } finally {
      setBetaLoading(false);
    }
  };

  // Fetch economics data (treasury rates, calendar, news)
  const fetchEconomicsData = async () => {
    if (economicsLoading) return;
    setEconomicsLoading(true);
    try {
      const response = await fetch('/api/economics?type=all');
      const data = await response.json();
      if (data.success) {
        setEconomicsData(data);
        console.log('📊 Economics data loaded:', {
          treasury: data.treasury ? 'OK' : 'N/A',
          calendar: data.calendar?.data?.length || 0,
          news: data.news?.data?.length || 0
        });
      }
    } catch (error) {
      console.error('Economics fetch error:', error);
    } finally {
      setEconomicsLoading(false);
    }
  };

  // ===== TRANSACTION FUNCTIONS =====

  // Calculate average cost from transactions
  const calculateAverageCost = (txns) => {
    let totalShares = 0;
    let totalCost = 0;

    txns.forEach(t => {
      if (t.type === 'BUY') {
        totalCost += t.quantity * t.price;
        totalShares += t.quantity;
      } else if (t.type === 'SELL') {
        // For average cost method: reduce shares, adjust cost proportionally
        const avgCostPerShare = totalShares > 0 ? totalCost / totalShares : 0;
        totalCost -= t.quantity * avgCostPerShare;
        totalShares -= t.quantity;
      }
    });

    return {
      shares: Math.max(0, totalShares),
      avgCost: totalShares > 0 ? totalCost / totalShares : 0
    };
  };

  // Fetch transactions for a holding
  const fetchTransactions = async (holdingId) => {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('holding_id', holdingId)
        .order('transaction_date', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching transactions:', error);
      return [];
    }
  };

  // Handle clicking on a holding to view transactions
  const handleHoldingClick = async (holding) => {
    setSelectedHolding(holding);
    setTransactionsLoading(true);
    setShowTransactionModal(true);

    const txns = await fetchTransactions(holding.id);
    setTransactions(txns);
    setTransactionsLoading(false);
  };

  // Handle adding a new transaction
  const handleAddTransaction = async () => {
    if (!selectedHolding || !newTransaction.quantity || !newTransaction.price) return;

    try {
      const { data, error } = await supabase
        .from('transactions')
        .insert([{
          holding_id: selectedHolding.id,
          portfolio_id: portfolioId,
          type: newTransaction.type,
          quantity: parseFloat(newTransaction.quantity),
          price: parseFloat(newTransaction.price),
          transaction_date: newTransaction.transaction_date,
          notes: newTransaction.notes || null
        }])
        .select()
        .single();

      if (error) throw error;

      // Update transactions list
      const allTransactions = [...transactions, data].sort(
        (a, b) => new Date(a.transaction_date) - new Date(b.transaction_date)
      );
      setTransactions(allTransactions);

      // Recalculate avg_cost and shares
      const { shares, avgCost } = calculateAverageCost(allTransactions);

      // Update holding in database
      const { error: updateError } = await supabase
        .from('holdings')
        .update({ shares, avg_cost: avgCost })
        .eq('id', selectedHolding.id);

      if (updateError) throw updateError;

      // Update local holdings state
      setHoldings(holdings.map(h =>
        h.id === selectedHolding.id
          ? { ...h, shares, avg_cost: avgCost }
          : h
      ));

      // Update selected holding
      setSelectedHolding({ ...selectedHolding, shares, avg_cost: avgCost });

      // Reset form and close modal
      setNewTransaction({
        type: 'BUY',
        quantity: '',
        price: '',
        transaction_date: new Date().toISOString().split('T')[0],
        notes: ''
      });
      setShowAddTransactionModal(false);

    } catch (error) {
      console.error('Error adding transaction:', error);
      alert('Error adding transaction: ' + error.message);
    }
  };

  // Delete a transaction
  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm('Bu işlemi silmek istediğinize emin misiniz?')) return;

    try {
      const { error } = await supabase
        .from('transactions')
        .delete()
        .eq('id', transactionId);

      if (error) throw error;

      // Update transactions list
      const remainingTransactions = transactions.filter(t => t.id !== transactionId);
      setTransactions(remainingTransactions);

      // Recalculate avg_cost and shares
      const { shares, avgCost } = calculateAverageCost(remainingTransactions);

      // Update holding in database
      await supabase
        .from('holdings')
        .update({ shares, avg_cost: avgCost })
        .eq('id', selectedHolding.id);

      // Update local state
      setHoldings(holdings.map(h =>
        h.id === selectedHolding.id
          ? { ...h, shares, avg_cost: avgCost }
          : h
      ));
      setSelectedHolding({ ...selectedHolding, shares, avg_cost: avgCost });

    } catch (error) {
      console.error('Error deleting transaction:', error);
      alert('Error deleting transaction: ' + error.message);
    }
  };

  // ===== END TRANSACTION FUNCTIONS =====

  const handleAddHolding = async () => {
    if (!newHolding.symbol || !newHolding.shares || !portfolioId) return;

    try {
      const holdingData = {
        portfolio_id: portfolioId,
        symbol: newHolding.symbol.toUpperCase(),
        name: newHolding.name || newHolding.symbol.toUpperCase(),
        type: newHolding.type,
        shares: parseFloat(newHolding.shares),
        avg_cost: parseFloat(newHolding.avgCost) || 0,
        sector: newHolding.sector || 'Other'
      };

      const { data, error } = await supabase
        .from('holdings')
        .insert([holdingData])
        .select()
        .single();

      if (error) throw error;

      const newHoldingWithPrice = { ...data, current_price: data.avg_cost };
      setHoldings([...holdings, newHoldingWithPrice]);
      setNewHolding({ symbol: '', name: '', type: 'Stock', shares: '', avgCost: '', sector: '' });
      setShowAddModal(false);

      await fetchPrices([...holdings, newHoldingWithPrice]);

      // Reload news with new symbol
      const symbols = [...holdings, newHoldingWithPrice].filter(h => h.type !== 'Crypto').map(h => h.symbol);
      await loadNews(symbols);
    } catch (error) {
      console.error('Error adding holding:', error);
      alert('Error adding holding: ' + error.message);
    }
  };

  const handleEditHolding = (holding) => {
    setEditingHolding({
      ...holding,
      shares: holding.shares.toString(),
      avgCost: (holding.avg_cost || 0).toString(),
      sector: holding.sector || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateHolding = async () => {
    if (!editingHolding) return;

    try {
      const updateData = {
        symbol: editingHolding.symbol,
        name: editingHolding.name,
        type: editingHolding.type,
        shares: parseFloat(editingHolding.shares),
        avg_cost: parseFloat(editingHolding.avgCost) || 0,
        sector: editingHolding.sector || 'Other'
      };

      const { error } = await supabase
        .from('holdings')
        .update(updateData)
        .eq('id', editingHolding.id);

      if (error) throw error;

      setHoldings(holdings.map(h =>
        h.id === editingHolding.id
          ? { ...h, ...updateData, current_price: h.current_price }
          : h
      ));
      setShowEditModal(false);
      setEditingHolding(null);
    } catch (error) {
      console.error('Error updating holding:', error);
      alert('Error updating holding: ' + error.message);
    }
  };

  const handleDeleteHolding = async (id) => {
    if (!window.confirm('Are you sure you want to delete this holding?')) return;

    try {
      const { error } = await supabase
        .from('holdings')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setHoldings(holdings.filter(h => h.id !== id));
    } catch (error) {
      console.error('Error deleting holding:', error);
      alert('Error deleting holding: ' + error.message);
    }
  };

  const handleUpdateCash = async () => {
    if (!portfolioId) return;

    try {
      const newCash = parseFloat(newCashBalance) || 0;
      const { error } = await supabase
        .from('portfolios')
        .update({ cash_balance: newCash })
        .eq('id', portfolioId);

      if (error) throw error;
      setCashBalance(newCash);
      setEditCash(false);
    } catch (error) {
      console.error('Error updating cash:', error);
      alert('Error updating cash: ' + error.message);
    }
  };

  const handleUpdateCashTRY = async () => {
    if (!portfolioId) return;

    try {
      const newCash = parseFloat(newCashBalanceTRY) || 0;
      const { error } = await supabase
        .from('portfolios')
        .update({ cash_balance_try: newCash })
        .eq('id', portfolioId);

      if (error) throw error;
      setCashBalanceTRY(newCash);
      setEditCashTRY(false);
    } catch (error) {
      console.error('Error updating TRY cash:', error);
      alert('Error updating TRY cash: ' + error.message);
    }
  };

  const handleUpdateCashUSDT = async () => {
    if (!portfolioId) return;

    try {
      const newCash = parseFloat(newCashBalanceUSDT) || 0;
      const { error } = await supabase
        .from('portfolios')
        .update({ cash_balance_usdt: newCash })
        .eq('id', portfolioId);

      if (error) throw error;
      setCashBalanceUSDT(newCash);
      setEditCashUSDT(false);
    } catch (error) {
      console.error('Error updating USDT:', error);
      alert('Error updating USDT: ' + error.message);
    }
  };

  // Save withdrawal for a snapshot
  const saveWithdrawal = async (snapshotId) => {
    try {
      const amount = parseFloat(withdrawalAmount) || 0;
      const { error } = await supabase
        .from('snapshots')
        .update({ withdrawal: amount })
        .eq('id', snapshotId);

      if (error) throw error;

      // Update local state
      setSnapshots(prev => prev.map(snap =>
        snap.id === snapshotId ? { ...snap, withdrawal: amount } : snap
      ));
      setEditingWithdrawal(null);
      setWithdrawalAmount('');
    } catch (error) {
      console.error('Error saving withdrawal:', error);
      alert('Error saving withdrawal: ' + error.message);
    }
  };

  // Save manual snapshot
  const saveManualSnapshot = async () => {
    if (!portfolioId || !snapshotDate) return;

    // Check if snapshot already exists for this date
    const existingSnap = snapshots.find(s => s.snapshot_date === snapshotDate);
    if (existingSnap) {
      if (!window.confirm(language === 'tr'
        ? `${snapshotDate} tarihinde zaten bir snapshot var. Üzerine yazmak istiyor musunuz?`
        : `A snapshot already exists for ${snapshotDate}. Do you want to overwrite it?`)) {
        return;
      }
      // Delete existing
      await supabase.from('snapshots').delete().eq('id', existingSnap.id);
    }

    setSnapshotSaving(true);
    try {
      const holdingsData = holdings.map(h => ({
        symbol: h.symbol,
        shares: h.shares,
        price: h.current_price || h.avg_cost || 0,
        type: h.type || 'US'
      }));

      const { data, error } = await supabase
        .from('snapshots')
        .insert([{
          portfolio_id: portfolioId,
          snapshot_date: snapshotDate,
          total_value: metrics.totalValue,
          cash_balance: metrics.totalCashUsd,
          holdings_data: JSON.stringify(holdingsData),
          withdrawal: 0,
          beta: 0,
          sharpe_ratio: 0,
          volatility: 0
        }])
        .select()
        .single();

      if (error) throw error;

      // Update local state
      const newSnapshots = [...snapshots.filter(s => s.snapshot_date !== snapshotDate), data]
        .sort((a, b) => new Date(a.snapshot_date) - new Date(b.snapshot_date));
      setSnapshots(newSnapshots);

      alert(language === 'tr' ? 'Snapshot başarıyla kaydedildi!' : 'Snapshot saved successfully!');
    } catch (error) {
      console.error('Manual snapshot error:', error);
      alert((language === 'tr' ? 'Snapshot kaydedilemedi: ' : 'Failed to save snapshot: ') + error.message);
    }
    setSnapshotSaving(false);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);
  };

  // Düşük fiyatlı coinler için akıllı fiyat formatı (SHIB gibi)
  const formatPrice = (value) => {
    if (!value || value === 0) return '$0.00';
    const absValue = Math.abs(value);

    if (absValue >= 1) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
    } else if (absValue >= 0.01) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 4 }).format(value);
    } else if (absValue >= 0.0001) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 6 }).format(value);
    } else {
      // Çok düşük fiyatlar için (SHIB gibi)
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 8 }).format(value);
    }
  };

  const formatTRY = (value) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value || 0);
  };

  const formatPercent = (value) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${(value || 0).toFixed(2)}%`;
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Custom tooltip for pie chart
  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-neutral-800 border border-neutral-600 rounded-xl px-4 py-3 shadow-xl">
          <p className="text-amber-400 font-semibold text-sm">{data.name}</p>
          <p className="text-neutral-100 font-bold">{formatCurrency(data.value)}</p>
          <p className="text-neutral-400 text-sm">{data.payload.percentage}% of portfolio</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
        <div className="text-amber-500 text-xl flex items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin" />
          Loading your portfolio...
        </div>
      </div>
    );
  }

  // ===== TAB CONTENT COMPONENTS =====

  // Dashboard Tab Content
  const DashboardContent = () => (
    <>
      {/* Key Metrics - First Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
        {/* Total Value */}
        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-3xl p-6 border border-neutral-800/50 hover:border-amber-500/30 transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <span className="text-neutral-500 text-sm font-medium">{t.totalValue}</span>
              <InfoTooltip text={t.tooltips.totalValue} />
            </div>
            <div className="w-10 h-10 rounded-2xl bg-amber-500/10 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-amber-500" />
            </div>
          </div>
          <div className="text-2xl md:text-3xl font-bold text-neutral-50">{formatCurrency(metrics.totalValue)}</div>
          {usdTryRate > 0 && (
            <div className="text-sm text-neutral-500 mt-1">{formatTRY(metrics.totalValue * usdTryRate)}</div>
          )}
        </div>

        {/* Holdings Value */}
        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-3xl p-6 border border-neutral-800/50 hover:border-amber-500/30 transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <span className="text-neutral-500 text-sm font-medium">{t.holdingsValue}</span>
              <InfoTooltip text={t.tooltips.holdingsValue} />
            </div>
            <div className="w-10 h-10 rounded-2xl bg-amber-500/10 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-amber-500" />
            </div>
          </div>
          <div className="text-2xl md:text-3xl font-bold text-amber-400">{formatCurrency(metrics.holdingsValue)}</div>
          <div className="text-sm text-neutral-500 mt-1">{holdings.length} {t.assets}</div>
        </div>

        {/* Total Cash */}
        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-3xl p-6 border border-neutral-800/50 hover:border-emerald-500/30 transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <span className="text-neutral-500 text-sm font-medium">{t.totalCash}</span>
              <InfoTooltip text={t.tooltips.totalCash} />
            </div>
            <div className="w-10 h-10 rounded-2xl bg-emerald-500/10 flex items-center justify-center">
              <PiggyBank className="w-5 h-5 text-emerald-400" />
            </div>
          </div>
          <div className="text-2xl md:text-3xl font-bold text-emerald-400">{formatCurrency(metrics.totalCashUsd)}</div>
          <div className="text-sm text-neutral-500 mt-1">{t.combined}</div>
        </div>

        {/* Total Gain */}
        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-3xl p-6 border border-neutral-800/50 hover:border-amber-500/30 transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <span className="text-neutral-500 text-sm font-medium">{t.totalGainLoss}</span>
              <InfoTooltip text={t.tooltips.totalGainLoss} />
            </div>
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${metrics.totalGain >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
              {metrics.totalGain >= 0 ? <TrendingUp className="w-5 h-5 text-emerald-400" /> : <TrendingDown className="w-5 h-5 text-red-400" />}
            </div>
          </div>
          <div className={`text-2xl md:text-3xl font-bold ${metrics.totalGain >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {formatCurrency(metrics.totalGain)}
          </div>
          <div className={`text-sm ${metrics.totalGain >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {formatPercent(metrics.percentGain)}
          </div>
        </div>

        {/* Realized (Withdrawals) */}
        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-3xl p-6 border border-neutral-800/50 hover:border-purple-500/30 transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <span className="text-neutral-500 text-sm font-medium">{t.realized}</span>
              <InfoTooltip text={t.tooltips.realized} />
            </div>
            <div className="w-10 h-10 rounded-2xl bg-purple-500/10 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-purple-400" />
            </div>
          </div>
          <div className="text-2xl md:text-3xl font-bold text-purple-400">{formatCurrency(metrics.totalWithdrawals)}</div>
          <div className="text-sm text-neutral-500 mt-1">{t.adjusted}: {formatCurrency(metrics.adjustedTotalValue)}</div>
        </div>
      </div>

      {/* Financial Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-2xl p-4 border border-neutral-800/50">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-amber-500" />
            <span className="text-neutral-500 text-xs font-medium">{t.bestPerformer}</span>
            <InfoTooltip text={t.tooltips.topPerformers} />
          </div>
          <div className="text-lg font-bold text-neutral-50">{topPerformers[0]?.symbol || '-'}</div>
          <div className="text-xs text-emerald-400">{topPerformers[0] ? formatPercent(topPerformers[0].gain) : '-'}</div>
        </div>
        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-2xl p-4 border border-neutral-800/50">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 text-amber-500" />
            <span className="text-neutral-500 text-xs font-medium">{t.beta}</span>
            <InfoTooltip text={t.tooltips.beta} />
          </div>
          <div className="text-lg font-bold text-neutral-50">{metrics.beta}</div>
        </div>
        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-2xl p-4 border border-neutral-800/50">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-amber-500" />
            <span className="text-neutral-500 text-xs font-medium">{t.sharpeRatio}</span>
            <InfoTooltip text={t.tooltips.sharpeRatio} />
          </div>
          <div className="text-lg font-bold text-neutral-50">{metrics.sharpeRatio}</div>
        </div>
        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-2xl p-4 border border-neutral-800/50">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-emerald-400" />
            <span className="text-neutral-500 text-xs font-medium">{t.sortinoRatio}</span>
            <InfoTooltip text={t.tooltips.sortinoRatio} />
          </div>
          <div className="text-lg font-bold text-emerald-400">{metrics.sortinoRatio}</div>
        </div>
        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-2xl p-4 border border-neutral-800/50">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="w-4 h-4 text-amber-500" />
            <span className="text-neutral-500 text-xs font-medium">{t.volatility}</span>
            <InfoTooltip text={t.tooltips.volatility} />
          </div>
          <div className="text-lg font-bold text-neutral-50">{metrics.volatility}%</div>
        </div>
        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-2xl p-4 border border-neutral-800/50">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4 text-red-400" />
            <span className="text-neutral-500 text-xs font-medium">{t.maxDrawdown}</span>
            <InfoTooltip text={t.tooltips.maxDrawdown} />
          </div>
          <div className="text-lg font-bold text-red-400">-{metrics.maxDrawdown}%</div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Performance Chart */}
        <div className="lg:col-span-2 bg-neutral-900/50 backdrop-blur-sm rounded-3xl p-6 border border-neutral-800/50">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-neutral-50">{t.performanceHistory}</h2>
            <button
              onClick={() => setShowComparison(!showComparison)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all ${
                showComparison
                  ? 'bg-blue-500/20 text-blue-400'
                  : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700'
              }`}
            >
              <TrendingIcon className="w-4 h-4" />
              {t.vsSp500}
            </button>
          </div>
          {performanceData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              {showComparison ? (
                <ComposedChart data={performanceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#262626" vertical={false} />
                  <XAxis dataKey="date" stroke="#525252" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="#525252" fontSize={12} tickFormatter={(v) => `${v}%`} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#262626', border: '1px solid #404040', borderRadius: '12px' }}
                    labelStyle={{ color: '#a3a3a3' }}
                    itemStyle={{ color: '#fbbf24' }}
                    formatter={(value, name) => [`${value}%`, name === 'portfolioReturn' ? 'Your Portfolio' : 'S&P 500']}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="portfolioReturn" name="Your Portfolio" stroke={AMBER[500]} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="spyReturn" name="S&P 500" stroke="#3b82f6" strokeWidth={2} dot={false} strokeDasharray="5 5" />
                </ComposedChart>
              ) : (
                <AreaChart data={performanceData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={AMBER[500]} stopOpacity={0.4}/>
                      <stop offset="95%" stopColor={AMBER[500]} stopOpacity={0.05}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#262626" vertical={false} />
                  <XAxis dataKey="date" stroke="#525252" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis
                    stroke="#525252"
                    fontSize={12}
                    tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`}
                    tickLine={false}
                    axisLine={false}
                    domain={[(dataMin) => Math.floor(dataMin * 0.95), (dataMax) => Math.ceil(dataMax * 1.05)]}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#262626', border: '1px solid #404040', borderRadius: '12px' }}
                    labelStyle={{ color: '#a3a3a3' }}
                    itemStyle={{ color: '#fbbf24' }}
                    formatter={(value, name) => [formatCurrency(value), name === 'value' ? 'Total' : name === 'holdings' ? 'Holdings' : 'Cash']}
                  />
                  <Area type="monotone" dataKey="value" name="Total Value" stroke={AMBER[500]} strokeWidth={2} fill="url(#colorValue)" />
                </AreaChart>
              )}
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-neutral-500">
              <div className="text-center">
                <BarChart3 className="w-12 h-12 mx-auto mb-3 text-neutral-600" />
                <p>Performance data will appear after weekly snapshots.</p>
              </div>
            </div>
          )}
        </div>

        {/* Allocation - Pro Design */}
        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-3xl p-6 border border-neutral-800/50">
          <h2 className="text-xl font-semibold text-neutral-50 mb-4">{t.allocation}</h2>

          {/* Donut Chart with Center Value */}
          <div className="relative">
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie
                  data={allocationByAsset}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={70}
                  paddingAngle={1}
                  dataKey="value"
                  stroke="none"
                >
                  {allocationByAsset.map((entry, index) => (
                    <Cell
                      key={entry.name}
                      fill={
                        entry.name === 'Cash (USD)' ? '#10b981' :
                        entry.name === 'Cash (USDT)' ? '#22d3ee' :
                        entry.name === 'Cash (TRY)' ? '#ef4444' :
                        entry.type === 'Crypto' ? AMBER[300] :
                        entry.type === 'BIST' ? AMBER[700] :
                        entry.type === 'TEFAS' ? AMBER[200] :
                        entry.type === 'ETF' ? AMBER[400] :
                        COLORS[index % COLORS.length]
                      }
                    />
                  ))}
                </Pie>
                <Tooltip
                  content={<CustomPieTooltip />}
                  position={{ x: 0, y: -10 }}
                  wrapperStyle={{ zIndex: 100 }}
                />
              </PieChart>
            </ResponsiveContainer>
            {/* Center text - Total Value */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <div className="text-[10px] text-neutral-500 uppercase tracking-wide">Total</div>
                <div className="text-sm font-bold text-amber-400">{formatCurrency(metrics.totalValue).replace('$', '')}</div>
              </div>
            </div>
          </div>

          {/* Pro Legend - Horizontal Bars */}
          <div className="mt-4 space-y-2 max-h-[180px] overflow-y-auto pr-1">
            {allocationByAsset.map((item, index) => {
              const barColor = item.name === 'Cash (USD)' ? '#10b981' :
                item.name === 'Cash (USDT)' ? '#22d3ee' :
                item.name === 'Cash (TRY)' ? '#ef4444' :
                item.type === 'Crypto' ? AMBER[300] :
                item.type === 'BIST' ? AMBER[700] :
                item.type === 'TEFAS' ? AMBER[200] :
                item.type === 'ETF' ? AMBER[400] :
                COLORS[index % COLORS.length];

              return (
                <div key={item.name} className="group">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-neutral-200 text-xs font-medium">{item.name}</span>
                      <span className="text-neutral-600 text-xs">{item.type}</span>
                    </div>
                    <span className="text-neutral-100 text-xs font-semibold">{item.percentage}%</span>
                  </div>
                  <div className="h-1.5 bg-neutral-800 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.min(parseFloat(item.percentage), 100)}%`,
                        backgroundColor: barColor
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Monthly Performance Comparison - Portfolio vs S&P 500 */}
      {monthlyPerformanceData.length > 0 && (
        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-3xl p-6 border border-neutral-800/50 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-neutral-50">{t.monthlyComparison}</h2>
                <p className="text-sm text-neutral-500">{t.portfolio} {t.vsBenchmark}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-amber-500"></div>
                <span className="text-neutral-400">{t.portfolio}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-cyan-400"></div>
                <span className="text-neutral-400">S&P 500</span>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={monthlyPerformanceData} barCategoryGap="20%" margin={{ top: 25, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#262626" vertical={false} />
              <XAxis dataKey="month" stroke="#525252" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis
                stroke="#525252"
                fontSize={12}
                tickFormatter={(v) => `${v}%`}
                tickLine={false}
                axisLine={false}
                domain={['auto', 'auto']}
              />
              <Tooltip
                contentStyle={{ backgroundColor: '#171717', border: '1px solid #404040', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }}
                labelStyle={{ color: '#fbbf24', fontWeight: 600, marginBottom: '8px' }}
                itemStyle={{ color: '#e5e5e5' }}
                cursor={{ fill: 'rgba(251, 191, 36, 0.1)' }}
                formatter={(value, name) => [
                  <span style={{ color: name === 'portfolioReturn' ? '#fbbf24' : '#22d3ee' }}>
                    {value > 0 ? '+' : ''}{value}%
                  </span>,
                  name === 'portfolioReturn' ? t.portfolio : 'S&P 500'
                ]}
              />
              <Bar dataKey="portfolioReturn" name="portfolioReturn" fill={AMBER[500]} radius={[4, 4, 0, 0]}>
                <LabelList dataKey="portfolioReturn" position="top" formatter={(v) => `${v > 0 ? '+' : ''}${v}%`} fill="#fbbf24" fontSize={10} fontWeight={600} />
              </Bar>
              <Bar dataKey="spyReturn" name="spyReturn" fill="#22d3ee" radius={[4, 4, 0, 0]}>
                <LabelList dataKey="spyReturn" position="top" formatter={(v) => `${v > 0 ? '+' : ''}${v}%`} fill="#22d3ee" fontSize={10} fontWeight={600} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          {/* Alpha Summary */}
          <div className="mt-4 flex items-center justify-center gap-6 text-sm">
            {monthlyPerformanceData.slice(-2).map((month, idx) => (
              <div key={month.fullMonth} className="flex items-center gap-2">
                <span className="text-neutral-500">{month.month}:</span>
                <span className={month.alpha >= 0 ? 'text-emerald-400 font-semibold' : 'text-red-400 font-semibold'}>
                  {month.alpha >= 0 ? '+' : ''}{month.alpha}% {t.alpha}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Performers & Sector Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performers */}
        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-3xl p-6 border border-neutral-800/50">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <Target className="w-4 h-4 text-amber-500" />
            </div>
            <h2 className="text-xl font-semibold text-neutral-50">{t.topPerformers}</h2>
          </div>
          <div className="space-y-3">
            {topPerformers.length > 0 ? topPerformers.map((holding, index) => (
              <div key={holding.id} className="flex items-center justify-between p-3 rounded-2xl hover:bg-neutral-800/30 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500/20 to-amber-600/20 border border-amber-500/20 flex items-center justify-center text-amber-500 font-bold text-sm">
                    {index + 1}
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-100 text-sm">{holding.symbol}</div>
                    <div className="text-xs text-neutral-500">{holding.name}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-emerald-400 font-semibold text-sm">{formatPercent(holding.gain)}</div>
                  <div className="text-xs text-neutral-500">{formatCurrency(holding.current_price)}</div>
                </div>
              </div>
            )) : (
              <div className="text-center text-neutral-500 py-6">Add holdings to see top performers</div>
            )}
          </div>
        </div>

        {/* Sector Breakdown */}
        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-3xl p-6 border border-neutral-800/50">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <BarChart3 className="w-4 h-4 text-amber-500" />
            </div>
            <h2 className="text-xl font-semibold text-neutral-50">Sector Breakdown</h2>
          </div>
          <div className="space-y-4">
            {allocationBySector.length > 0 ? allocationBySector.slice(0, 6).map((sector) => {
              const percentage = metrics.holdingsValue > 0 ? (sector.value / metrics.holdingsValue) * 100 : 0;
              return (
                <div key={sector.name}>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="text-neutral-300 font-medium">{sector.name}</span>
                    <span className="text-neutral-500">{percentage.toFixed(1)}%</span>
                  </div>
                  <div className="h-2 bg-neutral-800 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700 ease-out" style={{ width: `${percentage}%`, background: `linear-gradient(90deg, ${AMBER[600]}, ${AMBER[400]})` }} />
                  </div>
                </div>
              );
            }) : (
              <div className="text-center text-neutral-500 py-6">Add holdings to see sector breakdown</div>
            )}
          </div>
        </div>
      </div>
    </>
  );

  // Holdings Tab Content
  const HoldingsContent = () => (
    <>
      {/* Cash Balances Section */}
      <div className="bg-neutral-900/50 backdrop-blur-sm rounded-3xl p-4 sm:p-6 border border-neutral-800/50 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-xl bg-emerald-500/10 flex items-center justify-center">
            <PiggyBank className="w-4 h-4 text-emerald-400" />
          </div>
          <h2 className="text-lg font-semibold text-neutral-50">{t.cashBalances}</h2>
          {usdTryRate > 0 && (
            <span className="text-xs text-neutral-500 ml-auto">1 USD = {usdTryRate.toFixed(2)} TRY</span>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* USD Cash */}
          <div className="p-4 rounded-2xl bg-neutral-800/30 border border-neutral-800/50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-400" />
                <span className="text-sm font-medium text-neutral-300">{t.usDollar}</span>
              </div>
            </div>
            {editCash ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  inputMode="decimal"
                  value={newCashBalance}
                  onChange={(e) => setNewCashBalance(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleUpdateCash()}
                  className="flex-1 bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white text-lg focus:outline-none focus:border-emerald-500"
                  placeholder="0.00"
                  autoFocus
                />
                <button onClick={handleUpdateCash} className="p-2 bg-emerald-500 rounded-lg hover:bg-emerald-400 transition-colors">
                  <Check className="w-4 h-4 text-white" />
                </button>
                <button onClick={() => setEditCash(false)} className="p-2 bg-neutral-700 rounded-lg hover:bg-neutral-600 transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="text-2xl font-bold text-emerald-400">{formatCurrency(cashBalance)}</div>
                <button
                  onClick={() => { setEditCash(true); setNewCashBalance(cashBalance.toString()); }}
                  className="p-2 bg-neutral-800 hover:bg-neutral-700 text-neutral-400 rounded-lg transition-all"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* USDT Cash */}
          <div className="p-4 rounded-2xl bg-neutral-800/30 border border-neutral-800/50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-lg text-cyan-400 font-bold">₮</span>
                <span className="text-sm font-medium text-neutral-300">{t.usdt}</span>
              </div>
            </div>
            {editCashUSDT ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  inputMode="decimal"
                  value={newCashBalanceUSDT}
                  onChange={(e) => setNewCashBalanceUSDT(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleUpdateCashUSDT()}
                  className="flex-1 bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white text-lg focus:outline-none focus:border-cyan-500"
                  placeholder="0.00"
                  autoFocus
                />
                <button onClick={handleUpdateCashUSDT} className="p-2 bg-cyan-500 rounded-lg hover:bg-cyan-400 transition-colors">
                  <Check className="w-4 h-4 text-white" />
                </button>
                <button onClick={() => setEditCashUSDT(false)} className="p-2 bg-neutral-700 rounded-lg hover:bg-neutral-600 transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-cyan-400">{cashBalanceUSDT.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT</div>
                  <div className="text-xs text-neutral-500">≈ {formatCurrency(cashBalanceUSDT)}</div>
                </div>
                <button
                  onClick={() => { setEditCashUSDT(true); setNewCashBalanceUSDT(cashBalanceUSDT.toString()); }}
                  className="p-2 bg-neutral-800 hover:bg-neutral-700 text-neutral-400 rounded-lg transition-all"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* TRY Cash */}
          <div className="p-4 rounded-2xl bg-neutral-800/30 border border-neutral-800/50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-lg text-red-400">₺</span>
                <span className="text-sm font-medium text-neutral-300">{t.turkishLira}</span>
              </div>
            </div>
            {editCashTRY ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  inputMode="decimal"
                  value={newCashBalanceTRY}
                  onChange={(e) => setNewCashBalanceTRY(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleUpdateCashTRY()}
                  className="flex-1 bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white text-lg focus:outline-none focus:border-red-500"
                  placeholder="0.00"
                  autoFocus
                />
                <button onClick={handleUpdateCashTRY} className="p-2 bg-red-500 rounded-lg hover:bg-red-400 transition-colors">
                  <Check className="w-4 h-4 text-white" />
                </button>
                <button onClick={() => setEditCashTRY(false)} className="p-2 bg-neutral-700 rounded-lg hover:bg-neutral-600 transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-red-400">{formatTRY(cashBalanceTRY)}</div>
                  {usdTryRate > 0 && cashBalanceTRY > 0 && (
                    <div className="text-xs text-neutral-500">≈ {formatCurrency(cashBalanceTRY / usdTryRate)}</div>
                  )}
                </div>
                <button
                  onClick={() => { setEditCashTRY(true); setNewCashBalanceTRY(cashBalanceTRY.toString()); }}
                  className="p-2 bg-neutral-800 hover:bg-neutral-700 text-neutral-400 rounded-lg transition-all"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Holdings Table */}
      <div className="bg-neutral-900/50 backdrop-blur-sm rounded-3xl border border-neutral-800/50 overflow-hidden">
        <div className="p-4 sm:p-6 border-b border-neutral-800/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-neutral-50">Holdings ({holdings.length})</h2>
            <p className="text-sm text-neutral-500 mt-1">{t.manageAssets}</p>
          </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {['All', 'Stock', 'ETF', 'BIST', 'TEFAS', 'Crypto', 'Bond', 'Commodity'].map(type => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-3 sm:px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  filterType === type
                    ? 'bg-amber-500 text-neutral-950'
                    : 'bg-neutral-800/50 text-neutral-400 hover:bg-neutral-800 hover:text-neutral-200'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowImportModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-neutral-800 hover:bg-neutral-700 text-neutral-200 font-medium rounded-xl transition-all duration-200 border border-neutral-700"
            >
              <Upload className="w-4 h-4" />
              <span className="hidden sm:inline">Import</span>
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 sm:px-5 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-neutral-950 font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-amber-500/20"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">{t.addAsset}</span>
              <span className="sm:hidden">Add</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden p-4 space-y-3">
        {displayHoldings.map(holding => {
          const price = holding.current_price || holding.avg_cost || 0;
          const value = holding.shares * price;
          const weight = metrics.totalValue > 0 ? (value / metrics.totalValue) * 100 : 0;
          const gainAmount = (price - (holding.avg_cost || 0)) * holding.shares;
          const gainPercent = holding.avg_cost ? ((price - holding.avg_cost) / holding.avg_cost) * 100 : 0;

          return (
            <div key={holding.id} className="bg-neutral-800/30 rounded-2xl p-4 border border-neutral-800/50">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                    <span className="text-amber-500 font-bold text-sm">{holding.symbol.slice(0, 2)}</span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-neutral-50">{holding.symbol}</span>
                      <span className="px-2 py-0.5 rounded-md text-xs font-medium" style={{ backgroundColor: `${TYPE_COLORS[holding.type]}15`, color: TYPE_COLORS[holding.type] }}>
                        {holding.type}
                      </span>
                    </div>
                    <p className="text-sm text-neutral-500 truncate max-w-[180px]">{holding.name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {isUSStock(holding) && (
                    <button onClick={() => fetchFundamentals(holding.symbol)} className="p-2 text-neutral-500 hover:text-blue-400 rounded-lg" title={t.viewFundamentals}>
                      <BookOpen className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => handleHoldingClick(holding)} className="p-2 text-neutral-500 hover:text-amber-400 rounded-lg" title={t.transactionHistory}>
                    <History className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleEditHolding(holding)} className="p-2 text-neutral-500 hover:text-amber-400 rounded-lg">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDeleteHolding(holding.id)} className="p-2 text-neutral-500 hover:text-red-400 rounded-lg">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-neutral-500">{t.value}</p>
                  {(holding.type === 'BIST' || holding.type === 'TEFAS') ? (
                    <>
                      <p className="text-lg font-semibold text-neutral-50">{formatTRY(value)}</p>
                      {usdTryRate > 0 && <p className="text-xs text-neutral-500">({formatCurrency(value / usdTryRate)})</p>}
                    </>
                  ) : (
                    <>
                      <p className="text-lg font-semibold text-neutral-50">{formatCurrency(value)}</p>
                      {usdTryRate > 0 && <p className="text-xs text-neutral-500">({formatTRY(value * usdTryRate)})</p>}
                    </>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-xs text-neutral-500">{t.gainPercent}</p>
                  <p className={`text-lg font-semibold ${gainPercent >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {formatPercent(gainPercent)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500">{t.shares}</p>
                  <p className="text-sm text-neutral-300">{holding.shares.toLocaleString()}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-neutral-500">{(holding.type === 'BIST' || holding.type === 'TEFAS') ? `${t.gain} ₺` : `${t.gain} $`}</p>
                  <p className={`text-sm font-medium ${gainAmount >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {(holding.type === 'BIST' || holding.type === 'TEFAS') ? formatTRY(gainAmount) : formatCurrency(gainAmount)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500">{t.avgCost} {(holding.type === 'BIST' || holding.type === 'TEFAS') ? '(₺)' : ''}</p>
                  <p className="text-sm text-neutral-400">
                    {(holding.type === 'BIST' || holding.type === 'TEFAS')
                      ? formatTRY(holding.avg_cost)
                      : formatCurrency(holding.avg_cost)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-neutral-500">{t.price} {(holding.type === 'BIST' || holding.type === 'TEFAS') ? '(₺)' : ''}</p>
                  <p className="text-sm text-neutral-300">
                    {(holding.type === 'BIST' || holding.type === 'TEFAS')
                      ? formatTRY(price)
                      : formatCurrency(price)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500">{t.weight}</p>
                  <p className="text-sm text-amber-400 font-medium">{weight.toFixed(1)}%</p>
                </div>
              </div>
            </div>
          );
        })}
        {displayHoldings.length === 0 && (
          <div className="p-8 text-center text-neutral-500">
            No holdings yet. Add your first asset to get started!
          </div>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-neutral-500 text-sm border-b border-neutral-800/50">
              <th className="p-4 font-medium cursor-pointer hover:text-amber-500 transition-colors" onClick={() => handleSort('symbol')}>{t.symbol}</th>
              <th className="p-4 font-medium">{t.name}</th>
              <th className="p-4 font-medium cursor-pointer hover:text-amber-500 transition-colors" onClick={() => handleSort('type')}>{t.type}</th>
              <th className="p-4 text-right font-medium cursor-pointer hover:text-amber-500 transition-colors" onClick={() => handleSort('shares')}>{t.shares}</th>
              <th className="p-4 text-right font-medium">{t.avgCost}</th>
              <th className="p-4 text-right font-medium cursor-pointer hover:text-amber-500 transition-colors" onClick={() => handleSort('current_price')}>{t.price}</th>
              <th className="p-4 text-right font-medium cursor-pointer hover:text-amber-500 transition-colors" onClick={() => handleSort('value')}>{t.value}</th>
              <th className="p-4 text-right font-medium cursor-pointer hover:text-amber-500 transition-colors" onClick={() => handleSort('weight')}>{t.weight}</th>
              <th className="p-4 text-right font-medium cursor-pointer hover:text-amber-500 transition-colors" onClick={() => handleSort('gain')}>{t.gainPercent}</th>
              <th className="p-4 text-right font-medium cursor-pointer hover:text-amber-500 transition-colors" onClick={() => handleSort('gainAmount')}>{t.gainAmount}</th>
              <th className="p-4 text-center font-medium">{t.actions}</th>
            </tr>
          </thead>
          <tbody>
            {displayHoldings.map(holding => {
              const price = holding.current_price || holding.avg_cost || 0;
              const value = holding.shares * price;
              const weight = metrics.totalValue > 0 ? (value / metrics.totalValue) * 100 : 0;
              const gainAmount = (price - (holding.avg_cost || 0)) * holding.shares;
              const gainPercent = holding.avg_cost ? ((price - holding.avg_cost) / holding.avg_cost) * 100 : 0;

              return (
                <tr key={holding.id} className="border-b border-neutral-800/30 hover:bg-neutral-800/20 transition-colors">
                  <td className="p-4"><span className="font-semibold text-neutral-50">{holding.symbol}</span></td>
                  <td className="p-4 text-neutral-400">{holding.name}</td>
                  <td className="p-4">
                    <span className="px-3 py-1 rounded-lg text-xs font-semibold" style={{ backgroundColor: `${TYPE_COLORS[holding.type]}15`, color: TYPE_COLORS[holding.type] }}>
                      {holding.type}
                    </span>
                  </td>
                  <td className="p-4 text-right text-neutral-300">{holding.shares.toLocaleString()}</td>
                  <td className="p-4 text-right text-neutral-400">
                    {(holding.type === 'BIST' || holding.type === 'TEFAS')
                      ? formatTRY(holding.avg_cost)
                      : formatPrice(holding.avg_cost)}
                  </td>
                  <td className="p-4 text-right text-neutral-200 font-medium">
                    {(holding.type === 'BIST' || holding.type === 'TEFAS')
                      ? formatTRY(price)
                      : formatPrice(price)}
                  </td>
                  <td className="p-4 text-right">
                    {(holding.type === 'BIST' || holding.type === 'TEFAS') ? (
                      <>
                        <div className="text-neutral-50 font-semibold">{formatTRY(value)}</div>
                        {usdTryRate > 0 && <div className="text-xs text-neutral-500">({formatCurrency(value / usdTryRate)})</div>}
                      </>
                    ) : (
                      <>
                        <div className="text-neutral-50 font-semibold">{formatCurrency(value)}</div>
                        {usdTryRate > 0 && <div className="text-xs text-neutral-500">({formatTRY(value * usdTryRate)})</div>}
                      </>
                    )}
                  </td>
                  <td className="p-4 text-right text-amber-400 font-medium">{weight.toFixed(1)}%</td>
                  <td className={`p-4 text-right font-semibold ${gainPercent >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatPercent(gainPercent)}</td>
                  <td className={`p-4 text-right font-semibold ${gainAmount >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {(holding.type === 'BIST' || holding.type === 'TEFAS') ? formatTRY(gainAmount) : formatCurrency(gainAmount)}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-center gap-1">
                      {isUSStock(holding) && (
                        <button onClick={() => fetchFundamentals(holding.symbol)} className="p-2 text-neutral-500 hover:text-blue-400 hover:bg-blue-500/10 rounded-xl transition-all duration-200" title={t.viewFundamentals}>
                          <BookOpen className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={() => handleHoldingClick(holding)} className="p-2 text-neutral-500 hover:text-amber-400 hover:bg-amber-500/10 rounded-xl transition-all duration-200" title={t.transactionHistory}>
                        <History className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleEditHolding(holding)} className="p-2 text-neutral-500 hover:text-amber-400 hover:bg-amber-500/10 rounded-xl transition-all duration-200" title={t.edit}>
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDeleteHolding(holding.id)} className="p-2 text-neutral-500 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-all duration-200" title={t.delete}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {displayHoldings.length === 0 && (
              <tr>
                <td colSpan="11" className="p-8 text-center text-neutral-500">
                  No holdings yet. Add your first asset to get started!
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      </div>
    </>
  );

  // News Tab Content
  const NewsContent = () => (
    <div className="bg-neutral-900/50 backdrop-blur-sm rounded-3xl p-6 border border-neutral-800/50">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
            <Newspaper className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-neutral-50">News Feed</h2>
            <p className="text-sm text-neutral-500">Latest news for your holdings</p>
          </div>
        </div>
        {/* Sentiment Summary */}
        {sentimentSummary.total > 0 && (
          <div className="flex items-center gap-4 bg-neutral-800/50 rounded-xl px-4 py-2">
            <div className="flex items-center gap-2 text-sm">
              <ThumbsUp className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 font-medium">{sentimentSummary.positive}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Minus className="w-4 h-4 text-neutral-400" />
              <span className="text-neutral-400 font-medium">{sentimentSummary.neutral}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <ThumbsDown className="w-4 h-4 text-red-400" />
              <span className="text-red-400 font-medium">{sentimentSummary.negative}</span>
            </div>
          </div>
        )}
      </div>

      {/* Symbol Filter */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        <button
          onClick={() => setSelectedNewsSymbol('All')}
          className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
            selectedNewsSymbol === 'All'
              ? 'bg-amber-500 text-neutral-950'
              : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700'
          }`}
        >
          All News
        </button>
        {[...new Set(news.map(n => n.symbol))].map(symbol => (
          <button
            key={symbol}
            onClick={() => setSelectedNewsSymbol(symbol)}
            className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
              selectedNewsSymbol === symbol
                ? 'bg-amber-500 text-neutral-950'
                : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700'
            }`}
          >
            {symbol}
          </button>
        ))}
      </div>

      {/* News Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {newsLoading ? (
          <div className="col-span-2 flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
          </div>
        ) : filteredNews.length > 0 ? (
          filteredNews.map((article, index) => (
            <a
              key={index}
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block p-5 rounded-2xl bg-neutral-800/30 hover:bg-neutral-800/50 transition-all group border border-neutral-800/50 hover:border-amber-500/30"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-amber-500/20 text-amber-400">
                      {article.symbol}
                    </span>
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${
                      article.sentiment === 'positive' ? 'bg-emerald-500/20 text-emerald-400' :
                      article.sentiment === 'negative' ? 'bg-red-500/20 text-red-400' :
                      'bg-neutral-700 text-neutral-400'
                    }`}>
                      {article.sentiment}
                    </span>
                  </div>
                  <h3 className="text-neutral-200 font-medium mb-2 group-hover:text-amber-400 transition-colors line-clamp-2">
                    {article.headline}
                  </h3>
                  <p className="text-neutral-500 text-sm line-clamp-2 mb-3">{article.summary}</p>
                  <div className="flex items-center gap-3 text-xs text-neutral-600">
                    <span>{article.source}</span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatTimeAgo(article.published_at)}
                    </span>
                    <ExternalLink className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
                {article.image_url && (
                  <img
                    src={article.image_url}
                    alt=""
                    className="w-24 h-24 rounded-xl object-cover flex-shrink-0"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
              </div>
            </a>
          ))
        ) : (
          <div className="col-span-2 text-center py-12 text-neutral-500">
            <Newspaper className="w-16 h-16 mx-auto mb-4 text-neutral-600" />
            <p className="text-lg">No news available yet.</p>
            <p className="text-sm mt-2">News will be fetched automatically twice daily at 09:00 and 18:00 UTC.</p>
          </div>
        )}
      </div>
    </div>
  );

  // Calendar Tab Content
  const CalendarContent = () => (
    <div className="bg-neutral-900/50 backdrop-blur-sm rounded-3xl p-6 border border-neutral-800/50">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
          <Calendar className="w-5 h-5 text-amber-500" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-neutral-50">Earnings Calendar</h2>
          <p className="text-sm text-neutral-500">Upcoming earnings for your holdings</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {earnings.length > 0 ? (
          earnings.map((earning, index) => {
            const reportDate = new Date(earning.report_date);
            const isToday = reportDate.toDateString() === new Date().toDateString();
            const daysUntil = Math.ceil((reportDate - new Date()) / (1000 * 60 * 60 * 24));
            const isSoon = daysUntil <= 7;

            return (
              <div
                key={index}
                className={`p-5 rounded-2xl border transition-all ${
                  isToday ? 'bg-amber-500/10 border-amber-500/30' :
                  isSoon ? 'bg-neutral-800/50 border-neutral-700/50' : 'bg-neutral-800/30 border-neutral-800/50'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xl font-bold text-neutral-100">{earning.symbol}</span>
                  <span className={`text-xs px-3 py-1.5 rounded-lg font-medium ${
                    earning.hour === 'bmo' ? 'bg-blue-500/20 text-blue-400' :
                    earning.hour === 'amc' ? 'bg-purple-500/20 text-purple-400' :
                    'bg-neutral-700 text-neutral-400'
                  }`}>
                    {earning.hour === 'bmo' ? '🌅 Before Open' :
                     earning.hour === 'amc' ? '🌙 After Close' : '⏰ TBD'}
                  </span>
                </div>
                <div className="text-sm text-neutral-400 mb-2">
                  {isToday ? (
                    <span className="text-amber-400 font-semibold flex items-center gap-2">
                      <Bell className="w-4 h-4" /> Today!
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      📅 {reportDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                    </span>
                  )}
                </div>
                {!isToday && (
                  <div className="text-xs text-neutral-500">
                    {daysUntil === 1 ? 'Tomorrow' : `In ${daysUntil} days`}
                  </div>
                )}
                {earning.eps_estimate && (
                  <div className="mt-3 pt-3 border-t border-neutral-700/50">
                    <div className="text-xs text-neutral-500">EPS Estimate</div>
                    <div className="text-lg font-semibold text-neutral-200">${earning.eps_estimate.toFixed(2)}</div>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="col-span-3 text-center py-12 text-neutral-500">
            <Calendar className="w-16 h-16 mx-auto mb-4 text-neutral-600" />
            <p className="text-lg">No upcoming earnings</p>
            <p className="text-sm mt-2">Calendar updates automatically twice daily.</p>
          </div>
        )}
      </div>
    </div>
  );

  // Economics Tab Content
  const EconomicsContent = () => {
    // Fetch data on mount
    useEffect(() => {
      if (!economicsData && !economicsLoading) {
        fetchEconomicsData();
      }
    }, []);

    // Macro interpretation cheatsheet
    const macroCheatsheet = language === 'tr' ? [
      { indicator: 'İşsizlik Oranı', high: '📉 Düşük = İyi ekonomi, Fed faiz artırabilir, hisse↑', low: '📈 Yüksek = Zayıf ekonomi, Fed faiz indirebilir, tahvil↑' },
      { indicator: 'CPI (Enflasyon)', high: '📈 Yüksek = Fed sıkılaştırır, faiz↑, hisse↓, altın↑', low: '📉 Düşük = Fed gevşetir, faiz↓, hisse↑' },
      { indicator: 'Core CPI', high: '📈 Yüksek = Kalıcı enflasyon, Fed agresif, tahvil↓', low: '📉 Düşük = Fiyat istikrarı, Fed rahat' },
      { indicator: 'NFP (Tarım Dışı İstihdam)', high: '📈 Yüksek = Güçlü ekonomi, USD↑, hisse↑', low: '📉 Düşük = Zayıf ekonomi, USD↓, Fed gevşer' },
      { indicator: 'GDP Büyüme', high: '📈 Yüksek = Ekonomi genişliyor, hisse↑, USD↑', low: '📉 Düşük/Negatif = Resesyon riski, güvenli liman↑' },
      { indicator: 'PMI (İmalat/Hizmet)', high: '📈 >50 = Genişleme, hisse↑, emtia↑', low: '📉 <50 = Daralma, defansif sektörler↑' },
      { indicator: 'Perakende Satışlar', high: '📈 Yüksek = Güçlü tüketici, hisse↑', low: '📉 Düşük = Zayıf talep, tüketim sektörü↓' },
      { indicator: 'FOMC / Fed Faiz', high: '📈 Yüksek = Para sıkışır, tahvil↓, hisse↓', low: '📉 Düşük = Para gevşer, hisse↑, tahvil↑' },
      { indicator: 'Tüketici Güveni', high: '📈 Yüksek = Harcama↑, hisse↑', low: '📉 Düşük = Temkinli tüketici, defansif↑' },
      { indicator: 'Konut Başlangıçları', high: '📈 Yüksek = Ekonomi güçlü, inşaat↑', low: '📉 Düşük = Ekonomi zayıf, faiz etkisi' }
    ] : [
      { indicator: 'Unemployment Rate', high: '📉 Low = Strong economy, Fed may hike, stocks↑', low: '📈 High = Weak economy, Fed may cut, bonds↑' },
      { indicator: 'CPI (Inflation)', high: '📈 High = Fed tightens, rates↑, stocks↓, gold↑', low: '📉 Low = Fed eases, rates↓, stocks↑' },
      { indicator: 'Core CPI', high: '📈 High = Sticky inflation, Fed aggressive, bonds↓', low: '📉 Low = Price stability, Fed relaxed' },
      { indicator: 'NFP (Nonfarm Payrolls)', high: '📈 High = Strong economy, USD↑, stocks↑', low: '📉 Low = Weak economy, USD↓, Fed eases' },
      { indicator: 'GDP Growth', high: '📈 High = Economy expanding, stocks↑, USD↑', low: '📉 Low/Negative = Recession risk, safe havens↑' },
      { indicator: 'PMI (Mfg/Services)', high: '📈 >50 = Expansion, stocks↑, commodities↑', low: '📉 <50 = Contraction, defensive sectors↑' },
      { indicator: 'Retail Sales', high: '📈 High = Strong consumer, stocks↑', low: '📉 Low = Weak demand, consumer sector↓' },
      { indicator: 'FOMC / Fed Rate', high: '📈 High = Tight money, bonds↓, stocks↓', low: '📉 Low = Easy money, stocks↑, bonds↑' },
      { indicator: 'Consumer Confidence', high: '📈 High = Spending↑, stocks↑', low: '📉 Low = Cautious consumer, defensives↑' },
      { indicator: 'Housing Starts', high: '📈 High = Strong economy, construction↑', low: '📉 Low = Weak economy, rate impact' }
    ];

    return (
      <div className="space-y-6">
        {/* Congressional Trades (Senate + House) - TOP PRIORITY */}
        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-3xl p-6 border border-neutral-800/50">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
              <Shield className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-neutral-50">
                {language === 'tr' ? '🏛️ Kongre İşlemleri' : '🏛️ Congressional Trades'}
              </h2>
              <p className="text-sm text-neutral-500">
                {language === 'tr' ? 'Senato & Temsilciler Meclisi' : 'Senate & House of Representatives'}
              </p>
            </div>
          </div>

          {economicsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-red-400 animate-spin" />
            </div>
          ) : economicsData?.congress?.data && economicsData.congress.data.length > 0 ? (
            <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
              {economicsData.congress.data.map((trade, i) => {
                const isPurchase = trade.transactionType?.toLowerCase().includes('purchase');
                const isSale = trade.transactionType?.toLowerCase().includes('sale');
                return (
                  <div
                    key={i}
                    className={`p-4 rounded-xl border transition-all hover:bg-neutral-800/50 ${
                      isPurchase ? 'bg-emerald-500/5 border-emerald-500/20' :
                      isSale ? 'bg-red-500/5 border-red-500/20' :
                      'bg-neutral-800/30 border-neutral-700/30'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            trade.chamber === 'Senate' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'
                          }`}>
                            {trade.chamber}
                          </span>
                          {trade.symbol && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 font-bold">
                              {trade.symbol}
                            </span>
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            isPurchase ? 'bg-emerald-500/20 text-emerald-400' :
                            isSale ? 'bg-red-500/20 text-red-400' :
                            'bg-neutral-600/50 text-neutral-400'
                          }`}>
                            {isPurchase ? (language === 'tr' ? 'Alış' : 'Buy') :
                             isSale ? (language === 'tr' ? 'Satış' : 'Sell') :
                             trade.transactionType || '-'}
                          </span>
                          {trade.owner && trade.owner !== 'Self' && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-pink-500/20 text-pink-400">
                              {trade.owner}
                            </span>
                          )}
                          {trade.assetType && trade.assetType !== 'Stock' && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400">
                              {trade.assetType}
                            </span>
                          )}
                        </div>
                        <div className="font-medium text-neutral-200">
                          {trade.representative}
                          {trade.state && <span className="text-neutral-500 text-sm ml-2">({trade.state})</span>}
                        </div>
                        {trade.assetDescription && (
                          <div className="text-sm text-neutral-400 mt-1 line-clamp-1">
                            {trade.assetDescription}
                          </div>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        {trade.amount && (
                          <div className={`text-sm font-medium ${
                            isPurchase ? 'text-emerald-400' : isSale ? 'text-red-400' : 'text-neutral-300'
                          }`}>
                            {trade.amount}
                          </div>
                        )}
                        <div className="text-xs text-neutral-500 mt-1">
                          {trade.transactionDate ? new Date(trade.transactionDate).toLocaleDateString(
                            language === 'tr' ? 'tr-TR' : 'en-US',
                            { month: 'short', day: 'numeric', year: 'numeric' }
                          ) : '-'}
                        </div>
                        {trade.link && (
                          <a
                            href={trade.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-400 hover:text-blue-300 mt-1 inline-block"
                          >
                            {language === 'tr' ? 'Kaynak →' : 'Source →'}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-neutral-500">
              <Shield className="w-12 h-12 mx-auto mb-3 text-neutral-600" />
              <p>{language === 'tr' ? 'Kongre işlem verisi bulunamadı' : 'No congressional trade data'}</p>
            </div>
          )}

          {/* Info note */}
          <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl text-sm text-amber-400/80">
            <span className="font-medium">💡 </span>
            {language === 'tr'
              ? 'Kongre üyelerinin işlemleri genellikle piyasayı 1-2 ay önceden tahmin eder. STOCK Act gereği bildirim zorunludur.'
              : 'Congressional trades often predict market moves 1-2 months ahead. Disclosure required by STOCK Act.'}
          </div>
        </div>

        {/* Treasury Rates */}
        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-3xl p-6 border border-neutral-800/50">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-neutral-50">
                  {language === 'tr' ? 'ABD Hazine Faizleri' : 'US Treasury Rates'}
                </h2>
                <p className="text-sm text-neutral-500">
                  {economicsData?.treasury?.date || '-'}
                </p>
              </div>
            </div>
            <button
              onClick={fetchEconomicsData}
              disabled={economicsLoading}
              className="p-2 bg-neutral-800 hover:bg-neutral-700 rounded-xl transition-all"
            >
              <RefreshCw className={`w-4 h-4 text-neutral-400 ${economicsLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {economicsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
            </div>
          ) : economicsData?.treasury && !economicsData.treasury.error ? (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
              {[
                { label: '1M', value: economicsData.treasury.month1 },
                { label: '3M', value: economicsData.treasury.month3 },
                { label: '6M', value: economicsData.treasury.month6 },
                { label: '1Y', value: economicsData.treasury.year1 },
                { label: '2Y', value: economicsData.treasury.year2 },
                { label: '5Y', value: economicsData.treasury.year5 },
                { label: '7Y', value: economicsData.treasury.year7 },
                { label: '10Y', value: economicsData.treasury.year10 },
                { label: '20Y', value: economicsData.treasury.year20 },
                { label: '30Y', value: economicsData.treasury.year30 }
              ].map((rate, i) => (
                <div key={i} className="bg-neutral-800/50 rounded-xl p-3 text-center">
                  <div className="text-xs text-neutral-500 mb-1">{rate.label}</div>
                  <div className="text-lg font-bold text-blue-400">
                    {rate.value ? `${rate.value.toFixed(2)}%` : '-'}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-neutral-500">
              {language === 'tr' ? 'Veri yüklenemedi' : 'Data unavailable'}
            </div>
          )}

          {/* Yield curve note */}
          {economicsData?.treasury && economicsData.treasury.year2 && economicsData.treasury.year10 && (
            <div className={`mt-4 p-3 rounded-xl text-sm ${
              economicsData.treasury.year2 > economicsData.treasury.year10
                ? 'bg-red-500/10 text-red-400 border border-red-500/20'
                : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
            }`}>
              <span className="font-medium">
                {economicsData.treasury.year2 > economicsData.treasury.year10
                  ? (language === 'tr' ? '⚠️ Verim Eğrisi Ters: ' : '⚠️ Yield Curve Inverted: ')
                  : (language === 'tr' ? '✅ Verim Eğrisi Normal: ' : '✅ Yield Curve Normal: ')
                }
              </span>
              2Y ({economicsData.treasury.year2?.toFixed(2)}%) vs 10Y ({economicsData.treasury.year10?.toFixed(2)}%)
              <span className="text-xs ml-2">
                {economicsData.treasury.year2 > economicsData.treasury.year10
                  ? (language === 'tr' ? '(Resesyon sinyali)' : '(Recession signal)')
                  : ''}
              </span>
            </div>
          )}
        </div>

        {/* Macro Cheatsheet */}
        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-3xl p-6 border border-neutral-800/50">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-neutral-50">
                {language === 'tr' ? 'Makro Veri Yorumlama Rehberi' : 'Macro Data Interpretation Guide'}
              </h2>
              <p className="text-sm text-neutral-500">
                {language === 'tr' ? 'Verilerin piyasaya etkisi' : 'Impact of data on markets'}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {macroCheatsheet.map((item, i) => (
              <div key={i} className="bg-neutral-800/30 rounded-xl p-4 border border-neutral-700/30">
                <div className="font-medium text-neutral-200 mb-2">{item.indicator}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <div className="text-emerald-400/80">{item.high}</div>
                  <div className="text-red-400/80">{item.low}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Economic Calendar */}
        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-3xl p-6 border border-neutral-800/50">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-neutral-50">
                {language === 'tr' ? 'Ekonomik Takvim' : 'Economic Calendar'}
              </h2>
              <p className="text-sm text-neutral-500">
                {language === 'tr' ? 'Önümüzdeki 30 gün' : 'Next 30 days'}
              </p>
            </div>
          </div>

          {economicsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
            </div>
          ) : economicsData?.calendar?.data && economicsData.calendar.data.length > 0 ? (
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
              {economicsData.calendar.data.slice(0, 30).map((event, i) => {
                const eventDate = new Date(event.date);
                const isToday = eventDate.toDateString() === new Date().toDateString();
                const isTomorrow = eventDate.toDateString() === new Date(Date.now() + 86400000).toDateString();

                return (
                  <div
                    key={i}
                    className={`p-4 rounded-xl border transition-all ${
                      isToday ? 'bg-amber-500/10 border-amber-500/30' :
                      isTomorrow ? 'bg-purple-500/10 border-purple-500/30' :
                      'bg-neutral-800/30 border-neutral-700/30'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            isToday ? 'bg-amber-500/20 text-amber-400' :
                            isTomorrow ? 'bg-purple-500/20 text-purple-400' :
                            'bg-neutral-700 text-neutral-400'
                          }`}>
                            {isToday ? (language === 'tr' ? 'Bugün' : 'Today') :
                             isTomorrow ? (language === 'tr' ? 'Yarın' : 'Tomorrow') :
                             eventDate.toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                        <div className="font-medium text-neutral-200">{event.event}</div>
                      </div>
                      <div className="text-right text-sm">
                        {event.estimate !== null && (
                          <div className="text-neutral-500">
                            {language === 'tr' ? 'Beklenti' : 'Est'}: <span className="text-neutral-300">{event.estimate}</span>
                          </div>
                        )}
                        {event.previous !== null && (
                          <div className="text-neutral-500">
                            {language === 'tr' ? 'Önceki' : 'Prev'}: <span className="text-neutral-400">{event.previous}</span>
                          </div>
                        )}
                        {event.actual !== null && (
                          <div className="text-neutral-500">
                            {language === 'tr' ? 'Gerçek' : 'Actual'}: <span className={`font-medium ${
                              event.actual > event.estimate ? 'text-emerald-400' :
                              event.actual < event.estimate ? 'text-red-400' :
                              'text-neutral-300'
                            }`}>{event.actual}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-neutral-500">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-neutral-600" />
              <p>{language === 'tr' ? 'Takvim verisi bulunamadı' : 'No calendar data'}</p>
            </div>
          )}
        </div>

        {/* Economic News */}
        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-3xl p-6 border border-neutral-800/50">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <Newspaper className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-neutral-50">
                {language === 'tr' ? 'Ekonomi Haberleri' : 'Economic News'}
              </h2>
              <p className="text-sm text-neutral-500">
                {language === 'tr' ? 'Son gelişmeler' : 'Latest updates'}
              </p>
            </div>
          </div>

          {economicsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
            </div>
          ) : economicsData?.news?.data && economicsData.news.data.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {economicsData.news.data.slice(0, 10).map((item, i) => (
                <a
                  key={i}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-4 bg-neutral-800/30 rounded-xl border border-neutral-700/30 hover:bg-neutral-800/50 hover:border-neutral-600/50 transition-all group"
                >
                  <div className="flex items-start gap-3">
                    {item.image && (
                      <img
                        src={item.image}
                        alt=""
                        className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-neutral-200 group-hover:text-emerald-400 transition-colors line-clamp-2 text-sm">
                        {item.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-2 text-xs text-neutral-500">
                        <span>{item.source}</span>
                        <span>•</span>
                        <span>
                          {new Date(item.publishedDate).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-neutral-600 group-hover:text-emerald-400 flex-shrink-0" />
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-neutral-500">
              <Newspaper className="w-12 h-12 mx-auto mb-3 text-neutral-600" />
              <p>{language === 'tr' ? 'Haber bulunamadı' : 'No news available'}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Analytics Tab Content (formerly History)
  const AnalyticsContent = () => {
    const [showHistory, setShowHistory] = useState(false);

    return (
      <div className="space-y-6">
        {/* Risk Metrics Section - NOW AT TOP */}
        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-3xl p-6 border border-neutral-800/50">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <Activity className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-neutral-50">{t.riskMetrics}</h2>
              <p className="text-sm text-neutral-500">{language === 'TR' ? 'Portföy risk metrikleri' : 'Portfolio risk metrics'}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {/* Beta Card */}
            <div className="p-4 rounded-2xl bg-gradient-to-br from-amber-500/10 to-amber-600/5 border border-amber-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4 text-amber-500" />
                <span className="text-xs text-neutral-400 uppercase tracking-wide">{t.beta}</span>
                <InfoTooltip text={t.tooltips.beta} />
              </div>
              <div className="text-2xl font-bold text-neutral-50 mb-1">{metrics.beta}</div>
              <p className="text-xs text-neutral-500">
                {parseFloat(metrics.beta) > 1 ? (language === 'tr' ? 'Piyasadan oynak' : 'More volatile') : parseFloat(metrics.beta) < 1 ? (language === 'tr' ? 'Piyasadan stabil' : 'More stable') : (language === 'tr' ? 'Piyasa ile aynı' : 'Same as market')}
              </p>
            </div>

            {/* Sharpe Ratio Card */}
            <div className="p-4 rounded-2xl bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-4 h-4 text-blue-400" />
                <span className="text-xs text-neutral-400 uppercase tracking-wide">{t.sharpeRatio}</span>
                <InfoTooltip text={t.tooltips.sharpeRatio} />
              </div>
              <div className="text-2xl font-bold text-neutral-50 mb-1">{metrics.sharpeRatio}</div>
              <p className="text-xs text-neutral-500">
                {parseFloat(metrics.sharpeRatio) >= 2 ? (language === 'tr' ? 'Çok iyi' : 'Very good') : parseFloat(metrics.sharpeRatio) >= 1 ? (language === 'tr' ? 'İyi' : 'Good') : (language === 'tr' ? 'Düşük' : 'Low')}
              </p>
            </div>

            {/* Sortino Ratio Card */}
            <div className="p-4 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-4 h-4 text-emerald-400" />
                <span className="text-xs text-neutral-400 uppercase tracking-wide">{t.sortinoRatio}</span>
                <InfoTooltip text={t.tooltips.sortinoRatio} />
              </div>
              <div className="text-2xl font-bold text-emerald-400 mb-1">{metrics.sortinoRatio}</div>
              <p className="text-xs text-neutral-500">
                {parseFloat(metrics.sortinoRatio) >= 2 ? (language === 'tr' ? 'Mükemmel' : 'Excellent') : parseFloat(metrics.sortinoRatio) >= 1 ? (language === 'tr' ? 'İyi' : 'Good') : (language === 'tr' ? 'Gelişebilir' : 'Can improve')}
              </p>
            </div>

            {/* Volatility Card */}
            <div className="p-4 rounded-2xl bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/20">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-4 h-4 text-purple-400" />
                <span className="text-xs text-neutral-400 uppercase tracking-wide">{t.volatility}</span>
                <InfoTooltip text={t.tooltips.volatility} />
              </div>
              <div className="text-2xl font-bold text-neutral-50 mb-1">{metrics.volatility}%</div>
              <p className="text-xs text-neutral-500">
                {parseFloat(metrics.volatility) > 25 ? (language === 'tr' ? 'Yüksek' : 'High') : parseFloat(metrics.volatility) > 15 ? (language === 'tr' ? 'Normal' : 'Normal') : (language === 'tr' ? 'Düşük' : 'Low')}
              </p>
            </div>

            {/* Max Drawdown Card */}
            <div className="p-4 rounded-2xl bg-gradient-to-br from-red-500/10 to-red-600/5 border border-red-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-red-400" />
                <span className="text-xs text-neutral-400 uppercase tracking-wide">{t.maxDrawdown}</span>
                <InfoTooltip text={t.tooltips.maxDrawdown} />
              </div>
              <div className="text-2xl font-bold text-red-400 mb-1">{metrics.maxDrawdown}%</div>
              <p className="text-xs text-neutral-500">
                {parseFloat(metrics.maxDrawdown) > 20 ? (language === 'tr' ? 'Dikkat!' : 'Watch out!') : parseFloat(metrics.maxDrawdown) > 10 ? (language === 'tr' ? 'Normal' : 'Normal') : (language === 'tr' ? 'Düşük risk' : 'Low risk')}
              </p>
            </div>
          </div>
        </div>

        {/* Metrics Explained - Collapsible */}
        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-3xl border border-neutral-800/50 overflow-hidden">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="w-full p-6 flex items-center justify-between hover:bg-neutral-800/20 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <Globe className="w-5 h-5 text-amber-500" />
              </div>
              <div className="text-left">
                <h2 className="text-xl font-semibold text-neutral-50">Metrikleri Anla</h2>
                <p className="text-sm text-neutral-500">Bu rakamlar ne anlama geliyor?</p>
              </div>
            </div>
            <ChevronRight className={`w-5 h-5 text-neutral-400 transition-transform ${showHistory ? 'rotate-90' : ''}`} />
          </button>

          {showHistory && (
            <div className="p-6 pt-0 border-t border-neutral-800/50">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                {/* Beta */}
                <div className="p-4 rounded-2xl bg-neutral-800/30 border border-neutral-800/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Activity className="w-5 h-5 text-amber-500" />
                    <h3 className="font-semibold text-neutral-100">Beta</h3>
                  </div>
                  <p className="text-sm text-neutral-400 mb-3">
                    Portföyünüzün piyasaya göre ne kadar dalgalı olduğunu ölçer.
                  </p>
                  <div className="text-xs text-neutral-500 space-y-1">
                    <p>• <span className="text-neutral-300">β = 1.0:</span> Piyasayla aynı hareket</p>
                    <p>• <span className="text-neutral-300">β {'>'} 1.0:</span> Piyasadan daha volatil</p>
                    <p>• <span className="text-neutral-300">β {'<'} 1.0:</span> Piyasadan daha stabil</p>
                  </div>
                </div>

                {/* Sharpe Ratio */}
                <div className="p-4 rounded-2xl bg-neutral-800/30 border border-neutral-800/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-5 h-5 text-blue-400" />
                    <h3 className="font-semibold text-neutral-100">Sharpe Ratio</h3>
                  </div>
                  <p className="text-sm text-neutral-400 mb-3">
                    Her birim risk için ne kadar getiri aldığınızı gösterir.
                  </p>
                  <div className="text-xs text-neutral-500 space-y-1">
                    <p>• <span className="text-neutral-300">{'<'} 1.0:</span> Düşük risk-getiri</p>
                    <p>• <span className="text-neutral-300">1.0 - 2.0:</span> İyi</p>
                    <p>• <span className="text-neutral-300">{'>'} 2.0:</span> Çok iyi</p>
                  </div>
                </div>

                {/* Sortino Ratio */}
                <div className="p-4 rounded-2xl bg-neutral-800/30 border border-neutral-800/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-5 h-5 text-emerald-400" />
                    <h3 className="font-semibold text-neutral-100">Sortino Ratio</h3>
                  </div>
                  <p className="text-sm text-neutral-400 mb-3">
                    Sharpe gibi ama sadece aşağı yönlü volatiliteyi hesaplar.
                  </p>
                  <div className="text-xs text-neutral-500 space-y-1">
                    <p>• Sharpe'dan daha doğru ölçüm</p>
                    <p>• Yukarı volatilite cezalandırılmaz</p>
                    <p>• Yüksek = daha iyi</p>
                  </div>
                </div>

                {/* Volatility */}
                <div className="p-4 rounded-2xl bg-neutral-800/30 border border-neutral-800/50">
                  <div className="flex items-center gap-2 mb-2">
                    <BarChart3 className="w-5 h-5 text-purple-400" />
                    <h3 className="font-semibold text-neutral-100">Volatility</h3>
                  </div>
                  <p className="text-sm text-neutral-400 mb-3">
                    Portföy değerinin yıllık dalgalanma oranı.
                  </p>
                  <div className="text-xs text-neutral-500 space-y-1">
                    <p>• <span className="text-neutral-300">{'<'} 15%:</span> Düşük</p>
                    <p>• <span className="text-neutral-300">15-25%:</span> Normal</p>
                    <p>• <span className="text-neutral-300">{'>'} 25%:</span> Yüksek</p>
                  </div>
                </div>

                {/* Max Drawdown */}
                <div className="p-4 rounded-2xl bg-neutral-800/30 border border-neutral-800/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-5 h-5 text-red-400" />
                    <h3 className="font-semibold text-neutral-100">Max Drawdown</h3>
                  </div>
                  <p className="text-sm text-neutral-400 mb-3">
                    Zirveden dibe maksimum düşüş oranı.
                  </p>
                  <div className="text-xs text-neutral-500 space-y-1">
                    <p>• En kötü senaryoyu gösterir</p>
                    <p>• <span className="text-neutral-300">{'<'} 10%:</span> Düşük risk</p>
                    <p>• <span className="text-neutral-300">{'>'} 20%:</span> Yüksek risk</p>
                  </div>
                </div>

                {/* S&P 500 */}
                <div className="p-4 rounded-2xl bg-neutral-800/30 border border-neutral-800/50">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-5 h-5 text-blue-400" />
                    <h3 className="font-semibold text-neutral-100">Benchmark</h3>
                  </div>
                  <p className="text-sm text-neutral-400 mb-3">
                    S&P 500 ile karşılaştırma yapın.
                  </p>
                  <div className="text-xs text-neutral-500 space-y-1">
                    <p>• S&P 500 ort. yıllık %10 getiri</p>
                    <p>• Endeksi yeniyorsanız başarılı!</p>
                    <p>• Uzun vadede karşılaştırın</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Withdrawal Summary Cards - only show if there are withdrawals */}
        {metrics.totalWithdrawals > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Current Portfolio */}
            <div className="bg-neutral-900/50 backdrop-blur-sm rounded-2xl p-5 border border-neutral-800/50">
              <div className="flex items-center gap-2 mb-2">
                <Briefcase className="w-4 h-4 text-amber-500" />
                <span className="text-sm text-neutral-400">{t.currentPortfolio}</span>
              </div>
              <div className="text-2xl font-bold text-amber-400">{formatCurrency(metrics.totalValue)}</div>
              <div className="text-xs text-neutral-500 mt-1">Likit portföy değeri</div>
            </div>

            {/* Total Withdrawals */}
            <div className="bg-neutral-900/50 backdrop-blur-sm rounded-2xl p-5 border border-neutral-800/50">
              <div className="flex items-center gap-2 mb-2">
                <PiggyBank className="w-4 h-4 text-purple-500" />
                <span className="text-sm text-neutral-400">{t.totalWithdrawals}</span>
              </div>
              <div className="text-2xl font-bold text-purple-400">{formatCurrency(metrics.totalWithdrawals)}</div>
              <div className="text-xs text-neutral-500 mt-1">{language === 'tr' ? 'Gayrimenkul, araç vb.' : 'Real estate, vehicles, etc.'}</div>
            </div>

            {/* Total Wealth (Adjusted) */}
            <div className="bg-neutral-900/50 backdrop-blur-sm rounded-2xl p-5 border border-blue-500/30">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-blue-500" />
                <span className="text-sm text-neutral-400">{t.totalWealth}</span>
              </div>
              <div className="text-2xl font-bold text-blue-400">{formatCurrency(metrics.adjustedTotalValue)}</div>
              <div className="text-xs text-neutral-500 mt-1">{t.adjustedWithWithdrawals}</div>
            </div>
          </div>
        )}

        {/* Performance Charts */}
        {snapshots.length >= 2 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Value Over Time Chart - with Adjusted Value */}
            <div className="bg-neutral-900/50 backdrop-blur-sm rounded-3xl p-6 border border-neutral-800/50">
              <h3 className="text-lg font-semibold text-neutral-50 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-amber-500" />
                {t.portfolioValue}
              </h3>
              <div className="flex items-center gap-4 mb-3 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                  <span className="text-neutral-400">{t.currentPortfolio}</span>
                </div>
                {metrics.totalWithdrawals > 0 && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span className="text-neutral-400">{t.adjustedWithWithdrawals}</span>
                  </div>
                )}
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <ComposedChart data={performanceData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorValueHistory" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={AMBER[500]} stopOpacity={0.4}/>
                      <stop offset="95%" stopColor={AMBER[500]} stopOpacity={0.05}/>
                    </linearGradient>
                    <linearGradient id="colorAdjustedHistory" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.05}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#262626" vertical={false} />
                  <XAxis dataKey="date" stroke="#525252" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis
                    stroke="#525252"
                    fontSize={11}
                    tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`}
                    tickLine={false}
                    axisLine={false}
                    domain={[(dataMin) => Math.floor(dataMin * 0.95), (dataMax) => Math.ceil(dataMax * 1.05)]}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#262626', border: '1px solid #404040', borderRadius: '12px' }}
                    labelStyle={{ color: '#a3a3a3' }}
                    formatter={(value, name) => [
                      formatCurrency(value),
                      name === 'value' ? t.currentPortfolio : t.adjustedWithWithdrawals
                    ]}
                  />
                  {metrics.totalWithdrawals > 0 && (
                    <Area type="monotone" dataKey="adjustedValue" stroke="#3b82f6" strokeWidth={2} fill="url(#colorAdjustedHistory)" />
                  )}
                  <Area type="monotone" dataKey="value" stroke={AMBER[500]} strokeWidth={2} fill="url(#colorValueHistory)" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Composition Over Time */}
            <div className="bg-neutral-900/50 backdrop-blur-sm rounded-3xl p-6 border border-neutral-800/50">
              <h3 className="text-lg font-semibold text-neutral-50 mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-emerald-400" />
                Holdings vs Cash
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={performanceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#262626" vertical={false} />
                  <XAxis dataKey="date" stroke="#525252" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#525252" fontSize={11} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#262626', border: '1px solid #404040', borderRadius: '12px' }}
                    labelStyle={{ color: '#a3a3a3' }}
                    formatter={(value, name) => [formatCurrency(value), name]}
                  />
                  <Bar dataKey="holdings" name="Holdings" stackId="a" fill={AMBER[500]} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="cash" name="Cash" stackId="a" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Monthly Performance Comparison - Analytics Page */}
        {monthlyPerformanceData.length > 0 && (
          <div className="bg-neutral-900/50 backdrop-blur-sm rounded-3xl p-6 border border-neutral-800/50">
            <h3 className="text-lg font-semibold text-neutral-50 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-400" />
              {t.monthlyComparison}
            </h3>
            <div className="flex items-center gap-4 mb-3 text-xs">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-amber-500"></div>
                <span className="text-neutral-400">{t.portfolio}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-cyan-400"></div>
                <span className="text-neutral-400">S&P 500</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={monthlyPerformanceData} barCategoryGap="15%" margin={{ top: 25, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#262626" vertical={false} />
                <XAxis dataKey="month" stroke="#525252" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#525252" fontSize={11} tickFormatter={(v) => `${v}%`} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#171717', border: '1px solid #404040', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }}
                  labelStyle={{ color: '#fbbf24', fontWeight: 600, marginBottom: '8px' }}
                  itemStyle={{ color: '#e5e5e5' }}
                  cursor={{ fill: 'rgba(251, 191, 36, 0.1)' }}
                  formatter={(value, name) => [
                    <span style={{ color: name === 'portfolioReturn' ? '#fbbf24' : '#22d3ee' }}>
                      {value > 0 ? '+' : ''}{value}%
                    </span>,
                    name === 'portfolioReturn' ? t.portfolio : 'S&P 500'
                  ]}
                />
                <Bar dataKey="portfolioReturn" name="portfolioReturn" fill={AMBER[500]} radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="portfolioReturn" position="top" formatter={(v) => `${v > 0 ? '+' : ''}${v}%`} fill="#fbbf24" fontSize={9} fontWeight={600} />
                </Bar>
                <Bar dataKey="spyReturn" name="spyReturn" fill="#22d3ee" radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="spyReturn" position="top" formatter={(v) => `${v > 0 ? '+' : ''}${v}%`} fill="#22d3ee" fontSize={9} fontWeight={600} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Manual Snapshot Entry Card */}
        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-3xl border border-amber-500/30 overflow-hidden">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <Download className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-neutral-50">{language === 'tr' ? 'Haftalık Snapshot Kaydet' : 'Save Weekly Snapshot'}</h2>
                <p className="text-sm text-neutral-500">{language === 'tr' ? 'Portföy durumunuzu manuel olarak kaydedin' : 'Manually record your portfolio state'}</p>
              </div>
            </div>

            {/* Date Picker */}
            <div className="mb-5">
              <label className="block text-sm text-neutral-400 mb-1.5">{language === 'tr' ? 'Snapshot Tarihi' : 'Snapshot Date'}</label>
              <input
                type="date"
                value={snapshotDate}
                onChange={(e) => setSnapshotDate(e.target.value)}
                className="w-full sm:w-48 px-3 py-2 bg-neutral-800/80 border border-neutral-700 rounded-xl text-neutral-100 text-sm focus:border-amber-500 focus:outline-none"
              />
            </div>

            {/* Values Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
              <div className="bg-neutral-800/50 rounded-xl p-4">
                <p className="text-xs text-neutral-500 mb-1">{language === 'tr' ? 'Yatırımlar' : 'Holdings'}</p>
                <p className="text-lg font-semibold text-neutral-100">{formatCurrency(metrics.holdingsValue)}</p>
              </div>
              <div className="bg-neutral-800/50 rounded-xl p-4">
                <p className="text-xs text-neutral-500 mb-1">{language === 'tr' ? 'Nakit' : 'Cash'}</p>
                <p className="text-lg font-semibold text-emerald-400">{formatCurrency(metrics.totalCashUsd)}</p>
              </div>
              <div className="bg-neutral-800/50 rounded-xl p-4">
                <p className="text-xs text-neutral-500 mb-1">{language === 'tr' ? 'Toplam Değer' : 'Total Value'}</p>
                <p className="text-lg font-semibold text-amber-400">{formatCurrency(metrics.totalValue)}</p>
              </div>
              <div className="bg-neutral-800/50 rounded-xl p-4">
                <p className="text-xs text-neutral-500 mb-1">{language === 'tr' ? 'Haftalık P&L' : 'Weekly P&L'}</p>
                {(() => {
                  const lastSnap = snapshots.length > 0 ? snapshots[snapshots.length - 1] : null;
                  const weeklyPnl = lastSnap ? metrics.totalValue - lastSnap.total_value : 0;
                  const weeklyPnlPct = lastSnap && lastSnap.total_value > 0 ? (weeklyPnl / lastSnap.total_value) * 100 : 0;
                  return (
                    <div>
                      <p className={`text-lg font-semibold ${weeklyPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {weeklyPnl >= 0 ? '+' : ''}{formatCurrency(weeklyPnl)}
                      </p>
                      <p className={`text-xs ${weeklyPnl >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {weeklyPnl >= 0 ? '+' : ''}{weeklyPnlPct.toFixed(2)}%
                      </p>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Holdings count info + Save button */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-neutral-500">
                {holdings.length} {language === 'tr' ? 'pozisyon kaydedilecek' : 'positions will be recorded'}
              </p>
              <button
                onClick={saveManualSnapshot}
                disabled={snapshotSaving || !portfolioId}
                className="px-6 py-2.5 bg-amber-500 hover:bg-amber-400 disabled:bg-neutral-700 disabled:text-neutral-500 text-black font-semibold rounded-xl transition-colors flex items-center gap-2"
              >
                {snapshotSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {language === 'tr' ? 'Kaydediliyor...' : 'Saving...'}
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    {language === 'tr' ? 'Snapshot Kaydet' : 'Save Snapshot'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Snapshot History Table - Collapsible */}
        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-3xl border border-neutral-800/50 overflow-hidden">
          <div className="p-6 border-b border-neutral-800/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <History className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-neutral-50">{t.weeklyHistory}</h2>
                <p className="text-sm text-neutral-500">{language === 'tr' ? 'Haftalık portföy geçmişi' : 'Weekly portfolio history'}</p>
              </div>
            </div>
          </div>
          {snapshots.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-neutral-500 text-sm border-b border-neutral-800/50">
                    <th className="p-4 font-medium">{t.date}</th>
                    <th className="p-4 text-right font-medium">{t.totalValue}</th>
                    <th className="p-4 text-right font-medium">{t.totalCash}</th>
                    <th className="p-4 text-right font-medium">{t.holdingsTableHeader}</th>
                    <th className="p-4 text-right font-medium">{t.change}</th>
                    <th className="p-4 text-right font-medium">
                      <div className="flex items-center justify-end gap-1">
                        {t.cashWithdrawal}
                        <InfoTooltip text={t.tooltips.cashWithdrawal} />
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {snapshots.slice().reverse().map((snap, index, arr) => {
                    const prevSnap = arr[index + 1];
                    const change = prevSnap ? ((snap.total_value - prevSnap.total_value) / prevSnap.total_value) * 100 : 0;
                    const holdingsValue = snap.total_value - snap.cash_balance;

                    return (
                      <tr key={snap.id} className="border-b border-neutral-800/30 hover:bg-neutral-800/20">
                        <td className="p-4 text-neutral-300">
                          {new Date(snap.snapshot_date).toLocaleDateString('tr-TR', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="p-4 text-right text-amber-400 font-semibold">{formatCurrency(snap.total_value)}</td>
                        <td className="p-4 text-right text-emerald-400">{formatCurrency(snap.cash_balance)}</td>
                        <td className="p-4 text-right text-neutral-300">{formatCurrency(holdingsValue)}</td>
                        <td className={`p-4 text-right font-semibold ${change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {index === arr.length - 1 ? '-' : formatPercent(change)}
                        </td>
                        <td className="p-4 text-right">
                          {editingWithdrawal === snap.id ? (
                            <div className="flex items-center justify-end gap-2">
                              <input
                                type="number"
                                value={withdrawalAmount}
                                onChange={(e) => setWithdrawalAmount(e.target.value)}
                                className="w-24 px-2 py-1 text-sm bg-neutral-800 border border-neutral-700 rounded text-neutral-100 text-right"
                                placeholder="0"
                                autoFocus
                              />
                              <button
                                onClick={() => saveWithdrawal(snap.id)}
                                className="p-1 text-emerald-400 hover:text-emerald-300"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => { setEditingWithdrawal(null); setWithdrawalAmount(''); }}
                                className="p-1 text-red-400 hover:text-red-300"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <div
                              className="flex items-center justify-end gap-2 cursor-pointer group"
                              onClick={() => { setEditingWithdrawal(snap.id); setWithdrawalAmount(snap.withdrawal || ''); }}
                            >
                              <span className={snap.withdrawal > 0 ? 'text-purple-400' : 'text-neutral-600'}>
                                {snap.withdrawal > 0 ? formatCurrency(snap.withdrawal) : '-'}
                              </span>
                              <Edit2 className="w-3 h-3 text-neutral-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center text-neutral-500">
              <History className="w-16 h-16 mx-auto mb-4 text-neutral-600" />
              <p className="text-lg">{t.noSnapshot}</p>
              <p className="text-sm mt-2">{t.firstSnapshot}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Export Functions
  const exportToCSV = () => {
    const date = new Date().toISOString().split('T')[0];

    // Holdings CSV
    let csv = 'Barbarians Portfolio Report\n';
    csv += `Generated: ${new Date().toLocaleString()}\n\n`;
    csv += 'PORTFOLIO SUMMARY\n';
    csv += `Total Value,${metrics.totalValue.toFixed(2)}\n`;
    csv += `Holdings Value,${metrics.holdingsValue.toFixed(2)}\n`;
    csv += `Cash (USD),${cashBalance.toFixed(2)}\n`;
    csv += `Cash (USDT),${cashBalanceUSDT.toFixed(2)}\n`;
    csv += `Cash (TRY),${cashBalanceTRY.toFixed(2)}\n`;
    csv += `Total Gain/Loss,${metrics.totalGain.toFixed(2)}\n`;
    csv += `Gain %,${metrics.percentGain.toFixed(2)}%\n\n`;

    csv += 'RISK METRICS\n';
    csv += `Beta,${metrics.beta}\n`;
    csv += `Sharpe Ratio,${metrics.sharpeRatio}\n`;
    csv += `Sortino Ratio,${metrics.sortinoRatio}\n`;
    csv += `Volatility,${metrics.volatility}%\n`;
    csv += `Max Drawdown,${metrics.maxDrawdown}%\n\n`;

    csv += 'HOLDINGS\n';
    csv += 'Symbol,Name,Type,Shares,Avg Cost,Current Price,Value,Gain/Loss,Gain %,Weight %\n';

    holdings.forEach(h => {
      const value = h.shares * (h.current_price || h.avg_cost);
      const gain = (h.current_price - h.avg_cost) * h.shares;
      const gainPct = h.avg_cost > 0 ? ((h.current_price - h.avg_cost) / h.avg_cost) * 100 : 0;
      const weight = metrics.totalValue > 0 ? (value / metrics.totalValue) * 100 : 0;
      csv += `${h.symbol},${h.name},${h.type},${h.shares},${h.avg_cost.toFixed(2)},${(h.current_price || 0).toFixed(2)},${value.toFixed(2)},${gain.toFixed(2)},${gainPct.toFixed(2)}%,${weight.toFixed(1)}%\n`;
    });

    // Download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `barbarians-portfolio-${date}.csv`;
    link.click();
  };

  const exportToPDF = () => {
    // Create printable HTML - DARK MODE
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Barbarians Portfolio Report</title>
        <style>
          @media print {
            body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          }
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; background: #0a0a0a; color: #e5e5e5; }
          h1 { color: #fbbf24; margin-bottom: 5px; }
          h2 { color: #a3a3a3; margin-top: 30px; border-bottom: 2px solid #fbbf24; padding-bottom: 10px; }
          .subtitle { color: #737373; margin-bottom: 30px; }
          .summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-bottom: 30px; }
          .metric-box { background: #171717; padding: 20px; border-radius: 12px; border: 1px solid #262626; }
          .metric-label { font-size: 12px; color: #737373; text-transform: uppercase; letter-spacing: 0.5px; }
          .metric-value { font-size: 24px; font-weight: bold; color: #fbbf24; }
          .metric-value.positive { color: #10b981; }
          .metric-value.negative { color: #ef4444; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #171717; padding: 12px; text-align: left; font-size: 12px; text-transform: uppercase; color: #737373; border-bottom: 1px solid #262626; }
          td { padding: 12px; border-bottom: 1px solid #262626; color: #d4d4d4; }
          .text-right { text-align: right; }
          .positive { color: #10b981; }
          .negative { color: #ef4444; }
          .symbol { color: #fbbf24; font-weight: 600; }
          .footer { margin-top: 40px; text-align: center; color: #525252; font-size: 12px; border-top: 1px solid #262626; padding-top: 20px; }
        </style>
      </head>
      <body>
        <h1>🦬 Barbarians Portfolio Report</h1>
        <p class="subtitle">Generated on ${new Date().toLocaleString()}</p>

        <h2>${t.totalValue}</h2>
        <div class="summary-grid">
          <div class="metric-box">
            <div class="metric-label">${t.totalValue}</div>
            <div class="metric-value">${formatCurrency(metrics.totalValue)}</div>
          </div>
          <div class="metric-box">
            <div class="metric-label">${t.totalGainLoss}</div>
            <div class="metric-value ${metrics.totalGain >= 0 ? 'positive' : 'negative'}">${formatCurrency(metrics.totalGain)} (${metrics.percentGain.toFixed(2)}%)</div>
          </div>
          <div class="metric-box">
            <div class="metric-label">${t.totalCash}</div>
            <div class="metric-value">${formatCurrency(cashBalance + cashBalanceUSDT + (cashBalanceTRY / (usdTryRate || 35)))}</div>
          </div>
        </div>

        <h2>${t.riskMetrics}</h2>
        <div class="summary-grid">
          <div class="metric-box">
            <div class="metric-label">${t.beta}</div>
            <div class="metric-value">${metrics.beta}</div>
          </div>
          <div class="metric-box">
            <div class="metric-label">${t.sharpeRatio}</div>
            <div class="metric-value">${metrics.sharpeRatio}</div>
          </div>
          <div class="metric-box">
            <div class="metric-label">${t.sortinoRatio}</div>
            <div class="metric-value">${metrics.sortinoRatio}</div>
          </div>
          <div class="metric-box">
            <div class="metric-label">${t.volatility}</div>
            <div class="metric-value">${metrics.volatility}%</div>
          </div>
          <div class="metric-box">
            <div class="metric-label">${t.maxDrawdown}</div>
            <div class="metric-value negative">${metrics.maxDrawdown}%</div>
          </div>
        </div>

        <h2>${t.holdings} (${holdings.length})</h2>
        <table>
          <thead>
            <tr>
              <th>${t.symbol}</th>
              <th>${t.name}</th>
              <th>${t.type}</th>
              <th class="text-right">${t.shares}</th>
              <th class="text-right">${t.avgCost}</th>
              <th class="text-right">${t.price}</th>
              <th class="text-right">${t.value}</th>
              <th class="text-right">${t.gain}</th>
              <th class="text-right">${t.weight}</th>
            </tr>
          </thead>
          <tbody>
            ${holdings.map(h => {
              const value = h.shares * (h.current_price || h.avg_cost);
              const gain = (h.current_price - h.avg_cost) * h.shares;
              const gainPct = h.avg_cost > 0 ? ((h.current_price - h.avg_cost) / h.avg_cost) * 100 : 0;
              const weight = metrics.totalValue > 0 ? (value / metrics.totalValue) * 100 : 0;
              return `
                <tr>
                  <td class="symbol">${h.symbol}</td>
                  <td>${h.name}</td>
                  <td>${h.type}</td>
                  <td class="text-right">${h.shares.toLocaleString()}</td>
                  <td class="text-right">${formatCurrency(h.avg_cost)}</td>
                  <td class="text-right">${formatCurrency(h.current_price || 0)}</td>
                  <td class="text-right">${formatCurrency(value)}</td>
                  <td class="text-right ${gain >= 0 ? 'positive' : 'negative'}">${formatCurrency(gain)} (${gainPct.toFixed(1)}%)</td>
                  <td class="text-right">${weight.toFixed(1)}%</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>

        <div class="footer">
          <p>Barbarians Portfolio Tracker • barbarportfolio.com</p>
        </div>
      </body>
      </html>
    `;

    // Open print dialog
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Notes Tab Content - Weekly planning, research notes, trading journal
  const NotesContent = () => {
    const [notes, setNotes] = useState([]);
    const [selectedNote, setSelectedNote] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [noteForm, setNoteForm] = useState({ title: '', content: '', category: 'general', is_pinned: false });
    const [notesLoading, setNotesLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [filterCategory, setFilterCategory] = useState('all');

    // Get auth headers with Supabase session token (async function)
    const getNotesAuthHeaders = useCallback(async () => {
      const { data: { session } } = await supabase.auth.getSession();
      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || ''}`
      };
    }, []);

    const categories = [
      { id: 'all', label: language === 'tr' ? 'Tümü' : 'All', color: 'neutral' },
      { id: 'weekly', label: language === 'tr' ? 'Haftalık' : 'Weekly', color: 'amber' },
      { id: 'research', label: language === 'tr' ? 'Araştırma' : 'Research', color: 'blue' },
      { id: 'trade', label: language === 'tr' ? 'İşlem' : 'Trade', color: 'emerald' },
      { id: 'general', label: language === 'tr' ? 'Genel' : 'General', color: 'purple' }
    ];

    // Fetch notes
    const fetchNotes = useCallback(async () => {
      setNotesLoading(true);
      try {
        const headers = await getNotesAuthHeaders();
        const url = filterCategory === 'all'
          ? '/api/notes'
          : `/api/notes?category=${filterCategory}`;
        const res = await fetch(url, { headers });
        if (res.ok) {
          const data = await res.json();
          setNotes(data);
        }
      } catch (e) {
        console.error('Notes fetch error:', e);
      }
      setNotesLoading(false);
    }, [filterCategory, getNotesAuthHeaders]);

    useEffect(() => {
      fetchNotes();
    }, [fetchNotes]);

    // Create new note
    const createNote = async () => {
      setSaving(true);
      try {
        const headers = await getNotesAuthHeaders();
        const res = await fetch('/api/notes', {
          method: 'POST',
          headers,
          body: JSON.stringify(noteForm)
        });
        if (res.ok) {
          const newNote = await res.json();
          setNotes([newNote, ...notes]);
          setSelectedNote(newNote);
          setIsEditing(false);
          setNoteForm({ title: '', content: '', category: 'general', is_pinned: false });
        }
      } catch (e) {
        console.error('Create note error:', e);
      }
      setSaving(false);
    };

    // Update note
    const updateNote = async () => {
      if (!selectedNote) return;
      setSaving(true);
      try {
        const headers = await getNotesAuthHeaders();
        const res = await fetch(`/api/notes?id=${selectedNote.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(noteForm)
        });
        if (res.ok) {
          const updated = await res.json();
          setNotes(notes.map(n => n.id === updated.id ? updated : n));
          setSelectedNote(updated);
          setIsEditing(false);
        }
      } catch (e) {
        console.error('Update note error:', e);
      }
      setSaving(false);
    };

    // Delete note
    const deleteNote = async (id) => {
      if (!window.confirm(language === 'tr' ? 'Bu notu silmek istediğinize emin misiniz?' : 'Are you sure you want to delete this note?')) return;
      try {
        const headers = await getNotesAuthHeaders();
        const res = await fetch(`/api/notes?id=${id}`, { method: 'DELETE', headers });
        if (res.ok) {
          setNotes(notes.filter(n => n.id !== id));
          if (selectedNote?.id === id) {
            setSelectedNote(null);
            setIsEditing(false);
          }
        }
      } catch (e) {
        console.error('Delete note error:', e);
      }
    };

    // Toggle pin
    const togglePin = async (note) => {
      try {
        const headers = await getNotesAuthHeaders();
        const res = await fetch(`/api/notes?id=${note.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({ is_pinned: !note.is_pinned })
        });
        if (res.ok) {
          const updated = await res.json();
          setNotes(notes.map(n => n.id === updated.id ? updated : n).sort((a, b) => {
            if (a.is_pinned && !b.is_pinned) return -1;
            if (!a.is_pinned && b.is_pinned) return 1;
            return new Date(b.updated_at) - new Date(a.updated_at);
          }));
          if (selectedNote?.id === updated.id) setSelectedNote(updated);
        }
      } catch (e) {
        console.error('Toggle pin error:', e);
      }
    };

    // Start editing
    const startEdit = (note) => {
      setSelectedNote(note);
      setNoteForm({
        title: note.title,
        content: note.content || '',
        category: note.category || 'general',
        is_pinned: note.is_pinned || false
      });
      setIsEditing(true);
    };

    // Start new note
    const startNewNote = () => {
      setSelectedNote(null);
      setNoteForm({ title: '', content: '', category: 'general', is_pinned: false });
      setIsEditing(true);
    };

    const getCategoryColor = (cat) => {
      const c = categories.find(c => c.id === cat);
      return c?.color || 'neutral';
    };

    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-200px)]">
        {/* Notes List - Left Panel */}
        <div className="lg:col-span-1 bg-neutral-900/50 backdrop-blur-sm rounded-3xl border border-neutral-800/50 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-neutral-800/50">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-500" />
                <h2 className="text-lg font-semibold text-neutral-50">
                  {language === 'tr' ? 'Notlarım' : 'My Notes'}
                </h2>
              </div>
              <button
                onClick={startNewNote}
                className="p-2 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 rounded-xl transition-all"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>

            {/* Category Filter */}
            <div className="flex gap-1 flex-wrap">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setFilterCategory(cat.id)}
                  className={`px-3 py-1 text-xs rounded-lg transition-all ${
                    filterCategory === cat.id
                      ? `bg-${cat.color}-500/20 text-${cat.color}-400 border border-${cat.color}-500/30`
                      : 'bg-neutral-800/50 text-neutral-400 hover:bg-neutral-700/50'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Notes List */}
          <div className="flex-1 overflow-y-auto p-2">
            {notesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
              </div>
            ) : notes.length === 0 ? (
              <div className="text-center py-8 text-neutral-500">
                <FileText className="w-10 h-10 mx-auto mb-2 text-neutral-600" />
                <p className="text-sm">{language === 'tr' ? 'Henüz not yok' : 'No notes yet'}</p>
                <button
                  onClick={startNewNote}
                  className="mt-3 text-amber-400 hover:text-amber-300 text-sm"
                >
                  {language === 'tr' ? '+ İlk notunu oluştur' : '+ Create your first note'}
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {notes.map(note => (
                  <div
                    key={note.id}
                    onClick={() => { setSelectedNote(note); setIsEditing(false); }}
                    className={`p-3 rounded-xl cursor-pointer transition-all ${
                      selectedNote?.id === note.id
                        ? 'bg-amber-500/10 border border-amber-500/30'
                        : 'bg-neutral-800/30 border border-neutral-700/30 hover:bg-neutral-800/50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {note.is_pinned && (
                            <span className="text-amber-400">📌</span>
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded-full bg-${getCategoryColor(note.category)}-500/20 text-${getCategoryColor(note.category)}-400`}>
                            {categories.find(c => c.id === note.category)?.label || note.category}
                          </span>
                        </div>
                        <h3 className="font-medium text-neutral-200 truncate">{note.title}</h3>
                        <p className="text-xs text-neutral-500 mt-1 line-clamp-2">
                          {note.content?.replace(/[#*`[\]]/g, '').substring(0, 80) || (language === 'tr' ? 'Boş not' : 'Empty note')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-neutral-700/30">
                      <span className="text-xs text-neutral-600">
                        {new Date(note.updated_at).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', {
                          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                      <div className="flex gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); togglePin(note); }}
                          className={`p-1 rounded-lg transition-all ${note.is_pinned ? 'text-amber-400' : 'text-neutral-500 hover:text-amber-400'}`}
                        >
                          📌
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); startEdit(note); }}
                          className="p-1 text-neutral-500 hover:text-blue-400 rounded-lg transition-all"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteNote(note.id); }}
                          className="p-1 text-neutral-500 hover:text-red-400 rounded-lg transition-all"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Note Editor / Viewer - Right Panel */}
        <div className="lg:col-span-2 bg-neutral-900/50 backdrop-blur-sm rounded-3xl border border-neutral-800/50 flex flex-col overflow-hidden">
          {isEditing ? (
            /* Edit Mode */
            <>
              <div className="p-4 border-b border-neutral-800/50">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-neutral-50">
                    {selectedNote ? (language === 'tr' ? 'Notu Düzenle' : 'Edit Note') : (language === 'tr' ? 'Yeni Not' : 'New Note')}
                  </h2>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setIsEditing(false); if (!selectedNote) setNoteForm({ title: '', content: '', category: 'general', is_pinned: false }); }}
                      className="px-4 py-2 text-neutral-400 hover:text-neutral-200 transition-all"
                    >
                      {language === 'tr' ? 'İptal' : 'Cancel'}
                    </button>
                    <button
                      onClick={selectedNote ? updateNote : createNote}
                      disabled={!noteForm.title || saving}
                      className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-neutral-950 rounded-xl font-medium transition-all disabled:opacity-50 flex items-center gap-2"
                    >
                      {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                      {language === 'tr' ? 'Kaydet' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <div>
                  <input
                    type="text"
                    value={noteForm.title}
                    onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })}
                    placeholder={language === 'tr' ? 'Not başlığı...' : 'Note title...'}
                    className="w-full bg-neutral-800/50 border border-neutral-700 rounded-xl px-4 py-3 text-neutral-100 text-lg font-medium focus:outline-none focus:border-amber-500"
                  />
                </div>
                <div className="flex gap-3 flex-wrap">
                  <select
                    value={noteForm.category}
                    onChange={(e) => setNoteForm({ ...noteForm, category: e.target.value })}
                    className="bg-neutral-800/50 border border-neutral-700 rounded-xl px-4 py-2 text-neutral-300 focus:outline-none focus:border-amber-500"
                  >
                    {categories.filter(c => c.id !== 'all').map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.label}</option>
                    ))}
                  </select>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={noteForm.is_pinned}
                      onChange={(e) => setNoteForm({ ...noteForm, is_pinned: e.target.checked })}
                      className="w-4 h-4 rounded border-neutral-600 bg-neutral-800 text-amber-500 focus:ring-amber-500"
                    />
                    <span className="text-sm text-neutral-400">📌 {language === 'tr' ? 'Sabitle' : 'Pin'}</span>
                  </label>
                </div>
                <div className="flex-1">
                  <textarea
                    value={noteForm.content}
                    onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
                    placeholder={language === 'tr' ? 'Not içeriği... (Markdown desteklenir)\n\n## Başlık\n- Liste öğesi\n- [ ] Yapılacak' : 'Note content... (Markdown supported)\n\n## Heading\n- List item\n- [ ] Todo'}
                    className="w-full h-[400px] bg-neutral-800/50 border border-neutral-700 rounded-xl px-4 py-3 text-neutral-200 focus:outline-none focus:border-amber-500 resize-none font-mono text-sm"
                  />
                </div>
                <div className="text-xs text-neutral-600">
                  💡 {language === 'tr' ? 'Markdown desteklenir: ## başlık, **kalın**, - liste, - [ ] yapılacak' : 'Markdown supported: ## heading, **bold**, - list, - [ ] todo'}
                </div>
              </div>
            </>
          ) : selectedNote ? (
            /* View Mode */
            <>
              <div className="p-4 border-b border-neutral-800/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {selectedNote.is_pinned && <span className="text-amber-400">📌</span>}
                    <span className={`text-xs px-2 py-0.5 rounded-full bg-${getCategoryColor(selectedNote.category)}-500/20 text-${getCategoryColor(selectedNote.category)}-400`}>
                      {categories.find(c => c.id === selectedNote.category)?.label || selectedNote.category}
                    </span>
                    <span className="text-xs text-neutral-500">
                      {new Date(selectedNote.updated_at).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', {
                        year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </span>
                  </div>
                  <button
                    onClick={() => startEdit(selectedNote)}
                    className="px-4 py-2 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 rounded-xl font-medium transition-all flex items-center gap-2"
                  >
                    <Edit2 className="w-4 h-4" />
                    {language === 'tr' ? 'Düzenle' : 'Edit'}
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                <h1 className="text-2xl font-bold text-neutral-100 mb-4">{selectedNote.title}</h1>
                <div className="prose prose-invert prose-amber max-w-none">
                  {/* Simple markdown rendering */}
                  {selectedNote.content?.split('\n').map((line, i) => {
                    // Headings
                    if (line.startsWith('### ')) return <h3 key={i} className="text-lg font-semibold text-neutral-200 mt-4 mb-2">{line.slice(4)}</h3>;
                    if (line.startsWith('## ')) return <h2 key={i} className="text-xl font-semibold text-neutral-100 mt-5 mb-3">{line.slice(3)}</h2>;
                    if (line.startsWith('# ')) return <h1 key={i} className="text-2xl font-bold text-neutral-50 mt-6 mb-4">{line.slice(2)}</h1>;

                    // Checkboxes
                    if (line.startsWith('- [x] ') || line.startsWith('- [X] ')) {
                      return <div key={i} className="flex items-center gap-2 my-1 text-neutral-400"><Check className="w-4 h-4 text-emerald-400" /><span className="line-through">{line.slice(6)}</span></div>;
                    }
                    if (line.startsWith('- [ ] ')) {
                      return <div key={i} className="flex items-center gap-2 my-1 text-neutral-300"><div className="w-4 h-4 border border-neutral-600 rounded" /><span>{line.slice(6)}</span></div>;
                    }

                    // Lists
                    if (line.startsWith('- ')) return <div key={i} className="flex items-start gap-2 my-1 text-neutral-300"><span className="text-amber-500 mt-0.5">•</span><span>{line.slice(2)}</span></div>;

                    // Empty line
                    if (line.trim() === '') return <div key={i} className="h-3" />;

                    // Regular text with bold
                    const processedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-neutral-100">$1</strong>');
                    return <p key={i} className="text-neutral-300 my-1" dangerouslySetInnerHTML={{ __html: processedLine }} />;
                  })}
                </div>
              </div>
            </>
          ) : (
            /* Empty State */
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <FileText className="w-16 h-16 mx-auto mb-4 text-neutral-700" />
                <h3 className="text-lg font-medium text-neutral-400 mb-2">
                  {language === 'tr' ? 'Not seçin veya yeni oluşturun' : 'Select a note or create a new one'}
                </h3>
                <p className="text-sm text-neutral-600 mb-4">
                  {language === 'tr' ? 'Haftalık planlarınızı, araştırmalarınızı ve işlem notlarınızı burada tutun' : 'Keep your weekly plans, research, and trade notes here'}
                </p>
                <button
                  onClick={startNewNote}
                  className="px-4 py-2 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 rounded-xl font-medium transition-all"
                >
                  <Plus className="w-4 h-4 inline mr-2" />
                  {language === 'tr' ? 'Yeni Not' : 'New Note'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Tools Tab Content
  const ToolsContent = () => (
    <div className="max-w-2xl">
      {/* AAII Sentiment Survey Section */}
      <div className="bg-neutral-900/50 backdrop-blur-sm rounded-3xl p-6 border border-neutral-800/50 mt-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
            <Activity className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-neutral-50">AAII Sentiment Survey</h2>
            <p className="text-sm text-neutral-500">Contrarian market indicator</p>
          </div>
        </div>

        {/* What is it */}
        <div className="space-y-4 mb-6">
          <p className="text-sm text-neutral-300 leading-relaxed">
            AAII (American Association of Individual Investors) Sentiment Survey, 1987'den beri bireysel yatırımcıların önümüzdeki 6 ay için piyasa beklentilerini ölçen haftalık bir ankettir.
          </p>

          <div className="p-4 rounded-xl bg-neutral-800/30 border border-neutral-800/50">
            <div className="text-xs text-neutral-500 mb-2 uppercase tracking-wide">Tarihsel Ortalamalar</div>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <TrendingUp className="w-3 h-3 text-emerald-400" />
                  <span className="text-xs text-neutral-400">Bullish</span>
                </div>
                <div className="text-lg font-bold text-emerald-400">38%</div>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Minus className="w-3 h-3 text-neutral-400" />
                  <span className="text-xs text-neutral-400">Neutral</span>
                </div>
                <div className="text-lg font-bold text-neutral-400">31.5%</div>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <TrendingDown className="w-3 h-3 text-red-400" />
                  <span className="text-xs text-neutral-400">Bearish</span>
                </div>
                <div className="text-lg font-bold text-red-400">30.5%</div>
              </div>
            </div>
          </div>
        </div>

        {/* How to interpret */}
        <div className="space-y-3 mb-6">
          <div className="text-sm font-semibold text-neutral-200">Nasıl Yorumlanır?</div>

          <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30">
            <div className="flex items-start gap-3">
              <Bell className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-sm font-medium text-amber-400">Yüksek İyimserlik (Bullish {'>'} 45%)</div>
                <p className="text-xs text-amber-400/80 mt-1">
                  Yatırımcı iyimserliği ortalamanın üzerine çıktığında dikkatli olmakta fayda var. Tarihsel olarak %45 üzeri bullish seviyeleri genellikle piyasa düzeltmelerinden önce görülür. Risk yönetimini sıkılaştırmak mantıklı olabilir.
                </p>
              </div>
            </div>
          </div>

          <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
            <div className="flex items-start gap-3">
              <Target className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-sm font-medium text-emerald-400">Yüksek Karamsarlık (Bearish {'>'} 40%)</div>
                <p className="text-xs text-emerald-400/80 mt-1">
                  Karamsarlık %40'ı aştığında genellikle iyi alım fırsatları oluşur. %50+ seviyeleri nadir görülür ve güçlü alım sinyali verir. %60 üzeri ise tarihi alım fırsatlarına denk gelmiştir.
                </p>
              </div>
            </div>
          </div>

          <div className="p-3 rounded-xl bg-neutral-800/30 border border-neutral-700/30">
            <div className="flex items-start gap-3">
              <Activity className="w-4 h-4 text-neutral-400 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-sm font-medium text-neutral-300">Nötr Bölge (Ortalamalar civarı)</div>
                <p className="text-xs text-neutral-500 mt-1">
                  Bullish %38, Bearish %30.5 civarında seyrediyorsa belirgin bir sinyal yok. Diğer analiz yöntemleriyle birlikte değerlendirin.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Important note */}
        <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20 mb-6">
          <p className="text-xs text-blue-400/80 leading-relaxed">
            <strong className="text-blue-400">Not:</strong> Bu bir "contrarian" (ters) göstergedir. Kalabalığın aşırı iyimser olduğu dönemlerde temkinli olmak, aşırı kötümser olduğu dönemlerde ise fırsat aramak tarihsel olarak iyi sonuçlar vermiştir. Ancak tek başına yatırım kararı için yeterli değildir.
          </p>
        </div>

        {/* CTA Button */}
        <a
          href="https://www.aaii.com/sentimentsurvey"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-3 w-full p-4 rounded-2xl bg-blue-500/20 border border-blue-500/30 hover:bg-blue-500/30 transition-all group"
        >
          <Activity className="w-5 h-5 text-blue-400" />
          <span className="text-blue-400 font-semibold">Güncel Sonuçları Gör</span>
          <ExternalLink className="w-4 h-4 text-blue-400 group-hover:translate-x-1 transition-transform" />
        </a>

        <p className="text-xs text-neutral-600 text-center mt-3">
          Sonuçlar her Perşembe güncellenir
        </p>
      </div>

      {/* Export Report Section */}
      <div className="bg-neutral-900/50 backdrop-blur-sm rounded-3xl p-6 border border-neutral-800/50 mt-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
            <FileText className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-neutral-50">Export Report</h2>
            <p className="text-sm text-neutral-500">Download your portfolio report</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            onClick={exportToCSV}
            className="flex items-center justify-center gap-3 p-5 rounded-2xl bg-neutral-800/30 border border-neutral-800/50 hover:bg-neutral-800/50 hover:border-amber-500/30 transition-all group"
          >
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors">
              <Download className="w-6 h-6 text-emerald-400" />
            </div>
            <div className="text-left">
              <div className="text-lg font-semibold text-neutral-100">CSV Export</div>
              <div className="text-sm text-neutral-500">Spreadsheet format</div>
            </div>
          </button>

          <button
            onClick={exportToPDF}
            className="flex items-center justify-center gap-3 p-5 rounded-2xl bg-neutral-800/30 border border-neutral-800/50 hover:bg-neutral-800/50 hover:border-amber-500/30 transition-all group"
          >
            <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center group-hover:bg-red-500/20 transition-colors">
              <FileText className="w-6 h-6 text-red-400" />
            </div>
            <div className="text-left">
              <div className="text-lg font-semibold text-neutral-100">PDF Report</div>
              <div className="text-sm text-neutral-500">Print or save as PDF</div>
            </div>
          </button>
        </div>

        <p className="text-xs text-neutral-600 mt-4 text-center">
          Reports include portfolio summary, risk metrics, and all holdings
        </p>
      </div>
    </div>
  );

  // Render content based on active tab
  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return <DashboardContent />;
      case 'holdings': return <HoldingsContent />;
      case 'news': return <NewsContent />;
      case 'economics': return <EconomicsContent />;
      case 'calendar': return <CalendarContent />;
      case 'analytics': return <AnalyticsContent />;
      case 'notes': return <NotesContent />;
      case 'tools': return <ToolsContent />;
      default: return <DashboardContent />;
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 font-sans">
      {/* Sidebar Navigation */}
      <div className="fixed left-0 top-0 h-full w-64 bg-neutral-900/80 backdrop-blur-xl border-r border-neutral-800/50 z-40 hidden lg:flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-neutral-800/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-neutral-950" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-neutral-50">Barbarians Portfolio Tracker</h1>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                    : 'text-neutral-400 hover:bg-neutral-800/50 hover:text-neutral-200'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{t[tab.id]}</span>
                {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
              </button>
            );
          })}
        </nav>

        {/* Language Toggle & Help */}
        <div className="px-4 py-2 border-t border-neutral-800/50 space-y-2">
          <button
            onClick={() => setLanguage(language === 'tr' ? 'en' : 'tr')}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-neutral-800 hover:bg-neutral-700 text-neutral-300 rounded-xl transition-all"
          >
            <Globe className="w-4 h-4" />
            <span className="font-medium">{language === 'tr' ? 'English' : 'Türkçe'}</span>
          </button>
          <button
            onClick={() => setShowHelpModal(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-amber-500/10 hover:bg-amber-500/20 text-amber-500 rounded-xl transition-all"
          >
            <HelpCircle className="w-4 h-4" />
            <span className="font-medium">{t.help}</span>
          </button>
        </div>

        {/* User Info */}
        <div className="p-4 border-t border-neutral-800/50">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-neutral-800 flex items-center justify-center text-neutral-400">
              {user?.email?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-neutral-200 truncate">{user?.email}</p>
              <p className="text-xs text-neutral-500">{t.investor}</p>
            </div>
          </div>
          <button
            onClick={onLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-xl transition-all"
          >
            <LogOut className="w-4 h-4" />
            {t.logout}
          </button>
        </div>
      </div>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-neutral-900/90 backdrop-blur-xl border-b border-neutral-800/50 z-40">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
              <Wallet className="w-4 h-4 text-neutral-950" />
            </div>
            <span className="font-bold text-neutral-50">Barbarians</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowHelpModal(true)}
              className="p-2 bg-amber-500/10 hover:bg-amber-500/20 text-amber-500 rounded-lg"
              title={t.help}
            >
              <HelpCircle className="w-4 h-4" />
            </button>
            <button
              onClick={() => setLanguage(language === 'tr' ? 'en' : 'tr')}
              className="p-2 bg-neutral-800 hover:bg-neutral-700 text-neutral-300 rounded-lg"
            >
              <Globe className="w-4 h-4" />
            </button>
            <button
              onClick={() => fetchPrices(holdings)}
              disabled={pricesLoading}
              className="p-2 bg-neutral-800 hover:bg-neutral-700 text-neutral-300 rounded-lg"
            >
              <RefreshCw className={`w-4 h-4 ${pricesLoading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={onLogout} className="p-2 bg-red-500/10 text-red-400 rounded-lg">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Mobile Tab Bar */}
        <div className="flex overflow-x-auto px-2 pb-2 gap-1 scrollbar-hide">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl whitespace-nowrap transition-all text-sm font-medium ${
                  isActive
                    ? 'bg-amber-500 text-neutral-950'
                    : 'bg-neutral-800/50 text-neutral-400'
                }`}
              >
                <Icon className="w-4 h-4" />
                {t[tab.id]}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:ml-64 pt-28 lg:pt-0">
        {/* Desktop Header */}
        <div className="hidden lg:flex items-center justify-between p-6 border-b border-neutral-800/50">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-neutral-50">
                {t[activeTab]}
              </h1>
              {usdTryRate > 0 && (
                <div className="flex items-center gap-2 text-sm text-neutral-500">
                  <Globe className="w-4 h-4" />
                  USD/TRY: {usdTryRate.toFixed(2)}
                </div>
              )}
            </div>
            {activeTab === 'dashboard' && (
              <div className="mt-2 flex items-start gap-2 max-w-2xl">
                <span className="text-amber-500 text-lg leading-none">"</span>
                <div className="min-w-0">
                  <p className="text-sm text-neutral-400 italic truncate">{dailyQuote.quote}</p>
                  <p className="text-xs text-neutral-600 mt-0.5">— {dailyQuote.author}</p>
                </div>
              </div>
            )}
          </div>
          <button
            onClick={() => fetchPrices(holdings)}
            disabled={pricesLoading}
            className="flex items-center gap-2 px-4 py-2 bg-neutral-800 hover:bg-neutral-700 text-neutral-300 rounded-xl transition-all flex-shrink-0"
          >
            <RefreshCw className={`w-4 h-4 ${pricesLoading ? 'animate-spin' : ''}`} />
            {t.refreshPrices}
          </button>
        </div>

        {/* Page Content */}
        <div className="p-4 lg:p-6">
          {renderContent()}
        </div>
      </div>

      {/* Help/Glossary Modal */}
      <HelpModal isOpen={showHelpModal} onClose={() => setShowHelpModal(false)} t={t} />

      {/* Add Asset Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-neutral-900 rounded-3xl p-8 w-full max-w-md border border-neutral-800 shadow-2xl">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-neutral-50">{t.addAsset}</h2>
              <button onClick={() => { setShowAddModal(false); setNewHolding({ symbol: '', name: '', type: 'Stock', shares: '', avgCost: '', sector: '' }); }} className="p-2 text-neutral-500 hover:text-neutral-200 hover:bg-neutral-800 rounded-xl transition-all">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-neutral-400 mb-2 font-medium">{t.symbol} *</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={newHolding.symbol}
                      onChange={(e) => {
                        const symbol = e.target.value.toUpperCase();
                        // Otomatik tip tespiti - BIST, TEFAS, Crypto, ETF
                        const detectedType = detectAssetType(symbol);
                        setNewHolding({
                          ...newHolding,
                          symbol,
                          type: detectedType || newHolding.type // tespit edilemezse mevcut tipi koru
                        });
                      }}
                      className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-4 py-3 text-neutral-100 focus:outline-none focus:border-amber-500"
                      placeholder="AAPL, SISE, BTC..."
                    />
                    {symbolLoading && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-neutral-500 mt-1">{t.typeSymbolToAutofill}</p>
                </div>
                <div>
                  <label className="block text-sm text-neutral-400 mb-2 font-medium">{t.type}</label>
                  <select
                    value={newHolding.type}
                    onChange={(e) => setNewHolding({ ...newHolding, type: e.target.value, symbol: '', name: '', avgCost: '', sector: '' })}
                    className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-4 py-3 text-neutral-100 focus:outline-none focus:border-amber-500"
                  >
                    <option value="Stock">US Stock</option>
                    <option value="ETF">US ETF</option>
                    <option value="BIST">BIST Hisse</option>
                    <option value="TEFAS">TEFAS Fon</option>
                    <option value="Crypto">Crypto</option>
                    <option value="Bond">Bond</option>
                    <option value="Commodity">Commodity</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm text-neutral-400 mb-2 font-medium">{t.name}</label>
                <input
                  type="text"
                  value={newHolding.name}
                  onChange={(e) => setNewHolding({ ...newHolding, name: e.target.value })}
                  className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-4 py-3 text-neutral-100 focus:outline-none focus:border-amber-500"
                  placeholder={t.autoFilledFromSymbol}
                />
              </div>
              <div>
                <label className="block text-sm text-neutral-400 mb-2 font-medium">{t.sector}</label>
                <input
                  type="text"
                  value={newHolding.sector}
                  onChange={(e) => setNewHolding({ ...newHolding, sector: e.target.value })}
                  className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-4 py-3 text-neutral-100 focus:outline-none focus:border-amber-500"
                  placeholder={t.autoFilledFromSymbol}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-neutral-400 mb-2 font-medium">{t.shares} *</label>
                  <input
                    type="number"
                    value={newHolding.shares}
                    onChange={(e) => setNewHolding({ ...newHolding, shares: e.target.value })}
                    className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-4 py-3 text-neutral-100 focus:outline-none focus:border-amber-500"
                    placeholder="100"
                  />
                </div>
                <div>
                  <label className="block text-sm text-neutral-400 mb-2 font-medium">
                    {t.avgCost} {(newHolding.type === 'BIST' || newHolding.type === 'TEFAS') ? '(₺)' : '($)'}
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500">
                      {(newHolding.type === 'BIST' || newHolding.type === 'TEFAS') ? '₺' : '$'}
                    </span>
                    <input
                      type="number"
                      value={newHolding.avgCost}
                      onChange={(e) => setNewHolding({ ...newHolding, avgCost: e.target.value })}
                      className="w-full bg-neutral-800 border border-neutral-700 rounded-xl pl-8 pr-4 py-3 text-neutral-100 focus:outline-none focus:border-amber-500"
                      placeholder="Auto-filled"
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-4 mt-8">
                <button onClick={() => { setShowAddModal(false); setNewHolding({ symbol: '', name: '', type: 'Stock', shares: '', avgCost: '', sector: '' }); }} className="flex-1 px-6 py-3 bg-neutral-800 hover:bg-neutral-700 text-neutral-300 font-semibold rounded-xl transition-all duration-200">{t.cancel}</button>
                <button onClick={handleAddHolding} disabled={!newHolding.symbol || !newHolding.shares} className="flex-1 px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-neutral-950 font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-amber-500/20 disabled:opacity-50 disabled:cursor-not-allowed">{t.add}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Holdings Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-neutral-900 rounded-3xl p-8 w-full max-w-2xl border border-neutral-800 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-neutral-50">{t.importHoldings}</h2>
                <p className="text-sm text-neutral-500 mt-1">{t.importFromBroker}</p>
              </div>
              <button
                onClick={() => { setShowImportModal(false); setImportFile(null); setImportPreview([]); setImportError(''); }}
                className="p-2 text-neutral-500 hover:text-neutral-200 hover:bg-neutral-800 rounded-xl transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Broker Selection */}
            <div className="mb-6">
              <label className="block text-sm text-neutral-400 mb-2 font-medium">{t.selectBroker}</label>
              <select
                value={importBroker}
                onChange={(e) => { setImportBroker(e.target.value); setImportFile(null); setImportPreview([]); setImportError(''); }}
                className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-4 py-3 text-neutral-100 focus:outline-none focus:border-amber-500"
              >
                <option value="interactive-brokers">Interactive Brokers</option>
                <option value="generic">Generic CSV (Symbol, Quantity, Price)</option>
                <option value="midas" disabled>Midas (Coming Soon)</option>
              </select>
            </div>

            {/* File Upload */}
            <div className="mb-6">
              <label className="block text-sm text-neutral-400 mb-2 font-medium">{t.uploadFile}</label>
              <div className="border-2 border-dashed border-neutral-700 rounded-xl p-6 text-center hover:border-amber-500/50 transition-colors">
                <input
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleImportFileChange}
                  className="hidden"
                  id="import-file"
                />
                <label htmlFor="import-file" className="cursor-pointer">
                  <Upload className="w-10 h-10 text-neutral-600 mx-auto mb-3" />
                  {importFile ? (
                    <p className="text-amber-400 font-medium">{importFile.name}</p>
                  ) : (
                    <>
                      <p className="text-neutral-400">{t.clickToSelect}</p>
                      <p className="text-xs text-neutral-600 mt-1">{t.supported}</p>
                    </>
                  )}
                </label>
              </div>
            </div>

            {/* Loading */}
            {importLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
                <span className="ml-3 text-neutral-400">Processing file...</span>
              </div>
            )}

            {/* Error */}
            {importError && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-red-400 text-sm">{importError}</p>
              </div>
            )}

            {/* Preview */}
            {importPreview.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-neutral-200">{t.preview} ({importPreview.length} {t.holdings})</h3>
                  <span className="text-xs text-neutral-500">{t.reviewBeforeImport}</span>
                </div>
                <div className="bg-neutral-800/50 rounded-xl border border-neutral-800 overflow-hidden max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-neutral-800/80 sticky top-0">
                      <tr className="text-neutral-500">
                        <th className="p-3 text-left font-medium">{t.symbol}</th>
                        <th className="p-3 text-left font-medium">{t.type}</th>
                        <th className="p-3 text-right font-medium">{t.shares}</th>
                        <th className="p-3 text-right font-medium">{t.avgCost}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.map((h, i) => (
                        <tr key={i} className="border-t border-neutral-800/50">
                          <td className="p-3 text-neutral-200 font-medium">{h.symbol}</td>
                          <td className="p-3">
                            <span className="px-2 py-1 rounded-lg text-xs font-medium" style={{ backgroundColor: `${TYPE_COLORS[h.type]}15`, color: TYPE_COLORS[h.type] }}>
                              {h.type}
                            </span>
                          </td>
                          <td className="p-3 text-right text-neutral-300">{h.shares.toLocaleString()}</td>
                          <td className="p-3 text-right text-neutral-400">{formatPrice(h.avgCost)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-4">
              <button
                onClick={() => { setShowImportModal(false); setImportFile(null); setImportPreview([]); setImportError(''); }}
                className="flex-1 px-6 py-3 bg-neutral-800 hover:bg-neutral-700 text-neutral-300 font-semibold rounded-xl transition-all duration-200"
              >
                {t.cancel}
              </button>
              <button
                onClick={handleImportHoldings}
                disabled={importPreview.length === 0 || importLoading}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-neutral-950 font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-amber-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {importLoading ? t.loading : `${t.import} ${importPreview.length} ${t.holdings}`}
              </button>
            </div>

            {/* Help Text */}
            <div className="mt-6 p-4 bg-neutral-800/30 rounded-xl">
              <h4 className="text-sm font-medium text-neutral-300 mb-2">How to export from Interactive Brokers:</h4>
              <ol className="text-xs text-neutral-500 space-y-1 list-decimal list-inside">
                <li>Go to Performance & Reports → Statements</li>
                <li>Select "Activity" statement type</li>
                <li>Choose CSV format and download</li>
                <li>Upload the file here</li>
              </ol>
            </div>
          </div>
        </div>
      )}

      {/* Edit Asset Modal */}
      {showEditModal && editingHolding && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-neutral-900 rounded-3xl p-8 w-full max-w-md border border-neutral-800 shadow-2xl">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-neutral-50">Edit {editingHolding.symbol}</h2>
              <button onClick={() => { setShowEditModal(false); setEditingHolding(null); }} className="p-2 text-neutral-500 hover:text-neutral-200 hover:bg-neutral-800 rounded-xl transition-all">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-neutral-400 mb-2 font-medium">Symbol</label>
                  <input type="text" value={editingHolding.symbol} onChange={(e) => setEditingHolding({ ...editingHolding, symbol: e.target.value.toUpperCase() })} className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-4 py-3 text-neutral-100 focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="block text-sm text-neutral-400 mb-2 font-medium">Type</label>
                  <select value={editingHolding.type} onChange={(e) => setEditingHolding({ ...editingHolding, type: e.target.value })} className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-4 py-3 text-neutral-100 focus:outline-none focus:border-amber-500">
                    <option value="Stock">US Stock</option>
                    <option value="ETF">US ETF</option>
                    <option value="BIST">BIST Hisse</option>
                    <option value="TEFAS">TEFAS Fon</option>
                    <option value="Crypto">Crypto</option>
                    <option value="Bond">Bond</option>
                    <option value="Commodity">Commodity</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm text-neutral-400 mb-2 font-medium">Name</label>
                <input type="text" value={editingHolding.name} onChange={(e) => setEditingHolding({ ...editingHolding, name: e.target.value })} className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-4 py-3 text-neutral-100 focus:outline-none focus:border-amber-500" />
              </div>
              <div>
                <label className="block text-sm text-neutral-400 mb-2 font-medium">Sector</label>
                <input type="text" value={editingHolding.sector} onChange={(e) => setEditingHolding({ ...editingHolding, sector: e.target.value })} className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-4 py-3 text-neutral-100 focus:outline-none focus:border-amber-500" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-neutral-400 mb-2 font-medium">Shares</label>
                  <input type="number" value={editingHolding.shares} onChange={(e) => setEditingHolding({ ...editingHolding, shares: e.target.value })} className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-4 py-3 text-neutral-100 focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="block text-sm text-neutral-400 mb-2 font-medium">
                    Avg Cost {(editingHolding.type === 'BIST' || editingHolding.type === 'TEFAS') ? '(₺)' : '($)'}
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500">
                      {(editingHolding.type === 'BIST' || editingHolding.type === 'TEFAS') ? '₺' : '$'}
                    </span>
                    <input type="number" value={editingHolding.avgCost} onChange={(e) => setEditingHolding({ ...editingHolding, avgCost: e.target.value })} className="w-full bg-neutral-800 border border-neutral-700 rounded-xl pl-8 pr-4 py-3 text-neutral-100 focus:outline-none focus:border-amber-500" />
                  </div>
                </div>
              </div>
              <div className="flex gap-4 mt-8">
                <button onClick={() => { setShowEditModal(false); setEditingHolding(null); }} className="flex-1 px-6 py-3 bg-neutral-800 hover:bg-neutral-700 text-neutral-300 font-semibold rounded-xl transition-all duration-200">Cancel</button>
                <button onClick={handleUpdateHolding} className="flex-1 px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-neutral-950 font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-amber-500/20">Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Detail Modal */}
      {showTransactionModal && selectedHolding && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-neutral-900 rounded-3xl p-6 w-full max-w-2xl border border-neutral-800 shadow-2xl max-h-[85vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${TYPE_COLORS[selectedHolding.type]}20` }}>
                  <span className="text-lg font-bold" style={{ color: TYPE_COLORS[selectedHolding.type] }}>
                    {selectedHolding.symbol.substring(0, 2)}
                  </span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-neutral-50">{selectedHolding.symbol}</h2>
                  <p className="text-sm text-neutral-400">{selectedHolding.name}</p>
                </div>
              </div>
              <button
                onClick={() => { setShowTransactionModal(false); setSelectedHolding(null); setTransactions([]); }}
                className="p-2 text-neutral-500 hover:text-neutral-200 hover:bg-neutral-800 rounded-xl transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Summary Card */}
            <div className="bg-neutral-800/50 rounded-2xl p-4 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <Activity className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-medium text-neutral-300">{t.summary}</span>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-neutral-500">{t.current}</p>
                  <p className="text-lg font-bold text-neutral-50">
                    {selectedHolding.shares?.toLocaleString()} {t.shares.toLowerCase()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500">{t.avgCost}</p>
                  <p className="text-lg font-bold text-neutral-50">
                    {(selectedHolding.type === 'BIST' || selectedHolding.type === 'TEFAS')
                      ? formatTRY(selectedHolding.avg_cost)
                      : formatCurrency(selectedHolding.avg_cost)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500">{t.value}</p>
                  <p className="text-lg font-bold text-amber-400">
                    {(selectedHolding.type === 'BIST' || selectedHolding.type === 'TEFAS')
                      ? formatTRY(selectedHolding.shares * (selectedHolding.current_price || selectedHolding.avg_cost))
                      : formatCurrency(selectedHolding.shares * (selectedHolding.current_price || selectedHolding.avg_cost))}
                  </p>
                </div>
              </div>
            </div>

            {/* Transaction History Header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <History className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-medium text-neutral-300">{t.transactionHistory}</span>
              </div>
              <button
                onClick={() => setShowAddTransactionModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 rounded-lg text-sm font-medium transition-all"
              >
                <Plus className="w-4 h-4" />
                {t.addTransaction}
              </button>
            </div>

            {/* Transaction List */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {transactionsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
                </div>
              ) : transactions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-neutral-500">
                  <History className="w-12 h-12 mb-3 text-neutral-600" />
                  <p className="font-medium">{t.noTransactions}</p>
                  <p className="text-sm mt-1">{t.addFirstTransaction}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {transactions.map((txn) => {
                    const isBuy = txn.type === 'BUY';
                    const total = txn.quantity * txn.price;
                    return (
                      <div
                        key={txn.id}
                        className="flex items-center justify-between p-3 bg-neutral-800/30 rounded-xl hover:bg-neutral-800/50 transition-all group"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isBuy ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                            {isBuy ? (
                              <TrendingUp className="w-4 h-4 text-emerald-400" />
                            ) : (
                              <TrendingDown className="w-4 h-4 text-red-400" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={`text-sm font-semibold ${isBuy ? 'text-emerald-400' : 'text-red-400'}`}>
                                {isBuy ? t.buy : t.sell}
                              </span>
                              <span className="text-neutral-300 font-medium">
                                {txn.quantity.toLocaleString()} @ {(selectedHolding.type === 'BIST' || selectedHolding.type === 'TEFAS') ? formatTRY(txn.price) : formatCurrency(txn.price)}
                              </span>
                            </div>
                            <p className="text-xs text-neutral-500">
                              {new Date(txn.transaction_date).toLocaleDateString()}
                              {txn.notes && ` • ${txn.notes}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`text-sm font-semibold ${isBuy ? 'text-neutral-300' : 'text-emerald-400'}`}>
                            {isBuy ? '-' : '+'}{(selectedHolding.type === 'BIST' || selectedHolding.type === 'TEFAS') ? formatTRY(total) : formatCurrency(total)}
                          </span>
                          <button
                            onClick={() => handleDeleteTransaction(txn.id)}
                            className="p-1.5 text-neutral-600 hover:text-red-400 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="mt-4 pt-4 border-t border-neutral-800">
              <button
                onClick={() => { setShowTransactionModal(false); setSelectedHolding(null); setTransactions([]); }}
                className="w-full px-4 py-2.5 bg-neutral-800 hover:bg-neutral-700 text-neutral-300 font-medium rounded-xl transition-all"
              >
                {t.close}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Transaction Modal */}
      {showAddTransactionModal && selectedHolding && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-neutral-900 rounded-3xl p-6 w-full max-w-md border border-neutral-800 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-neutral-50">{t.addTransaction}</h2>
              <button
                onClick={() => setShowAddTransactionModal(false)}
                className="p-2 text-neutral-500 hover:text-neutral-200 hover:bg-neutral-800 rounded-xl transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Type Toggle */}
              <div>
                <label className="block text-sm text-neutral-400 mb-2 font-medium">{t.type}</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setNewTransaction({ ...newTransaction, type: 'BUY' })}
                    className={`px-4 py-3 rounded-xl font-semibold transition-all ${
                      newTransaction.type === 'BUY'
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                        : 'bg-neutral-800 text-neutral-400 border border-neutral-700 hover:bg-neutral-700'
                    }`}
                  >
                    <TrendingUp className="w-4 h-4 inline mr-2" />
                    {t.buy}
                  </button>
                  <button
                    onClick={() => setNewTransaction({ ...newTransaction, type: 'SELL' })}
                    className={`px-4 py-3 rounded-xl font-semibold transition-all ${
                      newTransaction.type === 'SELL'
                        ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                        : 'bg-neutral-800 text-neutral-400 border border-neutral-700 hover:bg-neutral-700'
                    }`}
                  >
                    <TrendingDown className="w-4 h-4 inline mr-2" />
                    {t.sell}
                  </button>
                </div>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm text-neutral-400 mb-2 font-medium">{t.transactionDate}</label>
                <input
                  type="date"
                  value={newTransaction.transaction_date}
                  onChange={(e) => setNewTransaction({ ...newTransaction, transaction_date: e.target.value })}
                  className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-4 py-3 text-neutral-100 focus:outline-none focus:border-amber-500"
                />
              </div>

              {/* Quantity and Price */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-neutral-400 mb-2 font-medium">{t.quantity}</label>
                  <input
                    type="number"
                    value={newTransaction.quantity}
                    onChange={(e) => setNewTransaction({ ...newTransaction, quantity: e.target.value })}
                    className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-4 py-3 text-neutral-100 focus:outline-none focus:border-amber-500"
                    placeholder="0"
                    step="any"
                  />
                </div>
                <div>
                  <label className="block text-sm text-neutral-400 mb-2 font-medium">
                    {t.pricePerUnit} {(selectedHolding.type === 'BIST' || selectedHolding.type === 'TEFAS') ? '(₺)' : '($)'}
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500">
                      {(selectedHolding.type === 'BIST' || selectedHolding.type === 'TEFAS') ? '₺' : '$'}
                    </span>
                    <input
                      type="number"
                      value={newTransaction.price}
                      onChange={(e) => setNewTransaction({ ...newTransaction, price: e.target.value })}
                      className="w-full bg-neutral-800 border border-neutral-700 rounded-xl pl-8 pr-4 py-3 text-neutral-100 focus:outline-none focus:border-amber-500"
                      placeholder="0.00"
                      step="any"
                    />
                  </div>
                </div>
              </div>

              {/* Total Preview */}
              {newTransaction.quantity && newTransaction.price && (
                <div className="p-3 bg-neutral-800/50 rounded-xl">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-neutral-400">Total</span>
                    <span className={`text-lg font-bold ${newTransaction.type === 'BUY' ? 'text-neutral-100' : 'text-emerald-400'}`}>
                      {(selectedHolding.type === 'BIST' || selectedHolding.type === 'TEFAS')
                        ? formatTRY(parseFloat(newTransaction.quantity) * parseFloat(newTransaction.price))
                        : formatCurrency(parseFloat(newTransaction.quantity) * parseFloat(newTransaction.price))}
                    </span>
                  </div>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm text-neutral-400 mb-2 font-medium">Notes ({t.cancel.toLowerCase()})</label>
                <input
                  type="text"
                  value={newTransaction.notes}
                  onChange={(e) => setNewTransaction({ ...newTransaction, notes: e.target.value })}
                  className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-4 py-3 text-neutral-100 focus:outline-none focus:border-amber-500"
                  placeholder="Optional note..."
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowAddTransactionModal(false)}
                  className="flex-1 px-4 py-3 bg-neutral-800 hover:bg-neutral-700 text-neutral-300 font-semibold rounded-xl transition-all"
                >
                  {t.cancel}
                </button>
                <button
                  onClick={handleAddTransaction}
                  disabled={!newTransaction.quantity || !newTransaction.price}
                  className={`flex-1 px-4 py-3 font-semibold rounded-xl transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                    newTransaction.type === 'BUY'
                      ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white shadow-emerald-500/20'
                      : 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-white shadow-red-500/20'
                  }`}
                >
                  {t.add} {newTransaction.type === 'BUY' ? t.buy : t.sell}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FMP Fundamentals Modal */}
      {showFundamentalsModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-neutral-900 rounded-3xl p-6 w-full max-w-2xl border border-neutral-800 shadow-2xl max-h-[85vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-blue-500/20 flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-neutral-50">{fundamentalsSymbol}</h2>
                  <p className="text-sm text-neutral-400">{t.fundamentals}</p>
                </div>
              </div>
              <button
                onClick={() => { setShowFundamentalsModal(false); setFundamentalsData(null); setFundamentalsSymbol(null); }}
                className="p-2 text-neutral-500 hover:text-neutral-200 hover:bg-neutral-800 rounded-xl transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {fundamentalsLoading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 text-blue-400 animate-spin mb-4" />
                  <p className="text-neutral-400 text-sm">{t.loading}</p>
                </div>
              ) : !fundamentalsData ? (
                <div className="flex flex-col items-center justify-center py-16 text-neutral-500">
                  <AlertCircle className="w-12 h-12 mb-3 text-neutral-600" />
                  <p className="font-medium">Data unavailable</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Tab Navigation */}
                  <div className="flex gap-1 bg-neutral-800/50 rounded-xl p-1">
                    <button
                      onClick={() => handleFundamentalsTabChange('overview')}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                        fundamentalsTab === 'overview'
                          ? 'bg-blue-600 text-white'
                          : 'text-neutral-400 hover:text-neutral-200 hover:bg-neutral-700/50'
                      }`}
                    >
                      Genel
                    </button>
                    <button
                      onClick={() => handleFundamentalsTabChange('financials')}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                        fundamentalsTab === 'financials'
                          ? 'bg-blue-600 text-white'
                          : 'text-neutral-400 hover:text-neutral-200 hover:bg-neutral-700/50'
                      }`}
                    >
                      Finansallar
                    </button>
                    <button
                      onClick={() => handleFundamentalsTabChange('insider')}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                        fundamentalsTab === 'insider'
                          ? 'bg-blue-600 text-white'
                          : 'text-neutral-400 hover:text-neutral-200 hover:bg-neutral-700/50'
                      }`}
                    >
                      {language === 'tr' ? 'İçeriden' : 'Insider'}
                    </button>
                  </div>

                  {/* Tab Content */}
                  {fundamentalsTab === 'overview' && (
                    <>
                  {/* Company Info Card */}
                  {fundamentalsData.profile && !fundamentalsData.profile.error && (
                    <div className="bg-neutral-800/50 rounded-2xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Info className="w-4 h-4 text-blue-400" />
                        <span className="text-sm font-medium text-neutral-300">{t.companyInfo}</span>
                        {fundamentalsData.profile.cached && (
                          <span className="text-xs text-neutral-600 ml-auto">cached</span>
                        )}
                      </div>
                      <h3 className="text-lg font-bold text-neutral-50 mb-2">{fundamentalsData.profile.companyName}</h3>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-neutral-500">{t.industry}:</span>
                          <span className="text-neutral-200 ml-2">{fundamentalsData.profile.industry || t.notAvailable}</span>
                        </div>
                        <div>
                          <span className="text-neutral-500">{t.marketCap}:</span>
                          <span className="text-neutral-200 ml-2">
                            {fundamentalsData.profile.marketCap
                              ? `$${(fundamentalsData.profile.marketCap / 1e9).toFixed(1)}B`
                              : t.notAvailable}
                          </span>
                        </div>
                        <div>
                          <span className="text-neutral-500">{t.ceo}:</span>
                          <span className="text-neutral-200 ml-2">{fundamentalsData.profile.ceo || t.notAvailable}</span>
                        </div>
                        <div>
                          <span className="text-neutral-500">{t.employees}:</span>
                          <span className="text-neutral-200 ml-2">
                            {fundamentalsData.profile.employees
                              ? fundamentalsData.profile.employees.toLocaleString()
                              : t.notAvailable}
                          </span>
                        </div>
                        <div>
                          <span className="text-neutral-500">{t.beta}:</span>
                          <span className={`ml-2 font-medium ${
                            fundamentalsData.profile.beta > 1.2 ? 'text-red-400' :
                            fundamentalsData.profile.beta < 0.8 ? 'text-emerald-400' : 'text-neutral-200'
                          }`}>
                            {fundamentalsData.profile.beta
                              ? fundamentalsData.profile.beta.toFixed(2)
                              : t.notAvailable}
                          </span>
                        </div>
                        <div>
                          <span className="text-neutral-500">P/E:</span>
                          <span className="text-neutral-200 ml-2">
                            {(fundamentalsData.metrics?.peRatio || fundamentalsData.profile.pe)
                              ? (fundamentalsData.metrics?.peRatio || fundamentalsData.profile.pe).toFixed(1)
                              : t.notAvailable}
                          </span>
                        </div>
                      </div>
                      {fundamentalsData.profile.description && (
                        <p className="text-xs text-neutral-400 mt-3 leading-relaxed">
                          {fundamentalsData.profile.description}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Financial Metrics Card */}
                  {fundamentalsData.metrics && fundamentalsData.metrics.error && (
                    <div className="bg-neutral-800/50 rounded-2xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <BarChart3 className="w-4 h-4 text-neutral-500" />
                        <span className="text-sm font-medium text-neutral-400">{t.financialMetrics}</span>
                      </div>
                      <p className="text-xs text-neutral-500">{fundamentalsData.metrics.error || t.notAvailable}</p>
                    </div>
                  )}
                  {fundamentalsData.metrics && !fundamentalsData.metrics.error && (
                    <div className="bg-neutral-800/50 rounded-2xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <BarChart3 className="w-4 h-4 text-amber-400" />
                        <span className="text-sm font-medium text-neutral-300">{t.financialMetrics}</span>
                        {fundamentalsData.metrics.cached && (
                          <span className="text-xs text-neutral-600 ml-auto">cached</span>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        {/* Valuation Metrics */}
                        <div className="space-y-3">
                          <div>
                            <p className="text-xs text-neutral-500">{t.peRatio}</p>
                            <p className="text-lg font-bold text-neutral-50">
                              {fundamentalsData.metrics.peRatio
                                ? fundamentalsData.metrics.peRatio.toFixed(1)
                                : t.notAvailable}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-neutral-500">{t.pbRatio}</p>
                            <p className="text-lg font-bold text-neutral-50">
                              {fundamentalsData.metrics.pbRatio
                                ? fundamentalsData.metrics.pbRatio.toFixed(2)
                                : t.notAvailable}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-neutral-500">{t.psRatio}</p>
                            <p className="text-lg font-bold text-neutral-50">
                              {fundamentalsData.metrics.psRatio
                                ? fundamentalsData.metrics.psRatio.toFixed(2)
                                : t.notAvailable}
                            </p>
                          </div>
                        </div>

                        {/* Profitability Metrics */}
                        <div className="space-y-3">
                          <div>
                            <p className="text-xs text-neutral-500">{t.roe}</p>
                            <p className={`text-lg font-bold ${fundamentalsData.metrics.roe > 15 ? 'text-emerald-400' : fundamentalsData.metrics.roe > 0 ? 'text-neutral-50' : 'text-red-400'}`}>
                              {fundamentalsData.metrics.roe
                                ? `${fundamentalsData.metrics.roe.toFixed(1)}%`
                                : t.notAvailable}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-neutral-500">{t.grossMargin}</p>
                            <p className="text-lg font-bold text-neutral-50">
                              {fundamentalsData.metrics.grossMargin
                                ? `${fundamentalsData.metrics.grossMargin.toFixed(1)}%`
                                : t.notAvailable}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-neutral-500">{t.netMargin}</p>
                            <p className={`text-lg font-bold ${fundamentalsData.metrics.netMargin > 10 ? 'text-emerald-400' : fundamentalsData.metrics.netMargin > 0 ? 'text-neutral-50' : 'text-red-400'}`}>
                              {fundamentalsData.metrics.netMargin
                                ? `${fundamentalsData.metrics.netMargin.toFixed(1)}%`
                                : t.notAvailable}
                            </p>
                          </div>
                        </div>

                        {/* Solvency Metrics */}
                        <div className="space-y-3">
                          <div>
                            <p className="text-xs text-neutral-500">{t.currentRatio}</p>
                            <p className={`text-lg font-bold ${fundamentalsData.metrics.currentRatio > 1.5 ? 'text-emerald-400' : fundamentalsData.metrics.currentRatio > 1 ? 'text-neutral-50' : 'text-red-400'}`}>
                              {fundamentalsData.metrics.currentRatio
                                ? fundamentalsData.metrics.currentRatio.toFixed(2)
                                : t.notAvailable}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-neutral-500">{t.debtToEquity}</p>
                            <p className={`text-lg font-bold ${fundamentalsData.metrics.debtToEquity < 0.5 ? 'text-emerald-400' : fundamentalsData.metrics.debtToEquity < 1.5 ? 'text-neutral-50' : 'text-red-400'}`}>
                              {fundamentalsData.metrics.debtToEquity
                                ? fundamentalsData.metrics.debtToEquity.toFixed(2)
                                : t.notAvailable}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-neutral-500">{t.roa}</p>
                            <p className="text-lg font-bold text-neutral-50">
                              {fundamentalsData.metrics.roa
                                ? `${fundamentalsData.metrics.roa.toFixed(1)}%`
                                : t.notAvailable}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Earnings History Card */}
                  {fundamentalsData.earnings && fundamentalsData.earnings.data && fundamentalsData.earnings.data.length > 0 && (
                    <div className="bg-neutral-800/50 rounded-2xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Calendar className="w-4 h-4 text-emerald-400" />
                        <span className="text-sm font-medium text-neutral-300">Earnings History</span>
                        {fundamentalsData.earnings.cached && (
                          <span className="text-xs text-neutral-600 ml-auto">cached</span>
                        )}
                      </div>
                      <div className="space-y-2">
                        {fundamentalsData.earnings.data.slice(0, 4).map((earning, idx) => {
                          const surprise = parseFloat(earning.surprise);
                          const isBeat = surprise > 0;
                          return (
                            <div key={idx} className="flex items-center justify-between p-2 bg-neutral-800/50 rounded-lg">
                              <span className="text-sm text-neutral-400">{earning.date}</span>
                              <div className="flex items-center gap-4">
                                <div className="text-right">
                                  <span className="text-xs text-neutral-500">Est: </span>
                                  <span className="text-sm text-neutral-300">${earning.epsEstimated?.toFixed(2) || '-'}</span>
                                </div>
                                <div className="text-right">
                                  <span className="text-xs text-neutral-500">Act: </span>
                                  <span className="text-sm text-neutral-50 font-medium">${earning.epsActual?.toFixed(2) || '-'}</span>
                                </div>
                                {earning.surprise && (
                                  <div className={`px-2 py-0.5 rounded text-xs font-medium ${isBeat ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                                    {isBeat ? '+' : ''}{surprise}%
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                    </>
                  )}

                  {/* Financials Tab Content */}
                  {fundamentalsTab === 'financials' && (
                    <>
                      {fundamentalsExtendedLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
                        </div>
                      ) : (
                        <>
                          {/* Income Statement Card */}
                          {fundamentalsExtendedData.income && fundamentalsExtendedData.income.data && fundamentalsExtendedData.income.data.length > 0 && (
                            <div className="bg-neutral-800/50 rounded-2xl p-4">
                              <div className="flex items-center gap-2 mb-3">
                                <TrendingUp className="w-4 h-4 text-emerald-400" />
                                <span className="text-sm font-medium text-neutral-300">Gelir Tablosu (Yıllık)</span>
                              </div>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="text-neutral-500 text-xs">
                                      <th className="text-left pb-2">Dönem</th>
                                      {fundamentalsExtendedData.income.data.slice(0, 4).map((s, i) => (
                                        <th key={i} className="text-right pb-2">{s.date?.substring(0, 4)}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody className="text-neutral-200">
                                    <tr>
                                      <td className="py-1 text-neutral-400">Gelir</td>
                                      {fundamentalsExtendedData.income.data.slice(0, 4).map((s, i) => (
                                        <td key={i} className="text-right py-1">${(s.revenue / 1e9).toFixed(1)}B</td>
                                      ))}
                                    </tr>
                                    <tr>
                                      <td className="py-1 text-neutral-400">Net Kâr</td>
                                      {fundamentalsExtendedData.income.data.slice(0, 4).map((s, i) => (
                                        <td key={i} className={`text-right py-1 ${s.netIncome > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                          ${(s.netIncome / 1e9).toFixed(1)}B
                                        </td>
                                      ))}
                                    </tr>
                                    <tr>
                                      <td className="py-1 text-neutral-400">Net Marj</td>
                                      {fundamentalsExtendedData.income.data.slice(0, 4).map((s, i) => (
                                        <td key={i} className={`text-right py-1 ${s.netMargin > 10 ? 'text-emerald-400' : 'text-neutral-200'}`}>
                                          {s.netMargin?.toFixed(1)}%
                                        </td>
                                      ))}
                                    </tr>
                                    <tr>
                                      <td className="py-1 text-neutral-400">EPS</td>
                                      {fundamentalsExtendedData.income.data.slice(0, 4).map((s, i) => (
                                        <td key={i} className="text-right py-1">${s.epsDiluted?.toFixed(2)}</td>
                                      ))}
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {/* Cash Flow Card */}
                          {fundamentalsExtendedData.cashflow && fundamentalsExtendedData.cashflow.data && fundamentalsExtendedData.cashflow.data.length > 0 && (
                            <div className="bg-neutral-800/50 rounded-2xl p-4">
                              <div className="flex items-center gap-2 mb-3">
                                <DollarSign className="w-4 h-4 text-blue-400" />
                                <span className="text-sm font-medium text-neutral-300">Nakit Akışı (Yıllık)</span>
                              </div>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="text-neutral-500 text-xs">
                                      <th className="text-left pb-2">Dönem</th>
                                      {fundamentalsExtendedData.cashflow.data.slice(0, 4).map((s, i) => (
                                        <th key={i} className="text-right pb-2">{s.date?.substring(0, 4)}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody className="text-neutral-200">
                                    <tr>
                                      <td className="py-1 text-neutral-400">Operasyonel</td>
                                      {fundamentalsExtendedData.cashflow.data.slice(0, 4).map((s, i) => (
                                        <td key={i} className={`text-right py-1 ${s.operatingCashFlow > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                          ${(s.operatingCashFlow / 1e9).toFixed(1)}B
                                        </td>
                                      ))}
                                    </tr>
                                    <tr>
                                      <td className="py-1 text-neutral-400">CapEx</td>
                                      {fundamentalsExtendedData.cashflow.data.slice(0, 4).map((s, i) => (
                                        <td key={i} className="text-right py-1 text-red-400">
                                          -${(Math.abs(s.capitalExpenditure || 0) / 1e9).toFixed(1)}B
                                        </td>
                                      ))}
                                    </tr>
                                    <tr className="font-semibold">
                                      <td className="py-1 text-neutral-300">Serbest Nakit Akışı</td>
                                      {fundamentalsExtendedData.cashflow.data.slice(0, 4).map((s, i) => (
                                        <td key={i} className={`text-right py-1 ${s.freeCashFlow > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                          ${(s.freeCashFlow / 1e9).toFixed(1)}B
                                        </td>
                                      ))}
                                    </tr>
                                    <tr>
                                      <td className="py-1 text-neutral-400">Temettü</td>
                                      {fundamentalsExtendedData.cashflow.data.slice(0, 4).map((s, i) => (
                                        <td key={i} className="text-right py-1">
                                          {s.dividendsPaid ? `-$${(Math.abs(s.dividendsPaid) / 1e9).toFixed(1)}B` : '-'}
                                        </td>
                                      ))}
                                    </tr>
                                    <tr>
                                      <td className="py-1 text-neutral-400">Hisse Geri Alım</td>
                                      {fundamentalsExtendedData.cashflow.data.slice(0, 4).map((s, i) => (
                                        <td key={i} className="text-right py-1">
                                          {s.commonStockRepurchased ? `-$${(Math.abs(s.commonStockRepurchased) / 1e9).toFixed(1)}B` : '-'}
                                        </td>
                                      ))}
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {/* No data message */}
                          {!fundamentalsExtendedData.income && !fundamentalsExtendedData.cashflow && (
                            <div className="text-center py-8 text-neutral-500">
                              <p>Finansal veri yüklenemedi</p>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}

                  {/* Insider Trading Tab Content */}
                  {fundamentalsTab === 'insider' && (
                    <>
                      {fundamentalsExtendedLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
                        </div>
                      ) : fundamentalsExtendedData.insider && fundamentalsExtendedData.insider.data && fundamentalsExtendedData.insider.data.length > 0 ? (
                        <div className="bg-neutral-800/50 rounded-2xl p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Users className="w-4 h-4 text-purple-400" />
                            <span className="text-sm font-medium text-neutral-300">
                              {language === 'tr' ? 'İçeriden İşlemler' : 'Insider Trades'}
                            </span>
                            <span className="text-xs text-neutral-500 ml-auto">
                              {language === 'tr' ? 'Son 50 işlem' : 'Last 50 trades'}
                            </span>
                          </div>
                          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead className="sticky top-0 bg-neutral-800">
                                <tr className="text-neutral-500 text-xs border-b border-neutral-700">
                                  <th className="text-left pb-2 pr-2">{language === 'tr' ? 'Tarih' : 'Date'}</th>
                                  <th className="text-left pb-2 pr-2">{language === 'tr' ? 'İsim' : 'Name'}</th>
                                  <th className="text-center pb-2 pr-2">{language === 'tr' ? 'Tip' : 'Type'}</th>
                                  <th className="text-right pb-2 pr-2">{language === 'tr' ? 'Adet' : 'Shares'}</th>
                                  <th className="text-right pb-2 pr-2">{language === 'tr' ? 'Fiyat' : 'Price'}</th>
                                  <th className="text-right pb-2">{language === 'tr' ? 'Toplam' : 'Total'}</th>
                                </tr>
                              </thead>
                              <tbody className="text-neutral-200">
                                {fundamentalsExtendedData.insider.data.map((trade, i) => {
                                  const isPurchase = trade.transactionType === 'P' || trade.transactionType === 'P-Purchase';
                                  const isSale = trade.transactionType === 'S' || trade.transactionType === 'S-Sale';
                                  const isAward = trade.transactionType === 'A' || trade.transactionType === 'A-Award';
                                  return (
                                    <tr key={i} className="border-b border-neutral-700/50 hover:bg-neutral-700/30">
                                      <td className="py-2 pr-2 text-neutral-400 text-xs whitespace-nowrap">
                                        {trade.date?.substring(0, 10)}
                                      </td>
                                      <td className="py-2 pr-2 text-neutral-200 text-xs max-w-[120px] truncate" title={trade.name}>
                                        {trade.name?.split(' ').slice(0, 2).join(' ')}
                                      </td>
                                      <td className="py-2 pr-2 text-center">
                                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                          isPurchase ? 'bg-emerald-500/20 text-emerald-400' :
                                          isSale ? 'bg-red-500/20 text-red-400' :
                                          isAward ? 'bg-blue-500/20 text-blue-400' :
                                          'bg-neutral-600/50 text-neutral-400'
                                        }`}>
                                          {isPurchase ? (language === 'tr' ? 'Alış' : 'Buy') :
                                           isSale ? (language === 'tr' ? 'Satış' : 'Sell') :
                                           isAward ? (language === 'tr' ? 'Ödül' : 'Award') :
                                           trade.transactionType}
                                        </span>
                                      </td>
                                      <td className={`py-2 pr-2 text-right text-xs ${
                                        isPurchase ? 'text-emerald-400' :
                                        isSale ? 'text-red-400' :
                                        'text-neutral-300'
                                      }`}>
                                        {trade.shares ? trade.shares.toLocaleString() : '-'}
                                      </td>
                                      <td className="py-2 pr-2 text-right text-xs text-neutral-300">
                                        {trade.price ? `$${trade.price.toFixed(2)}` : '-'}
                                      </td>
                                      <td className={`py-2 text-right text-xs font-medium ${
                                        isPurchase ? 'text-emerald-400' :
                                        isSale ? 'text-red-400' :
                                        'text-neutral-300'
                                      }`}>
                                        {trade.value ? `$${(trade.value / 1e6).toFixed(2)}M` : '-'}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          {/* Summary stats */}
                          {(() => {
                            const purchases = fundamentalsExtendedData.insider.data.filter(t => t.transactionType === 'P' || t.transactionType === 'P-Purchase');
                            const sales = fundamentalsExtendedData.insider.data.filter(t => t.transactionType === 'S' || t.transactionType === 'S-Sale');
                            const totalPurchaseValue = purchases.reduce((sum, t) => sum + (t.value || 0), 0);
                            const totalSaleValue = sales.reduce((sum, t) => sum + (t.value || 0), 0);
                            return (
                              <div className="mt-3 pt-3 border-t border-neutral-700 grid grid-cols-2 gap-4 text-xs">
                                <div className="flex items-center justify-between">
                                  <span className="text-neutral-500">{language === 'tr' ? 'Toplam Alış' : 'Total Buys'}</span>
                                  <span className="text-emerald-400 font-medium">
                                    {purchases.length} ({totalPurchaseValue > 0 ? `$${(totalPurchaseValue / 1e6).toFixed(1)}M` : '-'})
                                  </span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-neutral-500">{language === 'tr' ? 'Toplam Satış' : 'Total Sells'}</span>
                                  <span className="text-red-400 font-medium">
                                    {sales.length} ({totalSaleValue > 0 ? `$${(totalSaleValue / 1e6).toFixed(1)}M` : '-'})
                                  </span>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-neutral-500">
                          <Users className="w-8 h-8 mx-auto mb-2 text-neutral-600" />
                          <p>{language === 'tr' ? 'İçeriden işlem verisi bulunamadı' : 'No insider trading data found'}</p>
                        </div>
                      )}
                    </>
                  )}

                </div>
              )}
            </div>

            {/* Footer */}
            <div className="mt-4 pt-4 border-t border-neutral-800">
              <button
                onClick={() => { setShowFundamentalsModal(false); setFundamentalsData(null); setFundamentalsSymbol(null); setFundamentalsExtendedData({}); }}
                className="w-full px-4 py-3 bg-neutral-800 hover:bg-neutral-700 text-neutral-300 font-semibold rounded-xl transition-all"
              >
                {t.close}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
